import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Dumbbell, UtensilsCrossed, Church, Hotel, ShoppingBag, Sparkles, Briefcase, Warehouse,
  Music, Flame, Wind, Headphones, Moon, Laugh, Baby, Radio, Play, Pause, Square, Search,
  Plus, Check, ChevronRight, Heart, Mic, Zap, LayoutGrid, X, Shuffle, Volume2,
  VolumeX, SkipForward, SkipBack, Globe, BookOpen, Brain, Theater, Loader2,
  GraduationCap, Stethoscope, Building, Share2, Clock, Copy, ExternalLink, User, Lock
} from "lucide-react";
import FollowBusinesses from "./components/FollowBusinesses";  // 2.5.3a/b Following block (rows + nested feed; FollowFeed imported within)
import useConsumerFollows from "./hooks/useConsumerFollows";   // 2.5.3a/b shared follow+feed state

const SUBSCRIPTIONS_ENABLED = false;
// L2 (2026-08-30): playback resilience (reconnect ladder) + R2 fallback are RUNTIME-flagged from
// GET /public/config, so EITHER can be pulled dark in the field via Railway env WITHOUT a consumer
// redeploy. Both default OFF if the config fetch fails (safe). RESILIENCE_BUILD_KILL is a hard,
// code-level master override (set true to force both dark regardless of the runtime flags).
const RESILIENCE_BUILD_KILL = false;

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// CONFIG
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
const API_URL = 'https://groovie-production.up.railway.app/api/v1';
const BUSINESS_API_URL = 'https://groovie-business-api-production.up.railway.app/api/v1';
const NEWS_API_URL = 'https://groovie-business-api-production.up.railway.app/api/v1/consumer/news';
const SUB_API_URL = 'https://groovie-business-api-production.up.railway.app/api/v1/consumer';
const AUTH_API_URL = 'https://groovie-business-api-production.up.railway.app/api/v1/consumer/auth';

// -- L2 runtime feature flags (fetched once; safe-off default) ------------------------------------
const DEFAULT_RUNTIME_CFG = { playbackResilience: false, l2R2Fallback: false, l2LadderExhaustMs: 15000, l2ProbeMs: 15000, debug: false };
let _rtCfgCache = null;    // resolved config (module singleton)
let _rtCfgPromise = null;  // in-flight fetch (dedupes concurrent callers)
function loadRuntimeConfig() {
  if (_rtCfgCache) return Promise.resolve(_rtCfgCache);
  if (_rtCfgPromise) return _rtCfgPromise;
  _rtCfgPromise = fetch(`${BUSINESS_API_URL}/public/config`)
    .then(r => r.ok ? r.json() : Promise.reject(new Error('cfg ' + r.status)))
    .then(j => {
      _rtCfgCache = {
        playbackResilience: !!j.playback_resilience,
        l2R2Fallback: !!j.l2_r2_fallback,
        l2LadderExhaustMs: Number(j.l2_ladder_exhaust_ms) || DEFAULT_RUNTIME_CFG.l2LadderExhaustMs,
        l2ProbeMs: Number(j.l2_probe_ms) || DEFAULT_RUNTIME_CFG.l2ProbeMs,
      };
      return _rtCfgCache;
    })
    .catch(e => { console.warn('[L2] runtime config fetch failed -> safe defaults (both off):', e); return DEFAULT_RUNTIME_CFG; });
  return _rtCfgPromise;
}
// Per-browser overrides for STAGED GATING: enable a flag for THIS browser only (a throwaway test)
// without flipping the global Railway env for the whole field. URL param sets + persists to
// localStorage (survives reloads); ?l2_reset=1 clears them. URL > localStorage > server config.
//   ?l2_resilience=1  ?l2_fallback=1  ?l2_exhaust=15000  ?l2_reset=1
// Capture the ORIGINAL query string at module load — the app normalizes the URL (/listen/56?… -> /listen)
// before the player mounts, so reading window.location.search inside the hook would miss the overrides.
const _INITIAL_SEARCH = (typeof window !== 'undefined' && window.location) ? window.location.search : '';
// Moments/event context: a /m/{slug} link 302-redirects to /listen/{id}?ctx=moment. In that context a guest
// arrived for a birthday/event, not to browse — suppress the signup/follow banner so they land on music, not
// an account prompt. Regular /listen/{id} (no ctx) keeps the banner. (UX ruling 2026-09-13; banner only.)
const _IS_MOMENT = /[?&]ctx=moment(?:&|$)/.test(_INITIAL_SEARCH);
function _applyL2Overrides(cfg) {
  try {
    const qs = new URLSearchParams(_INITIAL_SEARCH);
    if (qs.get('l2_reset') === '1') { ['l2_ov_resilience', 'l2_ov_fallback', 'l2_ov_exhaust', 'l2_ov_debug'].forEach(k => { try { localStorage.removeItem(k); } catch (e) {} }); return cfg; }
    const readOv = (qk, lsk) => { let v = qs.get(qk); if (v !== null) { try { localStorage.setItem(lsk, v); } catch (e) {} } else { try { v = localStorage.getItem(lsk); } catch (e) { v = null; } } return v; };
    const res = readOv('l2_resilience', 'l2_ov_resilience');
    const fb = readOv('l2_fallback', 'l2_ov_fallback');
    const ex = readOv('l2_exhaust', 'l2_ov_exhaust');
    const dbg = readOv('l2_debug', 'l2_ov_debug');
    const out = { ...cfg };
    if (res != null) out.playbackResilience = (res === '1' || res === 'true');
    if (fb != null) out.l2R2Fallback = (fb === '1' || fb === 'true');
    if (ex != null) { const n = parseInt(ex, 10); if (n > 0) out.l2LadderExhaustMs = n; }
    if (dbg != null) out.debug = (dbg === '1' || dbg === 'true');
    if (res != null || fb != null || ex != null || dbg != null) console.warn('[L2] per-browser override active:', { resilience: out.playbackResilience, fallback: out.l2R2Fallback, exhaustMs: out.l2LadderExhaustMs, debug: out.debug });
    return out;
  } catch (e) { return cfg; }
}
function useRuntimeConfig() {
  const [cfg, setCfg] = useState(_applyL2Overrides(_rtCfgCache || DEFAULT_RUNTIME_CFG));
  useEffect(() => { let live = true; loadRuntimeConfig().then(c => { if (live) setCfg(_applyL2Overrides(c)); }); return () => { live = false; }; }, []);
  return cfg;
}

// -- L2 build-staleness auto-updater (silent) -----------------------------------------------------
// The stale-bundle class (a tab serving a pre-fix build for days — the real cause of the 3 airplane
// "failures") is closed two ways: (1) index.html is served no-cache (vercel.json) so a reload always
// gets the current shell; (2) this compares the deployed asset-manifest's main.js hash to the RUNNING
// bundle's hash and, when they differ, reloads at a natural boundary (app-open / idle-revisit / track
// change) — silent, no prompt. sessionStorage guard = at most one reload per session (no loop).
const _LOADED_BUILD = (() => {
  try { const s = document.querySelector('script[src*="/static/js/main."]'); const m = ((s && s.getAttribute('src')) || '').match(/main\.([a-z0-9]+)\.js/); return m ? m[1] : null; }
  catch (e) { return null; }
})();
let _updatePending = false;
async function checkForUpdate() {
  if (!_LOADED_BUILD) return false;   // dev / no hashed bundle -> nothing to compare
  try {
    const r = await fetch(`/asset-manifest.json?ts=${Date.now()}`, { cache: 'no-store' });
    if (!r.ok) return false;
    const j = await r.json();
    const m = String((j.files && j.files['main.js']) || '').match(/main\.([a-z0-9]+)\.js/);
    const serverBuild = m ? m[1] : null;
    if (serverBuild && serverBuild !== _LOADED_BUILD) { _updatePending = true; console.warn(`[L2-UPDATE] newer build available ${_LOADED_BUILD} -> ${serverBuild}`); }
    return _updatePending;
  } catch (e) { return false; }
}
function reloadForUpdate(reason) {
  if (!_updatePending) return;
  try { if (sessionStorage.getItem('l2_reloaded_build') === _LOADED_BUILD) return; sessionStorage.setItem('l2_reloaded_build', _LOADED_BUILD || '1'); } catch (e) {}
  console.warn(`[L2-UPDATE] reloading for new build @ ${reason}`);
  try { window.location.reload(); } catch (e) {}
}

const NEWS_INTERESTS = [
  'technology', 'finance', 'sports', 'health', 'entertainment',
  'science', 'business', 'politics', 'world', 'local',
];

const NEWS_VOICES = [
  { id: 'female_us', label: 'Jenny (US)' },
  { id: 'male_us', label: 'Guy (US)' },
  { id: 'female_us_2', label: 'Aria (US)' },
  { id: 'male_us_2', label: 'Chris (US)' },
  { id: 'male_uk', label: 'Ryan (UK)' },
  { id: 'female_uk', label: 'Sonia (UK)' },
  { id: 'female_ng', label: 'Ezinne (NG)' },
  { id: 'male_ng', label: 'Abeo (NG)' },
];

const NEWS_FREQUENCIES = [
  { value: 60, label: 'Every hour' },
  { value: 120, label: 'Every 2 hours' },
  { value: 240, label: 'Every 4 hours' },
];
const R2_URL = 'https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev';

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// JINGLES DATABASE
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const JINGLES = {
  "groovie-main-musical": `${R2_URL}/jingles/musical/groovie-main-musical-v2.mp3`,
  "groovie-premium-musical": `${R2_URL}/jingles/musical/groovie-premium-jingle.mp3`,
  "groovie-station-sting-2": `${R2_URL}/jingles/musical/groovie-station-sting-2.mp3`,
  "latin-musical": `${R2_URL}/jingles/musical/latin-musical.mp3`,
  "afrobeats-pulse-musical": `${R2_URL}/jingles/musical/afrobeats-pulse-musical.mp3`,
  "hot-hits-pop-musical-1": `${R2_URL}/jingles/musical/hot-hits-pop-musical-1.mp3`,
  "hot-hits-pop-musical-2": `${R2_URL}/jingles/musical/hot-hits-pop-musical-2.mp3`,
  "rnb-vibes-musical": `${R2_URL}/jingles/musical/rnb-vibes-musical.mp3`,
  "smooth-jazz-lounge-musical": `${R2_URL}/jingles/musical/smooth-jazz-lounge-musical.mp3`,
  "hiphop-musical": `${R2_URL}/jingles/musical/hiphop-musical.mp3`,
  "groovie-kids-musical": `${R2_URL}/jingles/musical/groovie-kids-musical.mp3`,
  "station-intro": `${R2_URL}/jingles/groovie-station-intro.mp3`,
  "station-outro": `${R2_URL}/jingles/groovie-station-outro.mp3`,
  "ad-break": `${R2_URL}/jingles/groovie-ad-break.mp3`,
  "ad-return": `${R2_URL}/jingles/groovie-ad-return.mp3`,
  "morning": `${R2_URL}/jingles/groovie-morning.mp3`,
  "late-night": `${R2_URL}/jingles/groovie-late-night.mp3`,
  "transition": `${R2_URL}/jingles/groovie-transition-short.mp3`,
  "latin-voice": `${R2_URL}/jingles/jingle-latin-intro.mp3`,
  "new-faith-radio-voice": `${R2_URL}/jingles/jingle-new-faith-radio-intro.mp3`,
  "afrobeats-pulse-voice": `${R2_URL}/jingles/jingle-afrobeats-pulse-intro.mp3`,
  "hot-hits-pop-voice": `${R2_URL}/jingles/jingle-hot-hits-pop-intro.mp3`,
  "rnb-vibes-voice": `${R2_URL}/jingles/jingle-rnb-vibes-intro.mp3`,
  "hiphop-voice": `${R2_URL}/jingles/jingle-hiphop-intro.mp3`,
  "smooth-jazz-lounge-voice": `${R2_URL}/jingles/jingle-smooth-jazz-lounge-intro.mp3`,
  "groovie-kids-voice": `${R2_URL}/jingles/jingle-groovie-kids.mp3`,
  "groovie-laughs-voice": `${R2_URL}/jingles/jingle-groovie-laughs.mp3`,
  "groovie-drama-voice": `${R2_URL}/jingles/jingle-drama.mp3`,
  "groovie-thinklab-voice": `${R2_URL}/jingles/jingle-thinklab.mp3`,
  "groovie-worlds-voice": `${R2_URL}/jingles/jingle-worlds.mp3`,
  "groovie-afterhours-voice": `${R2_URL}/jingles/jingle-afterhours.mp3`,
};

const STATION_JINGLES = {
  // HISTORICAL RENAME: 'glory-days-gospel' is a STALE SLUG from the pre-rename Gospel station; this station is now LATIN (name/genre/tracks/jingles). Do NOT rename the slug or revert the name to Gospel — it's load-bearing (STATION_TRACKS + jingle map + STATION_ICONS keys, and persisted in listener favorites/localStorage).
  "glory-days-gospel": { musical: "latin-musical", voice: "latin-voice" },
  "new-faith-radio": { musical: "groovie-premium-musical", voice: "new-faith-radio-voice" },
  "afrobeats-pulse": { musical: "afrobeats-pulse-musical", voice: "afrobeats-pulse-voice" },
  "hot-hits-pop": { musical: ["hot-hits-pop-musical-1", "hot-hits-pop-musical-2"], voice: "hot-hits-pop-voice" },
  "rnb-vibes": { musical: "rnb-vibes-musical", voice: "rnb-vibes-voice" },
  "inner-signal": { musical: "hiphop-musical", voice: "hiphop-voice" },
  "jazz-lounge": { musical: "smooth-jazz-lounge-musical", voice: "smooth-jazz-lounge-voice" },
  "kings-kids": { musical: "groovie-kids-musical", voice: "groovie-kids-voice" },
  "groovie-kids": { musical: ["groovie-kids-musical", "groovie-station-sting-2"], voice: "groovie-kids-voice" },
  "groovie-laughs": { musical: ["groovie-premium-musical", "groovie-station-sting-2"], voice: "groovie-laughs-voice" },
  "groovie-drama": { musical: ["groovie-premium-musical", "groovie-station-sting-2"], voice: "groovie-drama-voice" },
  "groovie-thinklab": { musical: ["groovie-premium-musical", "groovie-station-sting-2"], voice: "groovie-thinklab-voice" },
  "groovie-worlds": { musical: ["groovie-premium-musical", "groovie-station-sting-2"], voice: "groovie-worlds-voice" },
  "groovie-afterhours": { musical: ["groovie-premium-musical", "groovie-station-sting-2"], voice: "groovie-afterhours-voice" },
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// CHANNEL AUDIO (31 AI Content Pieces)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const CHANNEL_AUDIO = {
  "groovie-afterhours": [
    {"id": "groovie-afterhours-reflection-0b12f453", "title": "Reflection: finding peace with uncertainty", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-0b12f453.mp3`},
    {"id": "groovie-afterhours-reflection-f9dbe19b", "title": "Reflection: the people who shaped us", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-f9dbe19b.mp3`},
    {"id": "groovie-afterhours-reflection-2adccd17", "title": "Reflection: the people who shaped us", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-2adccd17.mp3`},
    {"id": "groovie-afterhours-reflection-1190e948", "title": "Reflection: the beauty in ordinary moments", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-1190e948.mp3`},
    {"id": "groovie-afterhours-reflection-74909922", "title": "Reflection: the people who shaped us", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-74909922.mp3`},
    {"id": "groovie-afterhours-reflection-89cb9850", "title": "Reflection: the roads not taken", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-89cb9850.mp3`},
    {"id": "groovie-afterhours-reflection-e28aac7b", "title": "Reflection: the roads not taken", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-e28aac7b.mp3`},
    {"id": "groovie-afterhours-reflection-cc91d338", "title": "Reflection: what we leave behind", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-cc91d338.mp3`},
    {"id": "groovie-afterhours-reflection-fa85fea9", "title": "Reflection: what home really means", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-fa85fea9.mp3`},
    {"id": "groovie-afterhours-reflection-8ae016b7", "title": "Reflection: the conversations we replay", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-8ae016b7.mp3`},
    {"id": "groovie-afterhours-reflection-18b46bf6", "title": "Reflection: learning to be alone", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-18b46bf6.mp3`},
    {"id": "groovie-afterhours-reflection-ece0a18e", "title": "Reflection: the things we carry", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-ece0a18e.mp3`},
    {"id": "groovie-afterhours-reflection-92a456a8", "title": "Reflection: why some nights feel longer", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-92a456a8.mp3`},
    {"id": "groovie-afterhours-reflection-8d91ccb1", "title": "Reflection: the art of doing nothing", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-8d91ccb1.mp3`},
    {"id": "groovie-afterhours-reflection-6a86111c", "title": "Reflection: letters to your younger self", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-6a86111c.mp3`},
    {"id": "groovie-afterhours-reflection-a12018f7", "title": "Reflection: the friends who drifted away", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-a12018f7.mp3`},
    {"id": "groovie-afterhours-reflection-38441cee", "title": "Reflection: why we hold on", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-38441cee.mp3`},
    {"id": "groovie-afterhours-reflection-9fd0c4bf", "title": "Reflection: the space between who you are and who yo", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-9fd0c4bf.mp3`},
    {"id": "groovie-afterhours-reflection-ece2e865", "title": "Reflection: the comfort of rain", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-ece2e865.mp3`},
    {"id": "groovie-afterhours-reflection-7888fc91", "title": "Reflection: things only the night understands", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-7888fc91.mp3`},
    {"id": "groovie-afterhours-reflection-301a2add", "title": "Reflection: the last page of a good book", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-301a2add.mp3`},
    {"id": "groovie-afterhours-reflection-78b2a4dc", "title": "Reflection: places that remember you", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-78b2a4dc.mp3`},
    {"id": "groovie-afterhours-reflection-0068f7b4", "title": "Reflection: the sound of an empty house", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-0068f7b4.mp3`},
    {"id": "groovie-afterhours-reflection-4f276001", "title": "Reflection: what forgiveness feels like", "host": "Isaac Night", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-4f276001.mp3`},
    {"id": "groovie-afterhours-reflection-66277141", "title": "Reflection: the moment before sleep", "host": "Luna Veil", "type": "reflection", "audio_url": `${R2_URL}/channels/groovie-afterhours/groovie-afterhours-reflection-66277141.mp3`},
  ],
  "groovie-drama": [
    {"id": "groovie-drama-hope-1b82afab", "title": "Hope: overcoming fear", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-1b82afab.mp3`},
    {"id": "groovie-drama-hope-a814db7b", "title": "Hope: second chances", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-a814db7b.mp3`},
    {"id": "groovie-drama-hope-f2c18062", "title": "Hope: unexpected kindness", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-f2c18062.mp3`},
    {"id": "groovie-drama-hope-6b94ee5b", "title": "Hope: second chances", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-6b94ee5b.mp3`},
    {"id": "groovie-drama-hope-1bdb3087", "title": "Hope: overcoming fear", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-1bdb3087.mp3`},
    {"id": "groovie-drama-hope-2b321205", "title": "Hope: overcoming fear", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-2b321205.mp3`},
    {"id": "groovie-drama-hope-7bd886a0", "title": "Hope: finding forgiveness", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-7bd886a0.mp3`},
    {"id": "groovie-drama-hope-03e9bbc5", "title": "Hope: reconnecting with family", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-03e9bbc5.mp3`},
    {"id": "groovie-drama-hope-878e6f20", "title": "Hope: a letter never sent", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-878e6f20.mp3`},
    {"id": "groovie-drama-hope-60bce1b9", "title": "Hope: the stranger who changed everything", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-60bce1b9.mp3`},
    {"id": "groovie-drama-hope-b305c818", "title": "Hope: learning to let go", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-b305c818.mp3`},
    {"id": "groovie-drama-hope-4a0f273f", "title": "Hope: the last conversation", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-4a0f273f.mp3`},
    {"id": "groovie-drama-hope-9866f2e6", "title": "Hope: starting over in a new city", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-9866f2e6.mp3`},
    {"id": "groovie-drama-hope-8de1310d", "title": "Hope: a promise kept", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-8de1310d.mp3`},
    {"id": "groovie-drama-hope-4b7b8eb9", "title": "Hope: the day everything shifted", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-4b7b8eb9.mp3`},
    {"id": "groovie-drama-hope-b8ac7880", "title": "Hope: finding home", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-b8ac7880.mp3`},
    {"id": "groovie-drama-hope-3811564c", "title": "Hope: the courage to speak up", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-3811564c.mp3`},
    {"id": "groovie-drama-hope-8e093c56", "title": "Hope: a friendship rekindled", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-8e093c56.mp3`},
    {"id": "groovie-drama-hope-421b3c11", "title": "Hope: the gift of silence", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-421b3c11.mp3`},
    {"id": "groovie-drama-hope-9d05ffec", "title": "Hope: walking away from comfort", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-9d05ffec.mp3`},
    {"id": "groovie-drama-hope-a28e5da4", "title": "Hope: rediscovering joy", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-a28e5da4.mp3`},
    {"id": "groovie-drama-hope-8baf7c9a", "title": "Hope: the weight of a secret", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-8baf7c9a.mp3`},
    {"id": "groovie-drama-hope-2cbd1bd2", "title": "Hope: choosing yourself", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-2cbd1bd2.mp3`},
    {"id": "groovie-drama-hope-32b408f1", "title": "Hope: when the music stopped", "host": "Marcus Vale", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-32b408f1.mp3`},
    {"id": "groovie-drama-hope-53ba4ec2", "title": "Hope: an apology twenty years late", "host": "Evelyn Noir", "type": "hope", "audio_url": `${R2_URL}/channels/groovie-drama/groovie-drama-hope-53ba4ec2.mp3`},
  ],
  "groovie-kids": [
    {"id": "groovie-kids-wonder_fact-9153160a", "title": "Wonder Fact: weather", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-9153160a.mp3`},
    {"id": "groovie-kids-wonder_fact-fe8b2939", "title": "Wonder Fact: volcanoes", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-fe8b2939.mp3`},
    {"id": "groovie-kids-wonder_fact-8d40ca38", "title": "Wonder Fact: oceans", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-8d40ca38.mp3`},
    {"id": "groovie-kids-wonder_fact-ff394163", "title": "Wonder Fact: robots", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-ff394163.mp3`},
    {"id": "groovie-kids-wonder_fact-51384fb2", "title": "Wonder Fact: elephants", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-51384fb2.mp3`},
    {"id": "groovie-kids-wonder_fact-cf293476", "title": "Wonder Fact: dinosaurs", "host": "Milo", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-cf293476.mp3`},
    {"id": "groovie-kids-wonder_fact-92522c14", "title": "Wonder Fact: space", "host": "Nova", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-92522c14.mp3`},
    {"id": "groovie-kids-wonder_fact-3aed43b2", "title": "Wonder Fact: animals", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-3aed43b2.mp3`},
    {"id": "groovie-kids-wonder_fact-7d14dd07", "title": "Wonder Fact: the human body", "host": "Milo", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-7d14dd07.mp3`},
    {"id": "groovie-kids-wonder_fact-756395f9", "title": "Wonder Fact: insects", "host": "Nova", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-756395f9.mp3`},
    {"id": "groovie-kids-wonder_fact-4c754e86", "title": "Wonder Fact: rainforests", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-4c754e86.mp3`},
    {"id": "groovie-kids-wonder_fact-13c6b2b5", "title": "Wonder Fact: the moon", "host": "Milo", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-13c6b2b5.mp3`},
    {"id": "groovie-kids-wonder_fact-89040491", "title": "Wonder Fact: dolphins", "host": "Nova", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-89040491.mp3`},
    {"id": "groovie-kids-wonder_fact-5da0af50", "title": "Wonder Fact: earthquakes", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-5da0af50.mp3`},
    {"id": "groovie-kids-wonder_fact-8f429db7", "title": "Wonder Fact: butterflies", "host": "Milo", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-8f429db7.mp3`},
    {"id": "groovie-kids-wonder_fact-c82d129c", "title": "Wonder Fact: the sun", "host": "Nova", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-c82d129c.mp3`},
    {"id": "groovie-kids-wonder_fact-a2780a29", "title": "Wonder Fact: polar bears", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-a2780a29.mp3`},
    {"id": "groovie-kids-wonder_fact-d6927623", "title": "Wonder Fact: lightning", "host": "Milo", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-d6927623.mp3`},
    {"id": "groovie-kids-wonder_fact-e7cba06a", "title": "Wonder Fact: coral reefs", "host": "Nova", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-e7cba06a.mp3`},
    {"id": "groovie-kids-wonder_fact-ff0c2ac9", "title": "Wonder Fact: planets", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-ff0c2ac9.mp3`},
    {"id": "groovie-kids-wonder_fact-a552eef8", "title": "Wonder Fact: fossils", "host": "Milo", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-a552eef8.mp3`},
    {"id": "groovie-kids-wonder_fact-3eb51a4a", "title": "Wonder Fact: penguins", "host": "Nova", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-3eb51a4a.mp3`},
    {"id": "groovie-kids-wonder_fact-2c0cbc24", "title": "Wonder Fact: tornadoes", "host": "Sage", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-2c0cbc24.mp3`},
    {"id": "groovie-kids-wonder_fact-a89c3d82", "title": "Wonder Fact: the deep sea", "host": "Milo", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-a89c3d82.mp3`},
    {"id": "groovie-kids-wonder_fact-c6c7192a", "title": "Wonder Fact: crystals", "host": "Nova", "type": "wonder_fact", "audio_url": `${R2_URL}/channels/groovie-kids/groovie-kids-wonder_fact-c6c7192a.mp3`},
  ],
  "groovie-laughs": [
    {"id": "groovie-laughs-rapid_fire-02571e92", "title": "Rapid Fire: dating apps", "host": "Jay Flex", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-02571e92.mp3`},
    {"id": "groovie-laughs-rapid_fire-2173df48", "title": "Rapid Fire: streaming services", "host": "Bola Bounce", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-2173df48.mp3`},
    {"id": "groovie-laughs-rapid_fire-0188fda8", "title": "Rapid Fire: coffee addiction", "host": "Bola Bounce", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-0188fda8.mp3`},
    {"id": "groovie-laughs-rapid_fire-033dc6ec", "title": "Rapid Fire: working from home", "host": "Jay Flex", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-033dc6ec.mp3`},
    {"id": "groovie-laughs-rapid_fire-da6e6b58", "title": "Rapid Fire: social media", "host": "Oliver Dry", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-da6e6b58.mp3`},
    {"id": "groovie-laughs-rapid_fire-3b81d84d", "title": "Rapid Fire: gym culture", "host": "Bola Bounce", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-3b81d84d.mp3`},
    {"id": "groovie-laughs-rapid_fire-93c2f6be", "title": "Rapid Fire: AI assistants", "host": "Jay Flex", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-93c2f6be.mp3`},
    {"id": "groovie-laughs-rapid_fire-1f6fa551", "title": "Rapid Fire: adulting", "host": "Oliver Dry", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-1f6fa551.mp3`},
    {"id": "groovie-laughs-rapid_fire-dea78e61", "title": "Rapid Fire: online shopping", "host": "Bola Bounce", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-dea78e61.mp3`},
    {"id": "groovie-laughs-rapid_fire-96d5473d", "title": "Rapid Fire: food delivery", "host": "Jay Flex", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-96d5473d.mp3`},
    {"id": "groovie-laughs-rapid_fire-a3f67a15", "title": "Rapid Fire: pet owners", "host": "Oliver Dry", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-a3f67a15.mp3`},
    {"id": "groovie-laughs-rapid_fire-65787bb0", "title": "Rapid Fire: public transport", "host": "Bola Bounce", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-65787bb0.mp3`},
    {"id": "groovie-laughs-rapid_fire-e1e0497f", "title": "Rapid Fire: Monday mornings", "host": "Jay Flex", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-e1e0497f.mp3`},
    {"id": "groovie-laughs-rapid_fire-9ac69964", "title": "Rapid Fire: autocorrect fails", "host": "Oliver Dry", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-9ac69964.mp3`},
    {"id": "groovie-laughs-rapid_fire-44444f7c", "title": "Rapid Fire: group chats", "host": "Bola Bounce", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-44444f7c.mp3`},
    {"id": "groovie-laughs-rapid_fire-3a84affa", "title": "Rapid Fire: video calls", "host": "Jay Flex", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-3a84affa.mp3`},
    {"id": "groovie-laughs-rapid_fire-e87212d3", "title": "Rapid Fire: airplane travel", "host": "Oliver Dry", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-e87212d3.mp3`},
    {"id": "groovie-laughs-rapid_fire-046525bd", "title": "Rapid Fire: cooking disasters", "host": "Bola Bounce", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-046525bd.mp3`},
    {"id": "groovie-laughs-rapid_fire-aef815db", "title": "Rapid Fire: new year resolutions", "host": "Jay Flex", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-aef815db.mp3`},
    {"id": "groovie-laughs-rapid_fire-544e6b8d", "title": "Rapid Fire: sleep habits", "host": "Oliver Dry", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-544e6b8d.mp3`},
    {"id": "groovie-laughs-rapid_fire-7f3425cc", "title": "Rapid Fire: smart home devices", "host": "Bola Bounce", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-7f3425cc.mp3`},
    {"id": "groovie-laughs-rapid_fire-c3724c9d", "title": "Rapid Fire: parking", "host": "Jay Flex", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-c3724c9d.mp3`},
    {"id": "groovie-laughs-rapid_fire-52b18cb2", "title": "Rapid Fire: office meetings", "host": "Oliver Dry", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-52b18cb2.mp3`},
    {"id": "groovie-laughs-rapid_fire-d84f0511", "title": "Rapid Fire: influencers", "host": "Bola Bounce", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-d84f0511.mp3`},
    {"id": "groovie-laughs-rapid_fire-d84fbed1", "title": "Rapid Fire: texting etiquette", "host": "Jay Flex", "type": "rapid_fire", "audio_url": `${R2_URL}/channels/groovie-laughs/groovie-laughs-rapid_fire-d84fbed1.mp3`},
  ],
  "groovie-thinklab": [
    {"id": "groovie-thinklab-explainer-88149edf", "title": "Explainer: why time feels different", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-88149edf.mp3`},
    {"id": "groovie-thinklab-explainer-2468d64c", "title": "Explainer: the science of music", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-2468d64c.mp3`},
    {"id": "groovie-thinklab-explainer-acd7fced", "title": "Explainer: why time feels different", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-acd7fced.mp3`},
    {"id": "groovie-thinklab-explainer-ae3c21c2", "title": "Explainer: the science of music", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-ae3c21c2.mp3`},
    {"id": "groovie-thinklab-explainer-181f78bd", "title": "Explainer: how habits form", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-181f78bd.mp3`},
    {"id": "groovie-thinklab-explainer-2105cf43", "title": "Explainer: how memory works", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-2105cf43.mp3`},
    {"id": "groovie-thinklab-explainer-2d476df6", "title": "Explainer: why we dream", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-2d476df6.mp3`},
    {"id": "groovie-thinklab-explainer-a7bdd65a", "title": "Explainer: the placebo effect", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-a7bdd65a.mp3`},
    {"id": "groovie-thinklab-explainer-d3a325ce", "title": "Explainer: why we yawn", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-d3a325ce.mp3`},
    {"id": "groovie-thinklab-explainer-d5a5a3ef", "title": "Explainer: how languages evolve", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-d5a5a3ef.mp3`},
    {"id": "groovie-thinklab-explainer-cbe22a50", "title": "Explainer: the psychology of color", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-cbe22a50.mp3`},
    {"id": "groovie-thinklab-explainer-8d73eca8", "title": "Explainer: why we forget", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-8d73eca8.mp3`},
    {"id": "groovie-thinklab-explainer-94c6fe09", "title": "Explainer: how caffeine works", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-94c6fe09.mp3`},
    {"id": "groovie-thinklab-explainer-755d1d6d", "title": "Explainer: the science of laughter", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-755d1d6d.mp3`},
    {"id": "groovie-thinklab-explainer-526f6201", "title": "Explainer: why some songs get stuck in your head", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-526f6201.mp3`},
    {"id": "groovie-thinklab-explainer-3e964977", "title": "Explainer: how your gut affects your mood", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-3e964977.mp3`},
    {"id": "groovie-thinklab-explainer-f8f7ca25", "title": "Explainer: the mathematics of beauty", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-f8f7ca25.mp3`},
    {"id": "groovie-thinklab-explainer-e7218bbe", "title": "Explainer: why we procrastinate", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-e7218bbe.mp3`},
    {"id": "groovie-thinklab-explainer-d730878c", "title": "Explainer: how optical illusions work", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-d730878c.mp3`},
    {"id": "groovie-thinklab-explainer-7c461cc3", "title": "Explainer: the science of smell", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-7c461cc3.mp3`},
    {"id": "groovie-thinklab-explainer-cbd7c00b", "title": "Explainer: why we cry", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-cbd7c00b.mp3`},
    {"id": "groovie-thinklab-explainer-fe83b904", "title": "Explainer: how birds navigate", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-fe83b904.mp3`},
    {"id": "groovie-thinklab-explainer-759bfbf4", "title": "Explainer: the history of zero", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-759bfbf4.mp3`},
    {"id": "groovie-thinklab-explainer-1ef3df40", "title": "Explainer: why silence feels uncomfortable", "host": "Dr. Maya", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-1ef3df40.mp3`},
    {"id": "groovie-thinklab-explainer-8419835f", "title": "Explainer: how gravity works", "host": "Professor Kai", "type": "explainer", "audio_url": `${R2_URL}/channels/groovie-thinklab/groovie-thinklab-explainer-8419835f.mp3`},
  ],
  "groovie-worlds": [
    {"id": "groovie-worlds-postcard-9d6fed2e", "title": "Postcard: a Kenyan safari sunrise", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-9d6fed2e.mp3`},
    {"id": "groovie-worlds-postcard-db0c7321", "title": "Postcard: a Buenos Aires tango hall", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-db0c7321.mp3`},
    {"id": "groovie-worlds-postcard-71ea2812", "title": "Postcard: a Kenyan safari sunrise", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-71ea2812.mp3`},
    {"id": "groovie-worlds-postcard-0aaa3bc0", "title": "Postcard: a Buenos Aires tango hall", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-0aaa3bc0.mp3`},
    {"id": "groovie-worlds-postcard-e6b1a2cd", "title": "Postcard: a Kenyan safari sunrise", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-e6b1a2cd.mp3`},
    {"id": "groovie-worlds-postcard-b73e12c8", "title": "Postcard: a Tokyo fish market at dawn", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-b73e12c8.mp3`},
    {"id": "groovie-worlds-postcard-5abf33c6", "title": "Postcard: a Moroccan riad at sunset", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-5abf33c6.mp3`},
    {"id": "groovie-worlds-postcard-341a9730", "title": "Postcard: an Istanbul bazaar", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-341a9730.mp3`},
    {"id": "groovie-worlds-postcard-0d4aa7c8", "title": "Postcard: a Venice canal at midnight", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-0d4aa7c8.mp3`},
    {"id": "groovie-worlds-postcard-9198949e", "title": "Postcard: a Bangkok street food alley", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-9198949e.mp3`},
    {"id": "groovie-worlds-postcard-22992410", "title": "Postcard: a Scottish highland trail", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-22992410.mp3`},
    {"id": "groovie-worlds-postcard-cc6789a6", "title": "Postcard: a Rio de Janeiro beach at golden hour", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-cc6789a6.mp3`},
    {"id": "groovie-worlds-postcard-3122e1e1", "title": "Postcard: a Parisian cafe on a rainy morning", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-3122e1e1.mp3`},
    {"id": "groovie-worlds-postcard-41d69683", "title": "Postcard: a New York City rooftop at night", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-41d69683.mp3`},
    {"id": "groovie-worlds-postcard-8d1cd3a6", "title": "Postcard: a Bali rice terrace at dawn", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-8d1cd3a6.mp3`},
    {"id": "groovie-worlds-postcard-c9dd35b4", "title": "Postcard: a Cairo spice market", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-c9dd35b4.mp3`},
    {"id": "groovie-worlds-postcard-e1c810b5", "title": "Postcard: a Norwegian fjord in winter", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-e1c810b5.mp3`},
    {"id": "groovie-worlds-postcard-1c63230d", "title": "Postcard: a Cuban jazz bar in Havana", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-1c63230d.mp3`},
    {"id": "groovie-worlds-postcard-1b9b68b1", "title": "Postcard: a Greek island village at noon", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-1b9b68b1.mp3`},
    {"id": "groovie-worlds-postcard-2edb5733", "title": "Postcard: a Seoul neon-lit alley", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-2edb5733.mp3`},
    {"id": "groovie-worlds-postcard-c8f4b1cb", "title": "Postcard: a Marrakech courtyard garden", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-c8f4b1cb.mp3`},
    {"id": "groovie-worlds-postcard-e76b68dd", "title": "Postcard: an Amsterdam canal in autumn", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-e76b68dd.mp3`},
    {"id": "groovie-worlds-postcard-55845cdd", "title": "Postcard: a Cape Town winery at sunset", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-55845cdd.mp3`},
    {"id": "groovie-worlds-postcard-cf8b84d6", "title": "Postcard: a Dublin pub on a Friday night", "host": "Elena Cross", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-cf8b84d6.mp3`},
    {"id": "groovie-worlds-postcard-26ed0761", "title": "Postcard: a Petra cave at sunrise", "host": "Omar Atlas", "type": "postcard", "audio_url": `${R2_URL}/channels/groovie-worlds/groovie-worlds-postcard-26ed0761.mp3`},
  ],
  "kings-kids": [
    {"id": "kings-kids-story-f0199d68", "title": "Bible Story: David and Goliath", "host": "Milo", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-f0199d68.mp3`},
    {"id": "kings-kids-story-c426a071", "title": "Bible Story: Noah's Ark", "host": "Nova", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-c426a071.mp3`},
    {"id": "kings-kids-story-b2b70639", "title": "Bible Story: Daniel and the Lions", "host": "Sage", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-b2b70639.mp3`},
    {"id": "kings-kids-story-b591997d", "title": "Bible Story: The Good Samaritan", "host": "Milo", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-b591997d.mp3`},
    {"id": "kings-kids-story-ffcb8ed0", "title": "Bible Story: Jonah and the Whale", "host": "Nova", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-ffcb8ed0.mp3`},
    {"id": "kings-kids-story-b1edb1cc", "title": "Bible Story: Moses parts the Red Sea", "host": "Sage", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-b1edb1cc.mp3`},
    {"id": "kings-kids-story-5f59b6a2", "title": "Bible Story: The birth of Jesus", "host": "Milo", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-5f59b6a2.mp3`},
    {"id": "kings-kids-story-a798b0ce", "title": "Bible Story: Jesus feeds five thousand", "host": "Nova", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-a798b0ce.mp3`},
    {"id": "kings-kids-story-b03aefba", "title": "Bible Story: Joseph's colorful coat", "host": "Sage", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-b03aefba.mp3`},
    {"id": "kings-kids-story-e54572a6", "title": "Bible Story: The story of Ruth", "host": "Milo", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-e54572a6.mp3`},
    {"id": "kings-kids-story-b0b5bf71", "title": "Bible Story: Zacchaeus the tax collector", "host": "Nova", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-b0b5bf71.mp3`},
    {"id": "kings-kids-story-3dac84d8", "title": "Bible Story: The Prodigal Son", "host": "Sage", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-3dac84d8.mp3`},
    {"id": "kings-kids-story-5ee898b1", "title": "Bible Story: Jesus walks on water", "host": "Milo", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-5ee898b1.mp3`},
    {"id": "kings-kids-story-dcb57c2f", "title": "Bible Story: The walls of Jericho", "host": "Nova", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-dcb57c2f.mp3`},
    {"id": "kings-kids-story-3fe92cd1", "title": "Bible Story: Queen Esther's bravery", "host": "Sage", "type": "bible_story", "audio_url": `${R2_URL}/channels/kings-kids/kings-kids-story-3fe92cd1.mp3`},
  ],
};

const PRESENTER_TRANSITIONS = {
  "groovie-afterhours": {
    "enjoy": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-enjoy-groovie-afterhours-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-enjoy-groovie-afterhours-male.mp3",
    },
    "intro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-intro-groovie-afterhours-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-intro-groovie-afterhours-male.mp3",
    },
    "now_playing": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-now_playing-groovie-afterhours-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-now_playing-groovie-afterhours-male.mp3",
    },
    "outro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-outro-groovie-afterhours-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-outro-groovie-afterhours-male.mp3",
    },
    "stay_tuned": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-stay_tuned-groovie-afterhours-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-stay_tuned-groovie-afterhours-male.mp3",
    },
    "well_be_back": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-well_be_back-groovie-afterhours-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-afterhours/presenter-well_be_back-groovie-afterhours-male.mp3",
    },
  },
  "groovie-drama": {
    "enjoy": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-enjoy-groovie-drama-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-enjoy-groovie-drama-male.mp3",
    },
    "intro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-intro-groovie-drama-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-intro-groovie-drama-male.mp3",
    },
    "now_playing": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-now_playing-groovie-drama-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-now_playing-groovie-drama-male.mp3",
    },
    "outro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-outro-groovie-drama-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-outro-groovie-drama-male.mp3",
    },
    "stay_tuned": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-stay_tuned-groovie-drama-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-stay_tuned-groovie-drama-male.mp3",
    },
    "well_be_back": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-well_be_back-groovie-drama-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-drama/presenter-well_be_back-groovie-drama-male.mp3",
    },
  },
  "groovie-kids": {
    "enjoy": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-enjoy-groovie-kids-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-enjoy-groovie-kids-male.mp3",
    },
    "intro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-intro-groovie-kids-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-intro-groovie-kids-male.mp3",
    },
    "now_playing": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-now_playing-groovie-kids-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-now_playing-groovie-kids-male.mp3",
    },
    "outro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-outro-groovie-kids-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-outro-groovie-kids-male.mp3",
    },
    "stay_tuned": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-stay_tuned-groovie-kids-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-stay_tuned-groovie-kids-male.mp3",
    },
    "well_be_back": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-well_be_back-groovie-kids-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-kids/presenter-well_be_back-groovie-kids-male.mp3",
    },
  },
  "groovie-laughs": {
    "enjoy": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-enjoy-groovie-laughs-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-enjoy-groovie-laughs-male.mp3",
    },
    "intro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-intro-groovie-laughs-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-intro-groovie-laughs-male.mp3",
    },
    "now_playing": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-now_playing-groovie-laughs-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-now_playing-groovie-laughs-male.mp3",
    },
    "outro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-outro-groovie-laughs-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-outro-groovie-laughs-male.mp3",
    },
    "stay_tuned": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-stay_tuned-groovie-laughs-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-stay_tuned-groovie-laughs-male.mp3",
    },
    "well_be_back": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-well_be_back-groovie-laughs-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-laughs/presenter-well_be_back-groovie-laughs-male.mp3",
    },
  },
  "groovie-thinklab": {
    "enjoy": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-enjoy-groovie-thinklab-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-enjoy-groovie-thinklab-male.mp3",
    },
    "intro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-intro-groovie-thinklab-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-intro-groovie-thinklab-male.mp3",
    },
    "now_playing": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-now_playing-groovie-thinklab-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-now_playing-groovie-thinklab-male.mp3",
    },
    "outro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-outro-groovie-thinklab-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-outro-groovie-thinklab-male.mp3",
    },
    "stay_tuned": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-stay_tuned-groovie-thinklab-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-stay_tuned-groovie-thinklab-male.mp3",
    },
    "well_be_back": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-well_be_back-groovie-thinklab-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-thinklab/presenter-well_be_back-groovie-thinklab-male.mp3",
    },
  },
  "groovie-worlds": {
    "enjoy": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-enjoy-groovie-worlds-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-enjoy-groovie-worlds-male.mp3",
    },
    "intro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-intro-groovie-worlds-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-intro-groovie-worlds-male.mp3",
    },
    "now_playing": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-now_playing-groovie-worlds-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-now_playing-groovie-worlds-male.mp3",
    },
    "outro": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-outro-groovie-worlds-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-outro-groovie-worlds-male.mp3",
    },
    "stay_tuned": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-stay_tuned-groovie-worlds-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-stay_tuned-groovie-worlds-male.mp3",
    },
    "well_be_back": {
      "female": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-well_be_back-groovie-worlds-female.mp3",
      "male": "https://pub-9c3dec046fa24a379b1b0d605ae975d2.r2.dev/transitions/groovie-worlds/presenter-well_be_back-groovie-worlds-male.mp3",
    },
  },
};

// Helper: get random presenter transition clip
const getTransition = (showSlug, type) => {
  const show = PRESENTER_TRANSITIONS[showSlug];
  if (!show || !show[type]) return null;
  const voice = Math.random() > 0.5 ? 'male' : 'female';
  const url = show[type][voice];
  if (!url) return null;
  return { type: 'transition', subtype: type, title: type.replace(/_/g, ' '), audio_url: url };
};


// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// ARTISTS & STATION MUSIC
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const ARTISTS = {
  "ayo-vantage": { name: "Ayo Vantage", genre: "Afrobeats" },
  "cesar-luis": { name: "Cesar Luis", genre: "Latin" },
  "femfem": { name: "Femfem", genre: "Afro-fusion" },
  "jazz": { name: "Various Jazz Artists", genre: "Jazz" },
  "kairos": { name: "Kairos", genre: "R&B" },
  "lil-reina": { name: "Lil Reina", genre: "Latin" },
  "lola-mills-jazz": { name: "Lola Mills", genre: "Jazz" },
  "luna-vale-pop": { name: "Luna Vale", genre: "Pop" },
  "luna-vega": { name: "Luna Vega", genre: "Latin Reggaeton" },
  "lyon-saint": { name: "Lyon Saint", genre: "Gospel Soul" },
  "nova-crown": { name: "Nova Crown", genre: "Hip Hop" },
  "sanmi-kora": { name: "Sanmi Kora", genre: "Afrobeats" },
  "sunny-and-the-star-crew": { name: "Sunny and the Star Crew", genre: "Christian Kids" },
  "zayden": { name: "Zayden", genre: "Hip Hop" },
  "zuriella": { name: "Zuriella", genre: "Soulful Pop" },
};

const STATION_MUSIC = {
  "afrobeats-pulse": [
    { title: "Anywhere e Sweet!", artist: "ayo-vantage", file: "Anywhere e Sweet!.mp3" },
    { title: "Anywhere e Sweet", artist: "ayo-vantage", file: "Anywhere e Sweet.mp3" },
    { title: "Clean  Money", artist: "ayo-vantage", file: "Clean  Money.mp3" },
    { title: "Currency", artist: "ayo-vantage", file: "Currency.mp3" },
    { title: "Eko", artist: "ayo-vantage", file: "Eko.mp3" },
    { title: "Forever Season", artist: "ayo-vantage", file: "Forever Season.mp3" },
    { title: "Ginger Me", artist: "ayo-vantage", file: "Ginger Me.mp3" },
    { title: "Hold Something", artist: "ayo-vantage", file: "Hold Something.mp3" },
    { title: "If You Know", artist: "ayo-vantage", file: "If You Know.mp3" },
    { title: "Jara", artist: "ayo-vantage", file: "Jara.mp3" },
    { title: "Lavida", artist: "ayo-vantage", file: "Lavida.mp3" },
    { title: "No Rush", artist: "ayo-vantage", file: "No Rush.mp3" },
    { title: "Pressure Soft (Afro Rave)", artist: "ayo-vantage", file: "Pressure Soft (Afro Rave).mp3" },
    { title: "Soft Power", artist: "ayo-vantage", file: "Soft Power.mp3" },
    { title: "Stand Well (Street Anthem)", artist: "ayo-vantage", file: "Stand Well (Street Anthem).mp3" },
    { title: "Tonight naTonight", artist: "ayo-vantage", file: "Tonight naTonight.mp3" },
    { title: "We No Dey Carry Last!", artist: "ayo-vantage", file: "We No Dey Carry Last!.mp3" },
    { title: "Be calming Down", artist: "sanmi-kora", file: "Be calming Down.mp3" },
    { title: "Benefit", artist: "sanmi-kora", file: "Benefit.mp3" },
    { title: "Calm Down", artist: "sanmi-kora", file: "Calm Down.mp3" },
    { title: "Eledumare Knows", artist: "sanmi-kora", file: "Eledumare Knows.mp3" },
    { title: "Enemies Watch me", artist: "sanmi-kora", file: "Enemies Watch me.mp3" },
    { title: "March Am", artist: "sanmi-kora", file: "March Am.mp3" },
    { title: "No Dull  My Energy", artist: "sanmi-kora", file: "No Dull  My Energy.mp3" },
    { title: "No Turn Back", artist: "sanmi-kora", file: "No Turn Back.mp3" },
    { title: "Omo Oodua", artist: "sanmi-kora", file: "Omo Oodua.mp3" },
    { title: "Ori", artist: "sanmi-kora", file: "Ori.mp3" },
    { title: "Shine by force", artist: "sanmi-kora", file: "Shine by force.mp3" },
    { title: "Street Sermon", artist: "sanmi-kora", file: "Street Sermon.mp3" },
    { title: "Wole", artist: "sanmi-kora", file: "Wole.mp3" },
  ],
  // HISTORICAL RENAME: 'glory-days-gospel' is a STALE SLUG from the pre-rename Gospel station; this station is now LATIN (name/genre/tracks/jingles). Do NOT rename the slug or revert the name to Gospel — it's load-bearing (STATION_TRACKS + jingle map + STATION_ICONS keys, and persisted in listener favorites/localStorage).
  "glory-days-gospel": [
    { title: "Bailo Para Él", artist: "lil-reina", file: "Bailo Para Él.mp3" },
    { title: "Crown Easy (Grace on Me)", artist: "lil-reina", file: "Crown Easy (Grace on Me).mp3" },
    { title: "Fue Su Gracia _ It Was Grace", artist: "lil-reina", file: "Fue Su Gracia _ It Was Grace.mp3" },
    { title: "Fuego Santo” (Holy Fire)_ (1)", artist: "lil-reina", file: "Fuego Santo” (Holy Fire)_ (1).mp3" },
    { title: "Fuego Santo” (Holy Fire)_", artist: "lil-reina", file: "Fuego Santo” (Holy Fire)_.mp3" },
    { title: "Gozo Eterno _ Eternal Joy", artist: "lil-reina", file: "Gozo Eterno _ Eternal Joy.mp3" },
    { title: "Gracia Que Baila", artist: "lil-reina", file: "Gracia Que Baila.mp3" },
    { title: "Mi Respirar  _ My Breath", artist: "lil-reina", file: "Mi Respirar  _ My Breath.mp3" },
    { title: "Navidad De Gracia (Christmas of Grace)", artist: "lil-reina", file: "Navidad De Gracia (Christmas of Grace).mp3" },
    { title: "Not That Girl No More", artist: "lil-reina", file: "Not That Girl No More.mp3" },
    { title: "Saint in the Club", artist: "lil-reina", file: "Saint in the Club.mp3" },
    { title: "Todo Para Ti, JESÚS _ All for you Jesus", artist: "lil-reina", file: "Todo Para Ti, JESÚS _ All for you Jesus.mp3" },
    { title: "Tu Amor Me Salva (Your Love Saves Me)", artist: "lil-reina", file: "Tu Amor Me Salva (Your Love Saves Me).mp3" },
    { title: "BAILA SIN MIEDO” (Spanglish Version", artist: "luna-vega", file: "BAILA SIN MIEDO” (Spanglish Version.mp3" },
    { title: "BESOS EN LA SOMBRA", artist: "luna-vega", file: "BESOS EN LA SOMBRA.mp3" },
    { title: "CORAZÓN CALLEJERO", artist: "luna-vega", file: "CORAZÓN CALLEJERO.mp3" },
    { title: "DAME LA NOCHE", artist: "luna-vega", file: "DAME LA NOCHE.mp3" },
    { title: "DESCONTROL", artist: "luna-vega", file: "DESCONTROL.mp3" },
    { title: "ENTRE TÚ Y YO", artist: "luna-vega", file: "ENTRE TÚ Y YO.mp3" },
    { title: "FUEGO LENTO", artist: "luna-vega", file: "FUEGO LENTO.mp3" },
    { title: "LIBRE COMO EL SOL", artist: "luna-vega", file: "LIBRE COMO EL SOL.mp3" },
    { title: "LOCA POR TI", artist: "luna-vega", file: "LOCA POR TI.mp3" },
    { title: "MI TENTACIÓN", artist: "luna-vega", file: "MI TENTACIÓN.mp3" },
    { title: "NO ME BUSQUES", artist: "luna-vega", file: "NO ME BUSQUES.mp3" },
    { title: "RITMO SALVAJE", artist: "luna-vega", file: "RITMO SALVAJE.mp3" },
    { title: "SIN REGLAS", artist: "luna-vega", file: "SIN REGLAS.mp3" },
    { title: "SIN TI NO", artist: "luna-vega", file: "SIN TI NO.mp3" },
    { title: "TE PROVOCO", artist: "luna-vega", file: "TE PROVOCO.mp3" },
    { title: "VEN PA'CA", artist: "luna-vega", file: "VEN PA’CA.mp3" },
    { title: "After Hours Control", artist: "cesar-luis", file: "After Hours Control.mp3" },
    { title: "After the Last Drop", artist: "cesar-luis", file: "After the Last Drop.mp3" },
    { title: "Blackout Tempo", artist: "cesar-luis", file: "Blackout Tempo.mp3" },
    { title: "Don't Ask, Feel", artist: "cesar-luis", file: "Don't Ask, Feel.mp3" },
    { title: "Floor on Fire", artist: "cesar-luis", file: "Floor on Fire.mp3" },
    { title: "Hands Up, Lights Out", artist: "cesar-luis", file: "Hands Up, Lights Out.mp3" },
    { title: "Midnight Heat", artist: "cesar-luis", file: "Midnight Heat.mp3" },
    { title: "Neon Pulse", artist: "cesar-luis", file: "Neon Pulse.mp3" },
    { title: "No Lights, No Limits", artist: "cesar-luis", file: "No Lights, No Limits.mp3" },
    { title: "Ritmo Sin Frenos", artist: "cesar-luis", file: "Ritmo Sin Frenos.mp3" },
    { title: "Slow Burn Signal", artist: "cesar-luis", file: "Slow Burn Signal.mp3" },
    { title: "This Is the Night", artist: "cesar-luis", file: "This Is the Night.mp3" },
  ],
  "hot-hits-pop": [
    { title: "Anywhere e Sweet!", artist: "ayo-vantage", file: "Anywhere e Sweet!.mp3" },
    { title: "Anywhere e Sweet", artist: "ayo-vantage", file: "Anywhere e Sweet.mp3" },
    { title: "Clean  Money", artist: "ayo-vantage", file: "Clean  Money.mp3" },
    { title: "Currency", artist: "ayo-vantage", file: "Currency.mp3" },
    { title: "Eko", artist: "ayo-vantage", file: "Eko.mp3" },
    { title: "Forever Season", artist: "ayo-vantage", file: "Forever Season.mp3" },
    { title: "Ginger Me", artist: "ayo-vantage", file: "Ginger Me.mp3" },
    { title: "Hold Something", artist: "ayo-vantage", file: "Hold Something.mp3" },
    { title: "If You Know", artist: "ayo-vantage", file: "If You Know.mp3" },
    { title: "Jara", artist: "ayo-vantage", file: "Jara.mp3" },
    { title: "Lavida", artist: "ayo-vantage", file: "Lavida.mp3" },
    { title: "No Rush", artist: "ayo-vantage", file: "No Rush.mp3" },
    { title: "Pressure Soft (Afro Rave)", artist: "ayo-vantage", file: "Pressure Soft (Afro Rave).mp3" },
    { title: "Soft Power", artist: "ayo-vantage", file: "Soft Power.mp3" },
    { title: "Stand Well (Street Anthem)", artist: "ayo-vantage", file: "Stand Well (Street Anthem).mp3" },
    { title: "Tonight naTonight", artist: "ayo-vantage", file: "Tonight naTonight.mp3" },
    { title: "We No Dey Carry Last!", artist: "ayo-vantage", file: "We No Dey Carry Last!.mp3" },
    { title: "713 STEADY", artist: "kairos", file: "713 STEADY.mp3" },
    { title: "All Night Long", artist: "kairos", file: "All Night Long.mp3" },
    { title: "Better Days", artist: "kairos", file: "Better Days.mp3" },
    { title: "Broken But Breathing", artist: "kairos", file: "Broken But Breathing.mp3" },
    { title: "City Dreams", artist: "kairos", file: "City Dreams.mp3" },
    { title: "Echoes", artist: "kairos", file: "Echoes.mp3" },
    { title: "EMPIRE MOTION", artist: "kairos", file: "EMPIRE MOTION.mp3" },
    { title: "Fall Again", artist: "kairos", file: "Fall Again.mp3" },
    { title: "Forever Mine", artist: "kairos", file: "Forever Mine.mp3" },
    { title: "Golden Nights", artist: "kairos", file: "Golden Nights.mp3" },
    { title: "Heaven's Timing", artist: "kairos", file: "Heaven's Timing.mp3" },
    { title: "High Life", artist: "kairos", file: "High Life.mp3" },
    { title: "Hold Me Close", artist: "kairos", file: "Hold Me Close.mp3" },
    { title: "Love in Motion", artist: "kairos", file: "Love in Motion.mp3" },
    { title: "Midnight Calls", artist: "kairos", file: "Midnight Calls.mp3" },
    { title: "My Season", artist: "kairos", file: "My Season.mp3" },
    { title: "No Looking Back", artist: "kairos", file: "No Looking Back.mp3" },
    { title: "No More Fear", artist: "kairos", file: "No More Fear.mp3" },
    { title: "On My Way", artist: "kairos", file: "On My Way.mp3" },
    { title: "Promise Ring", artist: "kairos", file: "Promise Ring.mp3" },
    { title: "Rich in Love", artist: "kairos", file: "Rich in Love.mp3" },
    { title: "Run to Me", artist: "kairos", file: "Run to Me.mp3" },
    { title: "Say My  Name", artist: "kairos", file: "Say My  Name.mp3" },
    { title: "Still Yours", artist: "kairos", file: "Still Yours.mp3" },
    { title: "Sunrise Over You", artist: "kairos", file: "Sunrise Over You.mp3" },
    { title: "United We Rise", artist: "kairos", file: "United We Rise.mp3" },
    { title: "We Made It", artist: "kairos", file: "We Made It.mp3" },
    { title: "You & I", artist: "kairos", file: "You & I.mp3" },
    { title: "Bailo Para Él", artist: "lil-reina", file: "Bailo Para Él.mp3" },
    { title: "Crown Easy (Grace on Me)", artist: "lil-reina", file: "Crown Easy (Grace on Me).mp3" },
    { title: "Fue Su Gracia _ It Was Grace", artist: "lil-reina", file: "Fue Su Gracia _ It Was Grace.mp3" },
    { title: "Fuego Santo” (Holy Fire)_ (1)", artist: "lil-reina", file: "Fuego Santo” (Holy Fire)_ (1).mp3" },
    { title: "Fuego Santo” (Holy Fire)_", artist: "lil-reina", file: "Fuego Santo” (Holy Fire)_.mp3" },
    { title: "Gozo Eterno _ Eternal Joy", artist: "lil-reina", file: "Gozo Eterno _ Eternal Joy.mp3" },
    { title: "Gracia Que Baila", artist: "lil-reina", file: "Gracia Que Baila.mp3" },
    { title: "Mi Respirar  _ My Breath", artist: "lil-reina", file: "Mi Respirar  _ My Breath.mp3" },
    { title: "Navidad De Gracia (Christmas of Grace)", artist: "lil-reina", file: "Navidad De Gracia (Christmas of Grace).mp3" },
    { title: "Not That Girl No More", artist: "lil-reina", file: "Not That Girl No More.mp3" },
    { title: "Saint in the Club", artist: "lil-reina", file: "Saint in the Club.mp3" },
    { title: "Todo Para Ti, JESÚS _ All for you Jesus", artist: "lil-reina", file: "Todo Para Ti, JESÚS _ All for you Jesus.mp3" },
    { title: "Tu Amor Me Salva (Your Love Saves Me)", artist: "lil-reina", file: "Tu Amor Me Salva (Your Love Saves Me).mp3" },
    { title: "Almost Famous Night", artist: "luna-vale-pop", file: "Almost Famous Night.mp3" },
    { title: "Better Than Yesterday", artist: "luna-vale-pop", file: "Better Than Yesterday.mp3" },
    { title: "Chasing The Skyline", artist: "luna-vale-pop", file: "Chasing The Skyline.mp3" },
    { title: "Golden Ordinary", artist: "luna-vale-pop", file: "Golden Ordinary.mp3" },
    { title: "Lights Don't Lie", artist: "luna-vale-pop", file: "Lights Don't Lie.mp3" },
    { title: "Run Toward The Noise", artist: "luna-vale-pop", file: "Run Toward The Noise.mp3" },
    { title: "Say It Louder", artist: "luna-vale-pop", file: "Say It Louder.mp3" },
    { title: "Still Learning Me", artist: "luna-vale-pop", file: "Still Learning Me.mp3" },
    { title: "This Is Our Moment", artist: "luna-vale-pop", file: "This Is Our Moment.mp3" },
    { title: "We'll Be Alright", artist: "luna-vale-pop", file: "We'll Be Alright.mp3" },
    { title: "BAILA SIN MIEDO” (Spanglish Version", artist: "luna-vega", file: "BAILA SIN MIEDO” (Spanglish Version.mp3" },
    { title: "BESOS EN LA SOMBRA", artist: "luna-vega", file: "BESOS EN LA SOMBRA.mp3" },
    { title: "CORAZÓN CALLEJERO", artist: "luna-vega", file: "CORAZÓN CALLEJERO.mp3" },
    { title: "DAME LA NOCHE", artist: "luna-vega", file: "DAME LA NOCHE.mp3" },
    { title: "DESCONTROL", artist: "luna-vega", file: "DESCONTROL.mp3" },
    { title: "ENTRE TÚ Y YO", artist: "luna-vega", file: "ENTRE TÚ Y YO.mp3" },
    { title: "FUEGO LENTO", artist: "luna-vega", file: "FUEGO LENTO.mp3" },
    { title: "LIBRE COMO EL SOL", artist: "luna-vega", file: "LIBRE COMO EL SOL.mp3" },
    { title: "LOCA POR TI", artist: "luna-vega", file: "LOCA POR TI.mp3" },
    { title: "MI TENTACIÓN", artist: "luna-vega", file: "MI TENTACIÓN.mp3" },
    { title: "NO ME BUSQUES", artist: "luna-vega", file: "NO ME BUSQUES.mp3" },
    { title: "RITMO SALVAJE", artist: "luna-vega", file: "RITMO SALVAJE.mp3" },
    { title: "SIN REGLAS", artist: "luna-vega", file: "SIN REGLAS.mp3" },
    { title: "SIN TI NO", artist: "luna-vega", file: "SIN TI NO.mp3" },
    { title: "TE PROVOCO", artist: "luna-vega", file: "TE PROVOCO.mp3" },
    { title: "VEN PA'CA", artist: "luna-vega", file: "VEN PA’CA.mp3" },
    { title: "ATL", artist: "nova-crown", file: "ATL.mp3" },
    { title: "Before the Crown", artist: "nova-crown", file: "Before the Crown.mp3" },
    { title: "Boston Made", artist: "nova-crown", file: "Boston Made.mp3" },
    { title: "Boston!", artist: "nova-crown", file: "Boston!.mp3" },
    { title: "Built Different", artist: "nova-crown", file: "Built Different.mp3" },
    { title: "Chicago", artist: "nova-crown", file: "Chicago.mp3" },
    { title: "Enemies Watching", artist: "nova-crown", file: "Enemies Watching.mp3" },
    { title: "I'm Still Here", artist: "nova-crown", file: "I'm Still Here.mp3" },
    { title: "Legacy Mode", artist: "nova-crown", file: "Legacy Mode.mp3" },
    { title: "Money Can't Sleep", artist: "nova-crown", file: "Money Can't Sleep.mp3" },
    { title: "My Circle", artist: "nova-crown", file: "My Circle.mp3" },
    { title: "No  Handouts", artist: "nova-crown", file: "No  Handouts.mp3" },
    { title: "NoApologies", artist: "nova-crown", file: "NoApologies.mp3" },
    { title: "Pain to Profit", artist: "nova-crown", file: "Pain to Profit.mp3" },
    { title: "Pressure Talks", artist: "nova-crown", file: "Pressure Talks.mp3" },
    { title: "Silent Wins", artist: "nova-crown", file: "Silent Wins.mp3" },
    { title: "Be calming Down", artist: "sanmi-kora", file: "Be calming Down.mp3" },
    { title: "Benefit", artist: "sanmi-kora", file: "Benefit.mp3" },
    { title: "Calm Down", artist: "sanmi-kora", file: "Calm Down.mp3" },
    { title: "Eledumare Knows", artist: "sanmi-kora", file: "Eledumare Knows.mp3" },
    { title: "Enemies Watch me", artist: "sanmi-kora", file: "Enemies Watch me.mp3" },
    { title: "March Am", artist: "sanmi-kora", file: "March Am.mp3" },
    { title: "No Dull  My Energy", artist: "sanmi-kora", file: "No Dull  My Energy.mp3" },
    { title: "No Turn Back", artist: "sanmi-kora", file: "No Turn Back.mp3" },
    { title: "Omo Oodua", artist: "sanmi-kora", file: "Omo Oodua.mp3" },
    { title: "Ori", artist: "sanmi-kora", file: "Ori.mp3" },
    { title: "Shine by force", artist: "sanmi-kora", file: "Shine by force.mp3" },
    { title: "Street Sermon", artist: "sanmi-kora", file: "Street Sermon.mp3" },
    { title: "Wole", artist: "sanmi-kora", file: "Wole.mp3" },
  ],
  "inner-signal": [
    { title: "ATL", artist: "nova-crown", file: "ATL.mp3" },
    { title: "Before the Crown", artist: "nova-crown", file: "Before the Crown.mp3" },
    { title: "Boston Made", artist: "nova-crown", file: "Boston Made.mp3" },
    { title: "Boston!", artist: "nova-crown", file: "Boston!.mp3" },
    { title: "Built Different", artist: "nova-crown", file: "Built Different.mp3" },
    { title: "Chicago", artist: "nova-crown", file: "Chicago.mp3" },
    { title: "Enemies Watching", artist: "nova-crown", file: "Enemies Watching.mp3" },
    { title: "I'm Still Here", artist: "nova-crown", file: "I'm Still Here.mp3" },
    { title: "Legacy Mode", artist: "nova-crown", file: "Legacy Mode.mp3" },
    { title: "Money Can't Sleep", artist: "nova-crown", file: "Money Can't Sleep.mp3" },
    { title: "My Circle", artist: "nova-crown", file: "My Circle.mp3" },
    { title: "No  Handouts", artist: "nova-crown", file: "No  Handouts.mp3" },
    { title: "NoApologies", artist: "nova-crown", file: "NoApologies.mp3" },
    { title: "Pain to Profit", artist: "nova-crown", file: "Pain to Profit.mp3" },
    { title: "Pressure Talks", artist: "nova-crown", file: "Pressure Talks.mp3" },
    { title: "Silent Wins", artist: "nova-crown", file: "Silent Wins.mp3" },
    { title: "Come to the light", artist: "zayden", file: "Come to the light.mp3" },
    { title: "Holy Flex", artist: "zayden", file: "Holy Flex.mp3" },
    { title: "I Found My Voice", artist: "zayden", file: "I Found My Voice.mp3" },
    { title: "King of My City", artist: "zayden", file: "King of My City.mp3" },
    { title: "Kingdom Business", artist: "zayden", file: "Kingdom Business.mp3" },
    { title: "Only You", artist: "zayden", file: "Only You.mp3" },
    { title: "Praise Goes Up", artist: "zayden", file: "Praise Goes Up.mp3" },
    { title: "Prayers into Steps", artist: "zayden", file: "Prayers into Steps.mp3" },
    { title: "Sent Me Back", artist: "zayden", file: "Sent Me Back.mp3" },
    { title: "Southside Still Standin", artist: "zayden", file: "Southside Still Standin.mp3" },
    { title: "Street Revival", artist: "zayden", file: "Street Revival.mp3" },
    { title: "Turn it Up", artist: "zayden", file: "Turn it Up.mp3" },
    { title: "Unstoppable", artist: "zayden", file: "Unstoppable.mp3" },
  ],
  "jazz-lounge": [
    { title: "Celestial Current (1)", artist: "jazz", file: "Celestial Current (1).mp3" },
    { title: "Celestial Current", artist: "jazz", file: "Celestial Current.mp3" },
    { title: "Late Night On Maple Lane (1)", artist: "jazz", file: "Late Night On Maple Lane (1).mp3" },
    { title: "Late Night On Maple Lane", artist: "jazz", file: "Late Night On Maple Lane.mp3" },
    { title: "Late-Night Lean (1)", artist: "jazz", file: "Late-Night Lean (1).mp3" },
    { title: "Late-Night Lean", artist: "jazz", file: "Late-Night Lean.mp3" },
    { title: "Midnight Turnstile (1)", artist: "jazz", file: "Midnight Turnstile (1).mp3" },
    { title: "Midnight Turnstile", artist: "jazz", file: "Midnight Turnstile.mp3" },
    { title: "Midnight Window Seat (1)", artist: "jazz", file: "Midnight Window Seat (1).mp3" },
    { title: "Midnight Window Seat", artist: "jazz", file: "Midnight Window Seat.mp3" },
    { title: "Slow Smoke in the Glass (1)", artist: "jazz", file: "Slow Smoke in the Glass (1).mp3" },
    { title: "Slow Smoke in the Glass", artist: "jazz", file: "Slow Smoke in the Glass.mp3" },
    { title: "Blue Stays Blue", artist: "lola-mills-jazz", file: "Blue Stays Blue.mp3" },
    { title: "Bluenotes and Street lights", artist: "lola-mills-jazz", file: "Bluenotes and Street lights.mp3" },
    { title: "Coffee After Rain", artist: "lola-mills-jazz", file: "Coffee After Rain.mp3" },
    { title: "Last Train Home", artist: "lola-mills-jazz", file: "Last Train Home.mp3" },
    { title: "Midnight on Riverside", artist: "lola-mills-jazz", file: "Midnight on Riverside.mp3" },
    { title: "Old Records New Feelings", artist: "lola-mills-jazz", file: "Old Records New Feelings.mp3" },
    { title: "Quiet Conversations", artist: "lola-mills-jazz", file: "Quiet Conversations.mp3" },
    { title: "Soft Lights in Harlem", artist: "lola-mills-jazz", file: "Soft Lights in Harlem.mp3" },
    { title: "Sunday Without Plans", artist: "lola-mills-jazz", file: "Sunday Without Plans.mp3" },
    { title: "Velvet Corners", artist: "lola-mills-jazz", file: "Velvet Corners.mp3" },
    { title: "Windows Half Open", artist: "lola-mills-jazz", file: "Windows Half Open.mp3" },
  ],
  "kings-kids": [
    { title: "Be Kind to One Another", artist: "sunny-and-the-star-crew", file: "Be Kind to One Another.mp3" },
    { title: "Be Strong and Courageous", artist: "sunny-and-the-star-crew", file: "Be Strong and Courageous.mp3" },
    { title: "Big Feelings, Small Me", artist: "sunny-and-the-star-crew", file: "Big Feelings, Small Me.mp3" },
    { title: "Brush Your Teeth Beat", artist: "sunny-and-the-star-crew", file: "Brush Your Teeth Beat.mp3" },
    { title: "Clean Up Dance", artist: "sunny-and-the-star-crew", file: "Clean Up Dance.mp3" },
    { title: "Count With Me", artist: "sunny-and-the-star-crew", file: "Count With Me.mp3" },
    { title: "Daniel Said His Prayers", artist: "sunny-and-the-star-crew", file: "Daniel Said His Prayers.mp3" },
    { title: "Faith Can Move Mountains", artist: "sunny-and-the-star-crew", file: "Faith Can Move Mountains.mp3" },
    { title: "Forgiveness", artist: "sunny-and-the-star-crew", file: "Forgiveness.mp3" },
    { title: "Good Morning World!", artist: "sunny-and-the-star-crew", file: "Good Morning World!.mp3" },
    { title: "Jesus Calmed the Storm", artist: "sunny-and-the-star-crew", file: "Jesus Calmed the Storm.mp3" },
    { title: "Jesus Fed the Crowd", artist: "sunny-and-the-star-crew", file: "Jesus Fed the Crowd.mp3" },
    { title: "Jonah Ran Away", artist: "sunny-and-the-star-crew", file: "Jonah Ran Away.mp3" },
    { title: "Joseph Never Gave Up", artist: "sunny-and-the-star-crew", file: "Joseph Never Gave Up.mp3" },
    { title: "Let's Try Again", artist: "sunny-and-the-star-crew", file: "Let's Try Again.mp3" },
    { title: "Love Is Patient", artist: "sunny-and-the-star-crew", file: "Love Is Patient.mp3" },
    { title: "Moses Crossed the Sea", artist: "sunny-and-the-star-crew", file: "Moses Crossed the Sea.mp3" },
    { title: "Noah Built The Boat", artist: "sunny-and-the-star-crew", file: "Noah Built The Boat.mp3" },
    { title: "Outside Day", artist: "sunny-and-the-star-crew", file: "Outside Day.mp3" },
    { title: "Sharing is Superpower", artist: "sunny-and-the-star-crew", file: "Sharing is Superpower.mp3" },
    { title: "Stars Say Goodnight", artist: "sunny-and-the-star-crew", file: "Stars Say Goodnight.mp3" },
    { title: "The Colors Song", artist: "sunny-and-the-star-crew", file: "The Colors Song.mp3" },
    { title: "The Samaritan", artist: "sunny-and-the-star-crew", file: "The Samaritan.mp3" },
    { title: "Time to be Kind", artist: "sunny-and-the-star-crew", file: "Time to be Kind.mp3" },
    { title: "We Are All Different", artist: "sunny-and-the-star-crew", file: "We Are All Different.mp3" },
  ],
  "new-faith-radio": [
    { title: "A New You3", artist: "femfem", file: "A New You3.mp3" },
    { title: "Amazing grace", artist: "femfem", file: "Amazing grace.mp3" },
    { title: "Blessed Assurance", artist: "femfem", file: "Blessed Assurance.mp3" },
    { title: "By His Wounds – Scripture Memory Afrobeat", artist: "femfem", file: "By His Wounds – Scripture Memory Afrobeat.mp3" },
    { title: "Deliver Me2", artist: "femfem", file: "Deliver Me2.mp3" },
    { title: "E don land o! - Femfem", artist: "femfem", file: "E don land o! - Femfem.mp3" },
    { title: "Echoes of you", artist: "femfem", file: "Echoes of you.mp3" },
    { title: "Eternal Covering", artist: "femfem", file: "Eternal Covering.mp3" },
    { title: "Eulogy", artist: "femfem", file: "Eulogy.mp3" },
    { title: "From My Mouth to God's Ears", artist: "femfem", file: "From My Mouth to God’s Ears.mp3" },
    { title: "Grace songs", artist: "femfem", file: "Grace songs.mp3" },
    { title: "Hark! The Herald", artist: "femfem", file: "Hark! The Herald.mp3" },
    { title: "Healing Is Here", artist: "femfem", file: "Healing Is Here.mp3" },
    { title: "Help me Believe", artist: "femfem", file: "Help me Believe.mp3" },
    { title: "I Break it!", artist: "femfem", file: "I Break it!.mp3" },
    { title: "I Don See Light", artist: "femfem", file: "I Don See Light.mp3" },
    { title: "I've got the power", artist: "femfem", file: "I've got the power.mp3" },
    { title: "In Christ Alone", artist: "femfem", file: "In Christ Alone.mp3" },
    { title: "Joy has come", artist: "femfem", file: "Joy has come.mp3" },
    { title: "Joy to The World", artist: "femfem", file: "Joy to The World.mp3" },
    { title: "Mercy and Grace (Won't Let Me Go)", artist: "femfem", file: "Mercy and Grace (Won’t Let Me Go).mp3" },
    { title: "Mercy Keep Catching Me", artist: "femfem", file: "Mercy Keep Catching Me.mp3" },
    { title: "Mighty Is Your Name", artist: "femfem", file: "Mighty Is Your Name.mp3" },
    { title: "My Help Arrives", artist: "femfem", file: "My Help Arrives.mp3" },
    { title: "My Muse", artist: "femfem", file: "My Muse.mp3" },
    { title: "New Beginnings", artist: "femfem", file: "New Beginnings.mp3" },
    { title: "O Come, All Ye Faithful", artist: "femfem", file: "O Come, All Ye Faithful.mp3" },
    { title: "Praise in the dance", artist: "femfem", file: "Praise in the dance.mp3" },
    { title: "Prayer of Faith - Scripture Memory Afrobeat", artist: "femfem", file: "Prayer of Faith - Scripture Memory Afrobeat.mp3" },
    { title: "Prodigal", artist: "femfem", file: "Prodigal.mp3" },
    { title: "Salvation", artist: "femfem", file: "Salvation.mp3" },
    { title: "Shield of Favor", artist: "femfem", file: "Shield of Favor.mp3" },
    { title: "Shielded2", artist: "femfem", file: "Shielded2.mp3" },
    { title: "Soldier Man", artist: "femfem", file: "Soldier Man.mp3" },
    { title: "Still I Believe (Remix)", artist: "femfem", file: "Still I Believe (Remix).mp3" },
    { title: "Still Standing", artist: "femfem", file: "Still Standing.mp3" },
    { title: "Still yours", artist: "femfem", file: "Still yours.mp3" },
    { title: "Success fire", artist: "femfem", file: "Success fire.mp3" },
    { title: "Testify with Grace", artist: "femfem", file: "Testify with Grace.mp3" },
    { title: "The Greatest", artist: "femfem", file: "The Greatest.mp3" },
    { title: "The Lord Who Heals You – Scripture Memory Afrobeat", artist: "femfem", file: "The Lord Who Heals You – Scripture Memory Afrobeat.mp3" },
    { title: "The One Before All_2", artist: "femfem", file: "The One Before All_2.mp3" },
    { title: "Victory Dance", artist: "femfem", file: "Victory Dance.mp3" },
    { title: "Welcome to the World", artist: "femfem", file: "Welcome to the World.mp3" },
    { title: "Where is the love", artist: "femfem", file: "Where is the love.mp3" },
    { title: "Who I am", artist: "femfem", file: "Who I am.mp3" },
    { title: "Who I am_2", artist: "femfem", file: "Who I am_2.mp3" },
    { title: "Whole World (Afro Praise Remix)", artist: "femfem", file: "Whole World (Afro Praise Remix).mp3" },
    { title: "Whole World {Fresh)", artist: "femfem", file: "Whole World {Fresh).mp3" },
    { title: "Whole World", artist: "femfem", file: "Whole World.mp3" },
    { title: "Your Name Is", artist: "femfem", file: "Your Name Is.mp3" },
    { title: "Bailo Para Él", artist: "lil-reina", file: "Bailo Para Él.mp3" },
    { title: "Crown Easy (Grace on Me)", artist: "lil-reina", file: "Crown Easy (Grace on Me).mp3" },
    { title: "Fue Su Gracia _ It Was Grace", artist: "lil-reina", file: "Fue Su Gracia _ It Was Grace.mp3" },
    { title: "Fuego Santo” (Holy Fire)_ (1)", artist: "lil-reina", file: "Fuego Santo” (Holy Fire)_ (1).mp3" },
    { title: "Fuego Santo” (Holy Fire)_", artist: "lil-reina", file: "Fuego Santo” (Holy Fire)_.mp3" },
    { title: "Gozo Eterno _ Eternal Joy", artist: "lil-reina", file: "Gozo Eterno _ Eternal Joy.mp3" },
    { title: "Gracia Que Baila", artist: "lil-reina", file: "Gracia Que Baila.mp3" },
    { title: "Mi Respirar  _ My Breath", artist: "lil-reina", file: "Mi Respirar  _ My Breath.mp3" },
    { title: "Navidad De Gracia (Christmas of Grace)", artist: "lil-reina", file: "Navidad De Gracia (Christmas of Grace).mp3" },
    { title: "Not That Girl No More", artist: "lil-reina", file: "Not That Girl No More.mp3" },
    { title: "Saint in the Club", artist: "lil-reina", file: "Saint in the Club.mp3" },
    { title: "Todo Para Ti, JESÚS _ All for you Jesus", artist: "lil-reina", file: "Todo Para Ti, JESÚS _ All for you Jesus.mp3" },
    { title: "Tu Amor Me Salva (Your Love Saves Me)", artist: "lil-reina", file: "Tu Amor Me Salva (Your Love Saves Me).mp3" },
    { title: "AfroHalo_1", artist: "lyon-saint", file: "AfroHalo_1.mp3" },
    { title: "Anchor of My Soul", artist: "lyon-saint", file: "Anchor of My Soul.mp3" },
    { title: "City of Saints", artist: "lyon-saint", file: "City of Saints.mp3" },
    { title: "Crown & Chains", artist: "lyon-saint", file: "Crown & Chains.mp3" },
    { title: "Gold Dust Heart", artist: "lyon-saint", file: "Gold Dust Heart.mp3" },
    { title: "Heir of Light", artist: "lyon-saint", file: "Heir of Light.mp3" },
    { title: "Heirs", artist: "lyon-saint", file: "Heirs.mp3" },
    { title: "His Grace", artist: "lyon-saint", file: "His Grace.mp3" },
    { title: "Holy in the Dark", artist: "lyon-saint", file: "Holy in the Dark.mp3" },
    { title: "Joy to The World (1)", artist: "lyon-saint", file: "Joy to The World (1).mp3" },
    { title: "King of All", artist: "lyon-saint", file: "King of All.mp3" },
    { title: "Lift Him Loud", artist: "lyon-saint", file: "Lift Him Loud.mp3" },
    { title: "Saints in Motion", artist: "lyon-saint", file: "Saints in Motion.mp3" },
    { title: "Silent Night", artist: "lyon-saint", file: "Silent Night.mp3" },
    { title: "Stand in the Gap", artist: "lyon-saint", file: "Stand in the Gap.mp3" },
    { title: "Still Standing", artist: "lyon-saint", file: "Still Standing.mp3" },
    { title: "Thank You", artist: "lyon-saint", file: "Thank You.mp3" },
    { title: "The First Noel", artist: "lyon-saint", file: "The First Noel.mp3" },
    { title: "The Gift and The Cross - Lyon", artist: "lyon-saint", file: "The Gift and The Cross - Lyon.mp3" },
    { title: "The Ones I Pray For", artist: "lyon-saint", file: "The Ones I Pray For.mp3" },
    { title: "Victory", artist: "lyon-saint", file: "Victory.mp3" },
    { title: "War Cry", artist: "lyon-saint", file: "War Cry.mp3" },
    { title: "What the Lord hasdone", artist: "lyon-saint", file: "What the Lord hasdone.mp3" },
    { title: "Come to the light", artist: "zayden", file: "Come to the light.mp3" },
    { title: "Holy Flex", artist: "zayden", file: "Holy Flex.mp3" },
    { title: "I Found My Voice", artist: "zayden", file: "I Found My Voice.mp3" },
    { title: "King of My City", artist: "zayden", file: "King of My City.mp3" },
    { title: "Kingdom Business", artist: "zayden", file: "Kingdom Business.mp3" },
    { title: "Only You", artist: "zayden", file: "Only You.mp3" },
    { title: "Praise Goes Up", artist: "zayden", file: "Praise Goes Up.mp3" },
    { title: "Prayers into Steps", artist: "zayden", file: "Prayers into Steps.mp3" },
    { title: "Sent Me Back", artist: "zayden", file: "Sent Me Back.mp3" },
    { title: "Southside Still Standin", artist: "zayden", file: "Southside Still Standin.mp3" },
    { title: "Street Revival", artist: "zayden", file: "Street Revival.mp3" },
    { title: "Turn it Up", artist: "zayden", file: "Turn it Up.mp3" },
    { title: "Unstoppable", artist: "zayden", file: "Unstoppable.mp3" },
    { title: "Blood Cover", artist: "zuriella", file: "Blood Cover.mp3" },
    { title: "Breath of Praise (Remastered)", artist: "zuriella", file: "Breath of Praise (Remastered).mp3" },
    { title: "Breath of Praise", artist: "zuriella", file: "Breath of Praise.mp3" },
    { title: "Covenant Keeping God", artist: "zuriella", file: "Covenant Keeping God.mp3" },
    { title: "Deeper (Remastered)", artist: "zuriella", file: "Deeper (Remastered).mp3" },
    { title: "Faithful Through It All", artist: "zuriella", file: "Faithful Through It All.mp3" },
    { title: "Grateful", artist: "zuriella", file: "Grateful.mp3" },
    { title: "Heaven!", artist: "zuriella", file: "Heaven!.mp3" },
    { title: "Is This Your Will_", artist: "zuriella", file: "Is This Your Will_.mp3" },
    { title: "Lead Me Lord", artist: "zuriella", file: "Lead Me Lord.mp3" },
    { title: "Life like that", artist: "zuriella", file: "Life like that.mp3" },
    { title: "Light in the Room - Zuri", artist: "zuriella", file: "Light in the Room - Zuri.mp3" },
    { title: "Majesty (You Reign Forever)", artist: "zuriella", file: "Majesty (You Reign Forever).mp3" },
    { title: "Never Failing Father", artist: "zuriella", file: "Never Failing Father.mp3" },
    { title: "Perfect Timing", artist: "zuriella", file: "Perfect Timing.mp3" },
    { title: "Such Wisdom", artist: "zuriella", file: "Such Wisdom.mp3" },
    { title: "The God Who Sees Me", artist: "zuriella", file: "The God Who Sees Me.mp3" },
    { title: "The One", artist: "zuriella", file: "The One.mp3" },
    { title: "This Is Love", artist: "zuriella", file: "This Is Love.mp3" },
    { title: "Wings Like Eagles", artist: "zuriella", file: "Wings Like Eagles.mp3" },
  ],
  "rnb-vibes": [
    { title: "713 STEADY", artist: "kairos", file: "713 STEADY.mp3" },
    { title: "All Night Long", artist: "kairos", file: "All Night Long.mp3" },
    { title: "Better Days", artist: "kairos", file: "Better Days.mp3" },
    { title: "Broken But Breathing", artist: "kairos", file: "Broken But Breathing.mp3" },
    { title: "City Dreams", artist: "kairos", file: "City Dreams.mp3" },
    { title: "Echoes", artist: "kairos", file: "Echoes.mp3" },
    { title: "EMPIRE MOTION", artist: "kairos", file: "EMPIRE MOTION.mp3" },
    { title: "Fall Again", artist: "kairos", file: "Fall Again.mp3" },
    { title: "Forever Mine", artist: "kairos", file: "Forever Mine.mp3" },
    { title: "Golden Nights", artist: "kairos", file: "Golden Nights.mp3" },
    { title: "Heaven's Timing", artist: "kairos", file: "Heaven's Timing.mp3" },
    { title: "High Life", artist: "kairos", file: "High Life.mp3" },
    { title: "Hold Me Close", artist: "kairos", file: "Hold Me Close.mp3" },
    { title: "Love in Motion", artist: "kairos", file: "Love in Motion.mp3" },
    { title: "Midnight Calls", artist: "kairos", file: "Midnight Calls.mp3" },
    { title: "My Season", artist: "kairos", file: "My Season.mp3" },
    { title: "No Looking Back", artist: "kairos", file: "No Looking Back.mp3" },
    { title: "No More Fear", artist: "kairos", file: "No More Fear.mp3" },
    { title: "On My Way", artist: "kairos", file: "On My Way.mp3" },
    { title: "Promise Ring", artist: "kairos", file: "Promise Ring.mp3" },
    { title: "Rich in Love", artist: "kairos", file: "Rich in Love.mp3" },
    { title: "Run to Me", artist: "kairos", file: "Run to Me.mp3" },
    { title: "Say My  Name", artist: "kairos", file: "Say My  Name.mp3" },
    { title: "Still Yours", artist: "kairos", file: "Still Yours.mp3" },
    { title: "Sunrise Over You", artist: "kairos", file: "Sunrise Over You.mp3" },
    { title: "United We Rise", artist: "kairos", file: "United We Rise.mp3" },
    { title: "We Made It", artist: "kairos", file: "We Made It.mp3" },
    { title: "You & I", artist: "kairos", file: "You & I.mp3" },
    { title: "AfroHalo_1", artist: "lyon-saint", file: "AfroHalo_1.mp3" },
    { title: "Anchor of My Soul", artist: "lyon-saint", file: "Anchor of My Soul.mp3" },
    { title: "City of Saints", artist: "lyon-saint", file: "City of Saints.mp3" },
    { title: "Crown & Chains", artist: "lyon-saint", file: "Crown & Chains.mp3" },
    { title: "Gold Dust Heart", artist: "lyon-saint", file: "Gold Dust Heart.mp3" },
    { title: "Heir of Light", artist: "lyon-saint", file: "Heir of Light.mp3" },
    { title: "Heirs", artist: "lyon-saint", file: "Heirs.mp3" },
    { title: "His Grace", artist: "lyon-saint", file: "His Grace.mp3" },
    { title: "Holy in the Dark", artist: "lyon-saint", file: "Holy in the Dark.mp3" },
    { title: "Joy to The World (1)", artist: "lyon-saint", file: "Joy to The World (1).mp3" },
    { title: "King of All", artist: "lyon-saint", file: "King of All.mp3" },
    { title: "Lift Him Loud", artist: "lyon-saint", file: "Lift Him Loud.mp3" },
    { title: "Saints in Motion", artist: "lyon-saint", file: "Saints in Motion.mp3" },
    { title: "Silent Night", artist: "lyon-saint", file: "Silent Night.mp3" },
    { title: "Stand in the Gap", artist: "lyon-saint", file: "Stand in the Gap.mp3" },
    { title: "Still Standing", artist: "lyon-saint", file: "Still Standing.mp3" },
    { title: "Thank You", artist: "lyon-saint", file: "Thank You.mp3" },
    { title: "The First Noel", artist: "lyon-saint", file: "The First Noel.mp3" },
    { title: "The Gift and The Cross - Lyon", artist: "lyon-saint", file: "The Gift and The Cross - Lyon.mp3" },
    { title: "The Ones I Pray For", artist: "lyon-saint", file: "The Ones I Pray For.mp3" },
    { title: "Victory", artist: "lyon-saint", file: "Victory.mp3" },
    { title: "War Cry", artist: "lyon-saint", file: "War Cry.mp3" },
    { title: "What the Lord hasdone", artist: "lyon-saint", file: "What the Lord hasdone.mp3" },
  ],
};

Object.keys(STATION_MUSIC).forEach(stationSlug => {
  STATION_MUSIC[stationSlug] = STATION_MUSIC[stationSlug].map((song, idx) => {
    const audioUrl = `${R2_URL}/music/${song.artist}/${encodeURIComponent(song.file)}`;
    return { ...song, id: `${stationSlug}-${idx}`, audio_url: audioUrl };
  });
});

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// CHANNELS & STATIONS DATA
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const GROOVIE_CHANNELS = [
  { slug: 'groovie-kids', name: 'Groovie Kids', tagline: 'Smart audio for curious minds', badge: '6+', color: 'emerald', listeners: '2.3K', isGroovieChannel: true },
  { slug: 'groovie-laughs', name: 'The Comedy Room', tagline: 'Stand-up & satire', badge: '18+', color: 'amber', listeners: '8.1K', isGroovieChannel: true },
  { slug: 'groovie-drama', name: 'Short Stories', tagline: 'Micro-fiction that resonates', badge: '13+', color: 'rose', listeners: '5.4K', isGroovieChannel: true },
  { slug: 'groovie-thinklab', name: 'ThinkLab', tagline: 'Ideas worth exploring', badge: '14+', color: 'blue', listeners: '12.7K', isGroovieChannel: true },
  { slug: 'groovie-worlds', name: 'World Radio', tagline: 'Stories from everywhere', badge: '12+', color: 'teal', listeners: '6.9K', isGroovieChannel: true },
  { slug: 'groovie-afterhours', name: 'After Hours', tagline: 'Late night reflections', badge: '16+', color: 'violet', listeners: '9.2K', isGroovieChannel: true }
];
const FREE_SHOWS = ['groovie-kids', 'comedy-room', 'thinklab'];

const MUSIC_STATIONS = [
  // HISTORICAL RENAME: 'glory-days-gospel' is a STALE SLUG from the pre-rename Gospel station; this station is now LATIN (name/genre/tracks/jingles). Do NOT rename the slug or revert the name to Gospel — it's load-bearing (STATION_TRACKS + jingle map + STATION_ICONS keys, and persisted in listener favorites/localStorage).
  { slug: 'glory-days-gospel', name: 'Latin', genre: 'Latin', color: 'orange', listeners: '22.7K' },
  { slug: 'new-faith-radio', name: 'New Faith', genre: 'Inspirational', color: 'purple', listeners: '11.8K' },
  { slug: 'afrobeats-pulse', name: 'Afrobeats', genre: 'Afrobeats', color: 'green', listeners: '24.5K' },
  { slug: 'hot-hits-pop', name: 'Hot Hits', genre: 'Pop', color: 'pink', listeners: '32.1K' },
  { slug: 'rnb-vibes', name: 'R&B Vibes', genre: 'R&B / Soul', color: 'fuchsia', listeners: '18.3K' },
  { slug: 'inner-signal', name: 'Hip Hop', genre: 'Hip Hop / Rap', color: 'red', listeners: '28.9K' },
  { slug: 'jazz-lounge', name: 'Jazz Lounge', genre: 'Jazz', color: 'slate', listeners: '9.4K' },
  { slug: 'kings-kids', name: 'KingsKids', genre: 'Christian Kids', color: 'sky', listeners: '3.1K' }
];

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// ICON MAPS (Lucide icons by slug â€” data arrays stay frozen)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const CHANNEL_ICONS = {
  'groovie-kids': Baby, 'groovie-laughs': Laugh, 'groovie-drama': Theater,
  'groovie-thinklab': Brain, 'groovie-worlds': Globe, 'groovie-afterhours': Moon,
};

const STATION_ICONS = {
  'glory-days-gospel': Music, 'new-faith-radio': Sparkles, 'afrobeats-pulse': Music,
  'hot-hits-pop': Flame, 'rnb-vibes': Headphones, 'inner-signal': Wind, 'jazz-lounge': Music, 'kings-kids': Baby,
};

const BUSINESS_TYPE_ICONS = {
  gym: Dumbbell, restaurant: UtensilsCrossed, religious_institution: Church,
  hotel: Hotel, retail_store: ShoppingBag, spa: Sparkles, corporate: Briefcase,
  warehouse: Warehouse, medical: Stethoscope, education: GraduationCap, others: Building,
};

const getBusinessIcon = (type) => BUSINESS_TYPE_ICONS[(type || 'others').toLowerCase().replace(/\s+/g, '_')] || Building;

const MOOD_COLORS = {
  smooth_jazz: 'indigo', upbeat: 'orange', ambient: 'teal', lofi_focus: 'purple', energetic: 'red',
  retail_store: 'orange', corporate: 'indigo', gym: 'red', restaurant: 'amber', hotel: 'purple',
  spa: 'teal', medical: 'cyan', religious_institution: 'amber', warehouse: 'slate',
  education: 'blue', others: 'slate',
};

const COLOR_MAP = {
  emerald: { card: 'from-emerald-900/60 via-emerald-800/30 to-neutral-900/80', border: 'border-emerald-500/20 hover:border-emerald-400/40', solid: 'bg-emerald-500', grad: 'from-emerald-600 to-emerald-800' },
  amber: { card: 'from-amber-900/60 via-amber-800/30 to-neutral-900/80', border: 'border-amber-500/20 hover:border-amber-400/40', solid: 'bg-amber-500', grad: 'from-amber-600 to-amber-800' },
  rose: { card: 'from-rose-900/60 via-rose-800/30 to-neutral-900/80', border: 'border-rose-500/20 hover:border-rose-400/40', solid: 'bg-rose-500', grad: 'from-rose-600 to-rose-800' },
  blue: { card: 'from-blue-900/60 via-blue-800/30 to-neutral-900/80', border: 'border-blue-500/20 hover:border-blue-400/40', solid: 'bg-blue-500', grad: 'from-blue-600 to-blue-800' },
  teal: { card: 'from-teal-900/60 via-teal-800/30 to-neutral-900/80', border: 'border-teal-500/20 hover:border-teal-400/40', solid: 'bg-teal-500', grad: 'from-teal-600 to-teal-800' },
  violet: { card: 'from-violet-900/60 via-violet-800/30 to-neutral-900/80', border: 'border-violet-500/20 hover:border-violet-400/40', solid: 'bg-violet-500', grad: 'from-violet-600 to-violet-800' },
  purple: { card: 'from-purple-900/60 via-purple-800/30 to-neutral-900/80', border: 'border-purple-500/20 hover:border-purple-400/40', solid: 'bg-purple-500', grad: 'from-purple-600 to-purple-800' },
  green: { card: 'from-green-900/60 via-green-800/30 to-neutral-900/80', border: 'border-green-500/20 hover:border-green-400/40', solid: 'bg-green-500', grad: 'from-green-600 to-green-800' },
  pink: { card: 'from-pink-900/60 via-pink-800/30 to-neutral-900/80', border: 'border-pink-500/20 hover:border-pink-400/40', solid: 'bg-pink-500', grad: 'from-pink-600 to-pink-800' },
  fuchsia: { card: 'from-fuchsia-900/60 via-fuchsia-800/30 to-neutral-900/80', border: 'border-fuchsia-500/20 hover:border-fuchsia-400/40', solid: 'bg-fuchsia-500', grad: 'from-fuchsia-600 to-fuchsia-800' },
  cyan: { card: 'from-cyan-900/60 via-cyan-800/30 to-neutral-900/80', border: 'border-cyan-500/20 hover:border-cyan-400/40', solid: 'bg-cyan-500', grad: 'from-cyan-600 to-cyan-800' },
  slate: { card: 'from-slate-900/60 via-slate-800/30 to-neutral-900/80', border: 'border-slate-500/20 hover:border-slate-400/40', solid: 'bg-slate-500', grad: 'from-slate-600 to-slate-800' },
  indigo: { card: 'from-indigo-900/60 via-indigo-800/30 to-neutral-900/80', border: 'border-indigo-500/20 hover:border-indigo-400/40', solid: 'bg-indigo-500', grad: 'from-indigo-600 to-indigo-800' },
  orange: { card: 'from-orange-900/60 via-orange-800/30 to-neutral-900/80', border: 'border-orange-500/20 hover:border-orange-400/40', solid: 'bg-orange-500', grad: 'from-orange-600 to-orange-800' },
  red: { card: 'from-red-900/60 via-red-800/30 to-neutral-900/80', border: 'border-red-500/20 hover:border-red-400/40', solid: 'bg-red-500', grad: 'from-red-600 to-red-800' },
};

const c = (color) => COLOR_MAP[color] || COLOR_MAP.slate;

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// HOOKS (FROZEN)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const useFavorites = () => {
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('groovie_favorites') || '[]'); } catch { return []; }
  });
  const toggleFavorite = (slug) => {
    const newFavorites = favorites.includes(slug) ? favorites.filter(f => f !== slug) : [...favorites, slug];
    setFavorites(newFavorites);
    localStorage.setItem('groovie_favorites', JSON.stringify(newFavorites));
  };
  const isFavorite = (slug) => favorites.includes(slug);
  return { favorites, toggleFavorite, isFavorite };
};

const useRecentlyPlayed = () => {
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem('groovie_recent') || '[]'); } catch { return []; }
  });
  const addRecent = (station) => {
    const filtered = recent.filter(r => r.slug !== station.slug);
    const newRecent = [{ ...station, playedAt: Date.now() }, ...filtered].slice(0, 10);
    setRecent(newRecent);
    localStorage.setItem('groovie_recent', JSON.stringify(newRecent));
  };
  return { recent, addRecent };
};

const useBusinessChannels = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);   // S1: bump to retry the fetch
  useEffect(() => {
    const fetchChannels = async () => {
      setLoading(true); setError(null);
      try {
        // S1/demo-fix: hard timeout so a hanging /public/channels can't leave the app in an
        // infinite loading shimmer (previously no timeout -> loading never cleared on a stalled fetch).
        const controller = new AbortController();
        const to = setTimeout(() => controller.abort(), 9000);
        let response;
        try { response = await fetch(`${BUSINESS_API_URL}/public/channels`, { signal: controller.signal }); }
        finally { clearTimeout(to); }
        if (!response.ok) throw new Error('Failed to fetch channels');
        const data = await response.json();
        const raw = data.channels || data || [];
        const normalized = raw.map(ch => ({
          id: ch.id,
          company_id: ch.company_id ?? null,   // brand grain — powers the inline Follow pill (2.5.3a UX revision)
          company_name: ch.business_name || "Unknown Business",
          company_slug: (ch.business_name || "unknown").toLowerCase().replace(/\s+/g, "-"),
          channel_name: ch.name || "Unnamed Channel",
          channel_slug: (ch.name || "channel").toLowerCase().replace(/\s+/g, "-"),
          description: ch.description || "",
          mood: ch.business_type || "others",
          stream_url: ch.stream_url,
          listeners: ch.listener_count || 0,
          is_live: !!ch.stream_url,
          artwork_url: ch.artwork_url,
          location_name: ch.location_name,
        }));
        setChannels(normalized);
      } catch (err) {
        setError(err.message);
        setChannels([]);
      } finally {
        setLoading(false);
      }
    };
    fetchChannels();
  }, [reloadKey]);
  return { channels, loading, error, retry: () => setReloadKey(k => k + 1) };
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// PLAYLIST GENERATOR (FROZEN)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const generatePlaylist = (stationSlug, isPremium = false, isGroovieChannel = false) => {
  const playlist = [];
  const stationJingles = STATION_JINGLES[stationSlug] || {};
  let jingleAlternator = true;
  let musicalVariantIdx = 0;   // per-playlist cursor: cycles multi-variant musical jingles (A,B,A,B) instead of random-with-repeats

  const getJingle = (preferMusical) => {
    if (preferMusical && stationJingles.musical) {
      const musical = stationJingles.musical;
      const jingleKey = Array.isArray(musical) ? musical[musicalVariantIdx++ % musical.length] : musical;
      return { type: 'jingle', subtype: 'musical', title: 'Station Jingle', audio_url: JINGLES[jingleKey] };
    } else if (stationJingles.voice) {
      return { type: 'jingle', subtype: 'voice', title: 'Station ID', audio_url: JINGLES[stationJingles.voice] };
    }
    // Fallbacks must announce themselves — silent substitution is the "13-tracks disease" (Jazz-Lounge class bug).
    console.warn(`[JINGLE FALLBACK] station "${stationSlug}" has no STATION_JINGLES entry — playing generic groovie-main-musical. Add a mapping to stop this.`);
    return { type: 'jingle', subtype: 'musical', title: 'Groovie', audio_url: JINGLES['groovie-main-musical'] };
  };

  const music = [...(STATION_MUSIC[stationSlug] || STATION_MUSIC['hot-hits-pop'])];
  const content = isGroovieChannel ? [...(CHANNEL_AUDIO[stationSlug] || [])] : [];
  music.sort(() => Math.random() - 0.5);
  content.sort(() => Math.random() - 0.5);

  if (stationSlug === 'groovie-kids') {
    playlist.push(getJingle(true));
    const npTrans = getTransition(stationSlug, 'now_playing'); if (npTrans) playlist.push(npTrans);
    content.forEach((piece, idx) => {
      if (idx > 0) playlist.push(getJingle(false));
      playlist.push({ type: 'content', ...piece });
      if (SUBSCRIPTIONS_ENABLED && !isPremium && idx === 1) {
        playlist.push({ type: 'ad_break', title: 'Ad Break', audio_url: JINGLES['ad-break'] });
        playlist.push({ type: 'ad_return', title: 'We\'re Back', audio_url: JINGLES['ad-return'] });
      }
    });
    playlist.push(getJingle(true));
    playlist.push({ type: 'jingle', subtype: 'voice', title: 'Outro', audio_url: JINGLES['station-outro'] });
    return playlist;
  }

  if (isGroovieChannel) {
    let contentIndex = 0, musicIndex = 0;
    playlist.push(getJingle(true));
    const introTrans = getTransition(stationSlug, 'intro'); if (introTrans) playlist.push(introTrans);
    const segments = Math.min(4, content.length);
    for (let seg = 0; seg < segments; seg++) {
      if (contentIndex < content.length) playlist.push({ type: 'content', ...content[contentIndex++] });
      if (musicIndex < music.length) playlist.push({ type: 'music', ...music[musicIndex++] });
      if (contentIndex < content.length) playlist.push({ type: 'content', ...content[contentIndex++] });
      jingleAlternator = !jingleAlternator;
      playlist.push(getJingle(jingleAlternator));
      if (musicIndex < music.length) playlist.push({ type: 'music', ...music[musicIndex++] });
      if (SUBSCRIPTIONS_ENABLED && !isPremium && seg === 1) {
        playlist.push({ type: 'ad_break', title: 'Ad Break', audio_url: JINGLES['ad-break'] });
        playlist.push({ type: 'ad_return', title: 'We\'re Back', audio_url: JINGLES['ad-return'] });
      }
    }
    playlist.push(getJingle(true));
    const outroTrans = getTransition(stationSlug, 'outro'); if (outroTrans) playlist.push(outroTrans);
    playlist.push({ type: 'jingle', subtype: 'voice', title: 'Outro', audio_url: JINGLES['station-outro'] });
    return playlist;
  }

  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) playlist.push({ type: 'jingle', subtype: 'voice', title: 'Good Morning', audio_url: JINGLES['morning'] });
  else if (hour >= 22 || hour < 5) playlist.push({ type: 'jingle', subtype: 'voice', title: 'Late Night', audio_url: JINGLES['late-night'] });
  playlist.push(getJingle(true));
  let songCount = 0;
  for (let i = 0; i < music.length; i++) {
    songCount++;
    if (songCount > 1 && songCount % 4 === 0) { jingleAlternator = !jingleAlternator; playlist.push(getJingle(jingleAlternator)); }
    playlist.push({ type: 'music', ...music[i] });
    if (SUBSCRIPTIONS_ENABLED && !isPremium && songCount === Math.floor(music.length / 2)) {
      playlist.push({ type: 'ad_break', title: 'Ad Break', audio_url: JINGLES['ad-break'] });
      playlist.push({ type: 'ad_return', title: 'We\'re Back', audio_url: JINGLES['ad-return'] });
    }
  }
  playlist.push(getJingle(true));
  playlist.push({ type: 'jingle', subtype: 'voice', title: 'Outro', audio_url: JINGLES['station-outro'] });
  return playlist;
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// MIX PLAYLIST GENERATOR (NEW â€” combines tracks from multiple items)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const generateMixPlaylist = (items, isPremium = false) => {
  const allTracks = [];
  items.forEach(item => {
    if (item.isBusinessChannel) return; // Live streams can't be mixed into file playlists
    if (item.isGroovieChannel) {
      (CHANNEL_AUDIO[item.slug] || []).forEach(t => allTracks.push({ type: 'content', ...t }));
    } else {
      (STATION_MUSIC[item.slug] || []).forEach(t => allTracks.push({ type: 'music', ...t }));
    }
  });
  allTracks.sort(() => Math.random() - 0.5);
  const playlist = [];
  playlist.push({ type: 'jingle', subtype: 'musical', title: 'Groovie Mix', audio_url: JINGLES['groovie-main-musical'] });
  allTracks.forEach((track, idx) => {
    playlist.push(track);
    if ((idx + 1) % 4 === 0 && idx < allTracks.length - 1) {
      playlist.push({ type: 'jingle', subtype: 'musical', title: 'Groovie', audio_url: JINGLES['groovie-premium-musical'] });
    }
    if (SUBSCRIPTIONS_ENABLED && !isPremium && idx === Math.floor(allTracks.length / 2)) {
      playlist.push({ type: 'ad_break', title: 'Ad Break', audio_url: JINGLES['ad-break'] });
      playlist.push({ type: 'ad_return', title: "We're Back", audio_url: JINGLES['ad-return'] });
    }
  });
  playlist.push({ type: 'jingle', subtype: 'voice', title: 'Outro', audio_url: JINGLES['station-outro'] });
  return playlist;
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// SHARE MODAL
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const ShareModal = ({ station, onClose }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `https://groovie.live/station/${station.slug}`;
  const shareText = `Listening to ${station.name} on Groovie - Autonomous Radio`;
  const copyLink = async () => { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-white text-lg font-semibold">Share</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={20} strokeWidth={1.5} /></button>
        </div>
        <p className="text-white/60 text-sm mb-4">Share "{station.name}" with friends</p>
        <button onClick={copyLink} className="w-full flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-xl mb-3 transition">
          {copied ? <Check size={18} strokeWidth={1.5} /> : <Copy size={18} strokeWidth={1.5} />}
          <span className="text-white text-sm">{copied ? 'Copied!' : 'Copy Link'}</span>
        </button>
        <div className="grid grid-cols-3 gap-3">
          <button onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank')} className="flex flex-col items-center gap-2 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition">
            <span className="text-white text-xs font-bold">ð•</span><span className="text-white/60 text-xs">X</span>
          </button>
          <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank')} className="flex flex-col items-center gap-2 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition">
            <Share2 size={18} strokeWidth={1.5} /><span className="text-white/60 text-xs">WhatsApp</span>
          </button>
          <button onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')} className="flex flex-col items-center gap-2 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition">
            <Share2 size={18} strokeWidth={1.5} /><span className="text-white/60 text-xs">Facebook</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// RADIO PLAYER (audio logic FROZEN â€” added optional prebuiltPlaylist prop)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const RadioPlayer = ({ station, isGroovieChannel, isPremium, onClose, onToggleFavorite, isFavorite, prebuiltPlaylist, onMinimize, followFeed = [], onFallbackChange }) => {
  const audioRef = useRef(null);
  const skipCountRef = useRef(0);
  const skipResetTimeRef = useRef(Date.now());
  const [playTrigger, setPlayTrigger] = useState(0);

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// AUTONOMOUS NEWS SYSTEM
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
const [newsSegment, setNewsSegment] = useState(null);
const [newsFetching, setNewsFetching] = useState(false);
const newsTimerRef = useRef(null);
const lastNewsFetchRef = useRef(0);
const songsSinceNewsRef = useRef(0);

const fetchNewsSegment = useCallback(async () => {
  const userPrefs = (() => { try { return JSON.parse(localStorage.getItem('groovie_prefs') || '{}'); } catch { return {}; } })();
  if (!userPrefs.news) return null;

  setNewsFetching(true);
  try {
    const res = await fetch(NEWS_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        city: userPrefs.news_city || '',
        state: userPrefs.news_state || '',
        country: userPrefs.news_country || 'us',
        interests: userPrefs.news_interests || ['world'],
        length: userPrefs.news_length || 'standard',
        voice: userPrefs.news_voice || 'female_us',
      }),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.status === 'ok') {
        setNewsSegment(data);
        lastNewsFetchRef.current = Date.now();
        console.log('[NEWS] Segment ready:', data.headline_count, 'headlines');
        return data;
      }
    }
  } catch (e) {
    console.warn('[NEWS] Fetch error:', e);
  } finally {
    setNewsFetching(false);
  }
  return null;
}, []);

// Auto-fetch news at configured interval
useEffect(() => {
  const userPrefs = (() => { try { return JSON.parse(localStorage.getItem('groovie_prefs') || '{}'); } catch { return {}; } })();
  if (!userPrefs.news) {
    if (newsTimerRef.current) clearInterval(newsTimerRef.current);
    return;
  }
  const freqMs = (userPrefs.news_frequency || 120) * 60 * 1000;
  // Fetch immediately if no segment yet
  if (!newsSegment && !newsFetching) fetchNewsSegment();
  newsTimerRef.current = setInterval(() => {
    fetchNewsSegment();
  }, freqMs);
  return () => { if (newsTimerRef.current) clearInterval(newsTimerRef.current); };
}, [newsSegment, newsFetching, fetchNewsSegment]);

const getNewsPlaylistItems = useCallback(() => {
  if (!newsSegment || newsSegment.status !== 'ok') return [];
  return [
    { type: 'news_intro', title: 'Groovie News', audio_url: newsSegment.intro_url },
    { type: 'news', title: 'News Update', audio_url: newsSegment.audio_url, headlines: newsSegment.headlines },
    { type: 'news_outro', title: 'Back to Music', audio_url: newsSegment.outro_url },
  ];
}, [newsSegment]);

  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const wantPlayingRef = useRef(false);              // L2/L3 mobile: user INTENT — should the (business) stream be playing?
  const [needsResume, setNeedsResume] = useState(false); // mobile gesture trap: reconnect play() was blocked -> show one-tap Resume
  const cfg = useRuntimeConfig();                        // L2: runtime flags (playbackResilience, l2R2Fallback, tunables)
  const [fallbackActive, setFallbackActive] = useState(false); // L2: R2 degraded-mode banner ("Reconnecting to live…")
  // L2 DEBUG INSTRUMENT (ships dark; ?l2_debug=1 only). The resilience effect writes ladder/latch state to
  // dbgRef; a separate always-on capture effect logs raw media events to dbgEventsRef. The overlay renders both.
  const dbgRef = useRef({ hasPlayed: false, reconnecting: false, attempt: 0, nextRetryAt: 0, exhausted: false, lastPlayResult: '—', fbOn: false, firstFailAt: 0 });
  const dbgEventsRef = useRef([]);                       // ring buffer: last media events {name, t}
  const [dbgTick, setDbgTick] = useState(0);            // forces overlay re-render on interval + events
  // Lift fallback state to the App so the minimized mini-bar can show the "reconnecting" indicator too
  // (a venue running this keeps the player minimized — the status must be visible there, not only in the card).
  useEffect(() => { if (onFallbackChange) onFallbackChange(fallbackActive); return () => { if (onFallbackChange) onFallbackChange(false); }; }, [fallbackActive, onFallbackChange]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showShare, setShowShare] = useState(false);

  const isBusinessChannel = station.isBusinessChannel || false;
  const currentItem = playlist[currentIndex];
  const gradient = c(station.color).grad;

  // L4 (2.5.3c): reset the per-session non-emergency injection counter on app open (mount).
  useEffect(() => { try { localStorage.setItem('groovie_inj_count', '0'); } catch { /* non-fatal */ } }, []);

  // -- 2.5.3b a-lite follow-announcement injection (AUDIO-LAYER; playback-safe) --------------
  // Picks ONE unheard followed-business announcement for the top of a fresh playlist build.
  // Rules: <=1 per build, <=2 non-emergency per app-open session (L4), session dedupe via
  // localStorage, round-robin across businesses, emergency prioritized (exempt from the cap).
  // EVERYTHING is wrapped: any failure returns null -> normal playback.
  const pickFollowInjection = () => {
    try {
      const feed = Array.isArray(followFeed) ? followFeed : [];
      if (!feed.length) { console.log('[Follow] injection skipped: none-eligible (no feed items)'); return null; }
      let heard = []; try { heard = JSON.parse(localStorage.getItem('groovie_heard_ann') || '[]'); } catch { heard = []; }
      const fresh = feed.filter(a => a && a.audio_url && !heard.includes(a.id));
      if (!fresh.length) { console.log(`[Follow] injection skipped: capped/deduped (all ${feed.length} item(s) already heard this session)`); return null; }
      let last = null; try { last = localStorage.getItem('groovie_last_ann_biz'); } catch { last = null; }
      const pick = fresh.find(a => a.is_emergency)                                   // emergency first
        || fresh.find(a => String(a.company_id) !== String(last))                    // else round-robin to a new business
        || fresh[0];
      // L4 (2.5.3c): per-session cap — at most 2 NON-emergency injections per app-open; emergency ALWAYS exempt.
      // Counter is reset to 0 on app open (mount effect below). Overflow degrades to the Following feed only.
      if (!pick.is_emergency) {
        let injCount = 0; try { injCount = parseInt(localStorage.getItem('groovie_inj_count') || '0', 10) || 0; } catch { injCount = 0; }
        if (injCount >= 2) { console.log(`[Follow] injection capped: ${injCount} non-emergency already this session -> feed only`); return null; }
        try { localStorage.setItem('groovie_inj_count', String(injCount + 1)); } catch { /* non-fatal */ }
      }
      try {
        localStorage.setItem('groovie_heard_ann', JSON.stringify([...heard, pick.id].slice(-200)));
        localStorage.setItem('groovie_last_ann_biz', String(pick.company_id));
      } catch { /* non-fatal */ }
      console.log(`[Follow] injected announcement ${pick.id} at playlist top`);
      return { type: 'announcement', subtype: 'business', title: pick.business_name || 'Update',
               audio_url: pick.audio_url, business_name: pick.business_name, announcementId: pick.id,
               is_offer: !!pick.is_offer, is_emergency: !!pick.is_emergency };
    } catch (e) { console.warn('[FOLLOW-INJECT] announcement pick failed — falling back to normal playback:', e); return null; }
  };
  // Build a normal playlist, then (best-effort) prepend one announcement. generatePlaylist is untouched;
  // any failure/slow-fetch degrades SILENTLY to normal playback (announced with a loud warn, never blocks the build).
  const buildFollowInjectedPlaylist = () => {
    const pl = generatePlaylist(station.slug, isPremium, isGroovieChannel);
    try { const inj = pickFollowInjection(); if (inj) pl.unshift(inj); }
    catch (e) { console.warn('[FOLLOW-INJECT] injection skipped — normal playlist served:', e); }
    return pl;
  };

  useEffect(() => {
    if (isBusinessChannel) {
      setPlaylist([{ type: 'stream', title: 'Live Stream', audio_url: station.stream_url }]);
      setCurrentIndex(0);
    } else if (prebuiltPlaylist && prebuiltPlaylist.length > 0) {
      setPlaylist(prebuiltPlaylist);
      setCurrentIndex(0);
    } else {
      setPlaylist(buildFollowInjectedPlaylist());   // 2.5.3b: normal build + best-effort top-of-playlist announcement
      setCurrentIndex(0);
    }
  }, [station.slug, station.stream_url, isPremium, isGroovieChannel, isBusinessChannel, prebuiltPlaylist]);

  useEffect(() => {
    if (!currentItem || !audioRef.current) return;
    const item = currentItem;
    let stallTimer = null;
    const t = setTimeout(() => {
      if (!audioRef.current || item !== playlist[currentIndex]) return;
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        wantPlayingRef.current = true;   // L2/L3: app is now intending playback
        stallTimer = setTimeout(() => {
          if (audioRef.current && audioRef.current.currentTime === 0) {
            console.log('[PLAYER] Stall detected - skipping');
            playNext(false);
          }
        }, 6000);
      }).catch((err) => { console.warn('play() rejected:', err); });
    }, 50);
    const clearStall = () => { if (stallTimer) clearTimeout(stallTimer); };
    if (audioRef.current) audioRef.current.addEventListener('timeupdate', clearStall, { once: true });
    return () => {
      clearTimeout(t);
      clearStall();
      if (audioRef.current) audioRef.current.removeEventListener('timeupdate', clearStall);
    };
  }, [currentIndex, playTrigger])

  // L2/L3 (2.5.4/2.5.4b MOBILE) PLAYBACK RESILIENCE — reconnect-with-backoff on a BUSINESS-STREAM stall.
  // Desktop failure (Aug-27 bounce): stream drop -> keeps playing ~buffer -> waiting/stalled -> currentTime
  // FREEZES (paused stays false) -> never recovers. Mobile failure (Femi real-device, Aug-28): the element
  // PAUSES/suspends on network loss, so a "!paused" detector never fires; and a programmatic play() after
  // suspension can be REJECTED (no user gesture). Fix: treat an INVOLUNTARY pause (wantPlaying but element
  // paused) as a stall too, run a single backoff ladder (not re-entrant), and if play() is gesture-blocked
  // surface a one-tap Resume instead of dying silently. Business live stream only; wrapped/flagged.
  useEffect(() => {
    // Runtime-flagged: the ladder arms only when cfg.playbackResilience is true (field-toggleable).
    if (RESILIENCE_BUILD_KILL || !cfg.playbackResilience || !isBusinessChannel || !audioRef.current) return;
    const a = audioRef.current;
    const BACKOFFS = [1000, 2000, 4000, 8000];
    let attempt = 0, frozenTicks = 0, lastCt = -1, pausedTicks = 0, hasPlayed = false;
    let backoffTimer = null, stallTimer = null, reconnecting = false;
    let firstFailAt = 0;                          // L2: ms epoch of the first unrecovered stall (0 = healthy)
    // ---- L2 R2-fallback state (business-only degraded mode) --------------------------------------
    let fbOn = false, fbTracks = [], fbIndex = 0, probeTimer = null;
    // LATENCY FIX (measured, not theorized): each ladder load()/play() against a dead mount HANGS on a
    // network timeout, and the exhaust check lived at the top of attemptLadder (only re-entered after
    // play() settles) -> fallback engaged ~40-55s late. Fix: a DEADLINE timer fires engageFallback at
    // exactly firstFailAt+exhaustMs regardless of the hung ladder, and tracks are PREFETCHED at first
    // stall so they're ready the instant exhaust fires. Target: music within ~exhaust+buffer of silence.
    let exhaustTimer = null, fbPrefetch = null;
    const prefetchFallback = () => {
      if (fbPrefetch || fbTracks.length) return;
      fbPrefetch = fetch(`${BUSINESS_API_URL}/public/channels/${station.id}/fallback-tracks`)
        .then(r => (r.ok ? r.json() : {})).then(j => { fbTracks = Array.isArray(j.tracks) ? j.tracks.slice() : []; })
        .catch(() => { fbTracks = []; });
    };
    const clearExhaust = () => { if (exhaustTimer) { clearTimeout(exhaustTimer); exhaustTimer = null; } };
    const armExhaustDeadline = () => {
      if (exhaustTimer || !cfg.l2R2Fallback) return;
      exhaustTimer = setTimeout(() => { exhaustTimer = null; if (!fbOn && hasPlayed && wantPlayingRef.current) engageFallback(); }, cfg.l2LadderExhaustMs);
    };
    const dbg = dbgRef.current;                    // L2 debug instrument: mirror closure state for the overlay
    const syncDbg = () => { dbg.hasPlayed = hasPlayed; dbg.reconnecting = reconnecting; dbg.attempt = attempt; dbg.fbOn = fbOn; dbg.firstFailAt = firstFailAt; dbg.exhausted = !!(firstFailAt && (Date.now() - firstFailAt) >= cfg.l2LadderExhaustMs); };
    const stopLadder = () => { reconnecting = false; if (backoffTimer) { clearTimeout(backoffTimer); backoffTimer = null; } syncDbg(); };

    // ---- L2 fallback: play the channel's own R2 music when the live stream is unrecoverable -------
    const playFbTrack = () => {
      const el = audioRef.current;
      if (!el || !fbOn || !fbTracks.length) return;
      const url = fbTracks[fbIndex % fbTracks.length];
      console.warn(`[L2-FALLBACK] playing R2 track ${(fbIndex % fbTracks.length) + 1}/${fbTracks.length}`);
      try { el.src = url; el.load(); const p = el.play(); if (p && p.catch) p.catch(() => { setNeedsResume(true); }); } catch (e) { /* try next on ended */ }
    };
    const onFbEnded = () => { if (fbOn) { fbIndex += 1; playFbTrack(); } };  // loop the shuffled R2 list
    const stopProbe = () => { if (probeTimer) { clearInterval(probeTimer); probeTimer = null; } };
    const recoverToLive = () => {
      if (!fbOn) return;
      console.warn('[L2-FALLBACK] live recovered -> returning to stream');
      stopProbe(); fbOn = false; setFallbackActive(false); firstFailAt = 0; attempt = 0; reconnecting = false; syncDbg();
      const el = audioRef.current;
      if (el) { try { el.src = station.stream_url; el.load(); const p = el.play(); if (p && p.catch) p.catch(() => setNeedsResume(true)); } catch (e) { /* onErr re-ladders */ } }
    };
    const probeOnce = () => {                      // background live-stream probe (muted, throwaway element)
      if (!fbOn) return;
      let pr = null, settled = false;
      const done = (ok) => { if (settled) return; settled = true; try { pr && pr.pause(); if (pr) pr.src = ''; } catch (e) {} if (ok) recoverToLive(); };
      try {
        pr = new Audio(); pr.muted = true; pr.preload = 'auto'; pr.src = station.stream_url;
        pr.addEventListener('playing', () => setTimeout(() => done(pr && pr.currentTime > 0.2), 1500));
        pr.addEventListener('error', () => done(false));
        const p = pr.play(); if (p && p.catch) p.catch(() => { /* error event decides */ });
        setTimeout(() => done(false), Math.min(Math.max(cfg.l2ProbeMs - 1000, 3000), 8000));
      } catch (e) { done(false); }
    };
    const startProbe = () => { stopProbe(); probeTimer = setInterval(probeOnce, cfg.l2ProbeMs); };
    const engageFallback = async () => {
      if (fbOn || !cfg.l2R2Fallback) return;
      fbOn = true; setFallbackActive(true); stopLadder(); clearExhaust(); dbg.fbOn = true; dbg.exhausted = true;
      console.warn('[L2-FALLBACK] engaged (live stream unrecoverable)');
      if (!fbTracks.length) { prefetchFallback(); try { await fbPrefetch; } catch (e) { /* fbTracks stays [] */ } } // tracks usually already prefetched at first stall
      if (!fbOn) return;                            // torn down while awaiting
      if (!fbTracks.length) { console.warn('[L2-FALLBACK] no R2 tracks -> abort, keep laddering'); fbOn = false; setFallbackActive(false); return; }
      fbIndex = 0; playFbTrack(); startProbe();
    };
    const teardownFallback = (restoreLive) => {     // user paused / effect teardown: leave degraded mode cleanly
      if (!fbOn && !restoreLive) return;
      stopProbe(); clearExhaust(); fbOn = false; setFallbackActive(false); firstFailAt = 0; attempt = 0; syncDbg();
      const el = audioRef.current;
      if (restoreLive && el && el.src !== station.stream_url) { try { el.src = station.stream_url; } catch (e) {} }
    };

    const attemptLadder = () => {
      const el = audioRef.current;
      if (!el || !wantPlayingRef.current) { reconnecting = false; return; }
      // L2: once the ladder has churned for the exhaust window with no recovery, hand off to R2 fallback.
      if (cfg.l2R2Fallback && !fbOn && hasPlayed && firstFailAt && (Date.now() - firstFailAt) >= cfg.l2LadderExhaustMs) {
        engageFallback(); return;
      }
      const delay = BACKOFFS[Math.min(attempt, BACKOFFS.length - 1)];
      attempt += 1;
      dbg.attempt = attempt; dbg.nextRetryAt = Date.now() + delay; dbg.reconnecting = true; // debug
      console.log(`[RESILIENCE] business-stream stall -> reconnect attempt ${attempt} (in ${delay}ms)`);
      if (backoffTimer) clearTimeout(backoffTimer);
      backoffTimer = setTimeout(() => {
        const el2 = audioRef.current;
        if (!el2 || !wantPlayingRef.current) { reconnecting = false; return; }
        try {
          el2.load();
          const p = el2.play();
          if (p && typeof p.then === 'function') {
            p.then(() => { dbg.lastPlayResult = 'fulfilled'; }).catch((err) => {
              dbg.lastPlayResult = (err && err.name) || 'error';   // debug: exact play() rejection
              if (err && err.name === 'NotAllowedError') {   // gesture trap -> wait for a user tap
                console.warn('[RESILIENCE] reconnect play() blocked (no gesture) -> Resume affordance');
                setNeedsResume(true); stopLadder();
              } else { attemptLadder(); }                     // stream not back yet -> keep laddering
            });
          } else { dbg.lastPlayResult = 'no-promise'; attemptLadder(); }
        } catch (e) { dbg.lastPlayResult = 'throw'; attemptLadder(); }
      }, delay);
    };
    // has-played latch: the resilience NEVER arms until the stream has successfully played once, so it can
    // never interfere with initial load/startup (the class of bug that broke all business streams).
    const startReconnect = () => {
      if (fbOn || reconnecting || !wantPlayingRef.current || !hasPlayed) return;  // fallback owns the element while degraded
      if (!firstFailAt) { firstFailAt = Date.now(); prefetchFallback(); armExhaustDeadline(); } // L2: start outage window + prefetch + deadline
      reconnecting = true; syncDbg(); attemptLadder();
    };
    const onPlaying = () => { hasPlayed = true; if (attempt) console.log('[RESILIENCE] business-stream recovered'); attempt = 0; frozenTicks = 0; lastCt = -1; pausedTicks = 0; if (!fbOn) { firstFailAt = 0; clearExhaust(); } setNeedsResume(false); dbg.lastPlayResult = 'playing-event'; stopLadder(); };
    // stalled/waiting: confirm still stalled after 3s (covers desktop buffer-drain)
    const onStall = () => { if (fbOn || stallTimer) return; stallTimer = setTimeout(() => { stallTimer = null; const el = audioRef.current; if (el && wantPlayingRef.current && el.readyState < 3) startReconnect(); }, 3000); };
    // (1) paused-state trap: an INVOLUNTARY pause while the user wants playback IS a stall (mobile)
    const onPause = () => { if (!fbOn && wantPlayingRef.current && !document.hidden) { console.log('[RESILIENCE] involuntary pause -> reconnect'); startReconnect(); } };
    const onErr = () => { if (!fbOn) startReconnect(); };
    // returning to the app after a background blip: if we should be playing but aren't, reconnect
    const onVis = () => { if (!fbOn && !document.hidden && wantPlayingRef.current) { const el = audioRef.current; if (el && (el.paused || el.readyState < 3)) startReconnect(); } };
    a.addEventListener('playing', onPlaying);
    a.addEventListener('stalled', onStall);
    a.addEventListener('waiting', onStall);
    a.addEventListener('pause', onPause);
    a.addEventListener('error', onErr);
    a.addEventListener('ended', onFbEnded);         // L2: advance the R2 fallback list
    document.addEventListener('visibilitychange', onVis);
    // currentTime-frozen / paused watchdog (covers browsers that suppress stalled/waiting)
    const iv = setInterval(() => {
      const el = audioRef.current;
      // user paused on purpose while degraded -> leave fallback, restore live src for the next resume
      if (fbOn && !wantPlayingRef.current) { teardownFallback(true); return; }
      if (fbOn) return;                             // fallback is driving the element; skip stall detection
      if (!el || !wantPlayingRef.current || !hasPlayed) { frozenTicks = 0; pausedTicks = 0; lastCt = -1; return; }
      if (el.paused) { pausedTicks += 1; if (pausedTicks >= 3) startReconnect(); return; }  // sustained pause = stall
      pausedTicks = 0;
      if (el.currentTime === lastCt) frozenTicks += 1; else { frozenTicks = 0; lastCt = el.currentTime; }
      if (frozenTicks >= 4) { frozenTicks = 0; startReconnect(); }
    }, 1000);
    return () => {
      a.removeEventListener('playing', onPlaying); a.removeEventListener('stalled', onStall);
      a.removeEventListener('waiting', onStall); a.removeEventListener('pause', onPause);
      a.removeEventListener('error', onErr); a.removeEventListener('ended', onFbEnded);
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(iv); if (stallTimer) clearTimeout(stallTimer); clearExhaust(); stopLadder(); teardownFallback(false);
    };
  }, [isBusinessChannel, station.stream_url, station.id, cfg.playbackResilience, cfg.l2R2Fallback, cfg.l2LadderExhaustMs, cfg.l2ProbeMs]);

  // ---- L2 DEBUG CAPTURE (always-on when ?l2_debug=1) --------------------------------------------
  // Independent of the resilience flag so it records the RAW media-event timeline even if the ladder
  // never arms (the suspected failure). Logs the 8 media events + online/offline/visibility, and forces
  // an overlay re-render on every event AND a 500ms heartbeat (whose freshness itself reveals timer
  // suspension). Writes nothing to playback — pure observer.
  useEffect(() => {
    if (!cfg.debug) return;
    const el = audioRef.current;
    const stamp = () => { const d = new Date(); return d.toTimeString().slice(0, 8) + '.' + String(d.getMilliseconds()).padStart(3, '0'); };
    const push = (name) => {
      const buf = dbgEventsRef.current;
      buf.push({ name, t: stamp() });
      if (buf.length > 8) buf.shift();
      setDbgTick(t => t + 1);
    };
    const MEDIA = ['stalled', 'waiting', 'pause', 'play', 'playing', 'error', 'suspend', 'emptied'];
    const handlers = MEDIA.map(name => { const h = () => push(name); if (el) el.addEventListener(name, h); return [name, h]; });
    const onOnline = () => push('net:online');
    const onOffline = () => push('net:offline');
    const onVis = () => push(document.hidden ? 'vis:hidden' : 'vis:visible');
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVis);
    const hb = setInterval(() => setDbgTick(t => t + 1), 500);   // heartbeat: last-render clock exposes timer suspension
    return () => {
      handlers.forEach(([name, h]) => { if (el) el.removeEventListener(name, h); });
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(hb);
    };
  }, [cfg.debug, station.stream_url]);

  const playNext = useCallback((isManualSkip = false) => {
    if (isBusinessChannel) return;
    if (_updatePending) { reloadForUpdate('track-boundary'); return; }   // L2: pick up a new build at a natural boundary
    const item = playlist[currentIndex];
    if (item && !item.isNews) songsSinceNewsRef.current += 1;
    if (songsSinceNewsRef.current >= 6 && newsSegment && newsSegment.status === 'ok') {
      const newsItems = [
        { type: 'news_intro', title: 'Groovie News', audio_url: newsSegment.intro_url, isNews: true },
        { type: 'news', title: 'News Update', audio_url: newsSegment.audio_url, isNews: true },
        { type: 'news_outro', title: 'Back to Music', audio_url: newsSegment.outro_url, isNews: true },
      ];
      const newPlaylist = [...playlist];
      newPlaylist.splice(currentIndex + 1, 0, ...newsItems);
      setPlaylist(newPlaylist);
      setCurrentIndex(currentIndex + 1);
      songsSinceNewsRef.current = 0;
      skipCountRef.current = 0;
      console.log('[NEWS] Injected after song', currentIndex);
      return;
    }
    // Skip limit for free users: 6 per hour
    if (SUBSCRIPTIONS_ENABLED && !isPremium && isManualSkip) {
      const now = Date.now();
      if (now - skipResetTimeRef.current > 3600000) {
        skipCountRef.current = 0;
        skipResetTimeRef.current = now;
      }
      skipCountRef.current += 1;
      if (skipCountRef.current > 6) {
        console.log('[GATE] Skip limit reached (6/hr)');
        return;
      }
    }
    if (currentIndex < playlist.length - 1) setCurrentIndex(currentIndex + 1);
    else {
      if (prebuiltPlaylist && prebuiltPlaylist.length > 0) {
        setPlaylist([...prebuiltPlaylist].sort(() => Math.random() - 0.5));
      } else {
        setPlaylist(buildFollowInjectedPlaylist());   // 2.5.3b: regen keeps injecting (dedupe prevents repeats)
      }
      setCurrentIndex(0);
      setPlayTrigger(t => t + 1);
      songsSinceNewsRef.current = 0;
      skipCountRef.current = 0;
      skipResetTimeRef.current = Date.now();
    }
  }, [currentIndex, playlist, station.slug, isPremium, isGroovieChannel, isBusinessChannel, prebuiltPlaylist, newsSegment]);

  const playPrevious = () => { if (!isBusinessChannel && currentIndex > 0) setCurrentIndex(currentIndex - 1); };
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      wantPlayingRef.current = false;             // user paused ON PURPOSE -> resilience must not fight it
      audioRef.current.pause();
    } else {
      wantPlayingRef.current = true;
      setNeedsResume(false);
      audioRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };
  // Mobile gesture-trap recovery: reconnect's play() was blocked (no user gesture) — this tap IS the gesture.
  const resumePlayback = () => {
    const el = audioRef.current; if (!el) return;
    wantPlayingRef.current = true; setNeedsResume(false);
    try { el.load(); el.play().then(() => setIsPlaying(true)).catch(() => {}); } catch (e) { /* noop */ }
  };

  const formatTime = (s) => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`;
  const progress = duration ? (currentTime / duration) * 100 : 0;
  const StationIcon = CHANNEL_ICONS[station.slug] || STATION_ICONS[station.slug] || (isBusinessChannel ? Building : Radio);

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-xl z-50 flex items-center justify-center p-4">
      {showShare && <ShareModal station={station} onClose={() => setShowShare(false)} />}
      <div className="w-full max-w-md">
        <div className="flex justify-between absolute top-6 left-6 right-6">
          <button onClick={onMinimize || onClose} className="text-white/40 hover:text-white transition">
            <ChevronRight size={24} strokeWidth={1.5} className="rotate-90" />
          </button>
          <button onClick={onClose} className="text-white/40 hover:text-white transition">
            <X size={24} strokeWidth={1.5} />
          </button>
        </div>

        <div className={`w-64 h-64 mx-auto rounded-2xl bg-gradient-to-br ${gradient} shadow-2xl flex items-center justify-center mb-8 relative overflow-hidden`}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-white/5" />
          <div className="text-center relative">
            <div className="w-20 h-20 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-3">
              <StationIcon size={32} strokeWidth={1.5} />
            </div>
            <p className="text-white/80 text-sm font-medium">{station.name}</p>
            {isBusinessChannel && !_IS_MOMENT && station.company_name && <p className="text-white/50 text-xs mt-1">{station.company_name}</p>}
          </div>
        </div>

        <div className="text-center mb-6">
          <h2 className="text-white text-xl font-semibold mb-1 truncate">
            {isBusinessChannel ? 'Live Stream' : currentItem?.title || 'Loading...'}
          </h2>
          <p className="text-white/50 text-sm">
            {isBusinessChannel ? station.company_name : currentItem?.artist ? ARTISTS[currentItem.artist]?.name || currentItem.artist : currentItem?.host || station.name}
          </p>
          {isBusinessChannel && fallbackActive ? (
            /* L2 degraded mode: live stream unrecoverable -> the room hears the station's own music, never silence */
            <div className="flex items-center justify-center gap-2 mt-2">
              <Loader2 size={12} className="text-amber-400 animate-spin" />
              <span className="text-amber-400 text-xs font-medium">Reconnecting to live…</span>
            </div>
          ) : (isBusinessChannel && station.is_live && (
            <div className="flex items-center justify-center gap-2 mt-2">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /><span className="text-red-400 text-xs font-medium">LIVE</span>
            </div>
          ))}
        </div>

        {!isBusinessChannel && (
          <div className="mb-6">
            <div className="bg-white/10 rounded-full h-1 cursor-pointer" onClick={(e) => {
              const rect = e.target.getBoundingClientRect();
              if (audioRef.current) audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
            }}>
              <div className="bg-white h-1 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex justify-between text-xs text-white/40 mt-2">
              <span>{formatTime(currentTime)}</span><span>{formatTime(duration)}</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-center gap-8 mb-8">
          <button onClick={() => onToggleFavorite(station.slug)} className={`transition ${isFavorite ? 'text-red-500' : 'text-white/40 hover:text-white'}`}>
            <Heart size={20} strokeWidth={1.5} fill={isFavorite ? 'currentColor' : 'none'} />
          </button>
          {!isBusinessChannel && (
            <button onClick={playPrevious} className="text-white/60 hover:text-white transition" disabled={currentIndex === 0}>
              <SkipBack size={20} strokeWidth={1.5} />
            </button>
          )}
          <button onClick={togglePlay} className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-black hover:scale-105 transition">
            {isPlaying ? <Pause size={24} strokeWidth={1.5} /> : <Play size={24} strokeWidth={1.5} />}
          </button>
          {!isBusinessChannel && (
            <button onClick={() => playNext(true)} className="text-white/60 hover:text-white transition">
              <SkipForward size={20} strokeWidth={1.5} />
            </button>
          )}
          <button onClick={() => setShowShare(true)} className="text-white/40 hover:text-white transition">
            <Share2 size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* L2/L3 mobile gesture-trap: reconnect play() was blocked -> honest one-tap Resume, never a silent death */}
        {needsResume && isBusinessChannel && (
          <button onClick={resumePlayback}
            className="w-full mb-6 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#fc552d] text-white font-semibold text-sm hover:brightness-110 transition">
            <Play size={16} strokeWidth={2} /> Connection dropped — tap to resume
          </button>
        )}

        {!isBusinessChannel && (
          <div className="bg-white/5 rounded-xl p-4 max-h-48 overflow-y-auto">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Up Next</p>
            {playlist.slice(currentIndex + 1, currentIndex + 5).map((item, idx) => (
              <button key={idx} onClick={() => setCurrentIndex(currentIndex + 1 + idx)}
                className="w-full flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-white/5 transition text-left">
                <div className={`w-8 h-8 rounded bg-gradient-to-br ${gradient} flex items-center justify-center text-white/80 text-xs`}>
                  {item.type === 'music' ? <Music size={12} /> : item.type === 'content' ? <Mic size={12} /> : <Radio size={12} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white/90 text-sm truncate">{item.title}</p>
                  <p className="text-white/40 text-xs truncate">{item.artist ? ARTISTS[item.artist]?.name : item.host || ''}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {currentItem && (
          <audio ref={audioRef} src={currentItem.audio_url}
            onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
            onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
            onEnded={() => playNext(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onError={() => { console.log('[PLAYER] Audio error - skipping'); playNext(false); }}
          />
        )}
        {cfg.debug && (() => {
          const el = audioRef.current;
          const d = dbgRef.current;
          const now = new Date();
          const clock = now.toTimeString().slice(0, 8) + '.' + String(now.getMilliseconds()).padStart(3, '0');
          const RS = ['0-NOTHING', '1-METADATA', '2-CURRENT', '3-FUTURE', '4-ENOUGH'];
          const NS = ['0-EMPTY', '1-IDLE', '2-LOADING', '3-NO_SOURCE'];
          const nextIn = d.nextRetryAt ? Math.max(0, d.nextRetryAt - now.getTime()) : 0;
          const events = dbgEventsRef.current.slice().reverse();
          const row = (k, v) => (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ color: '#8ab4ff' }}>{k}</span><span style={{ color: '#e8e8e8' }}>{String(v)}</span>
            </div>
          );
          // Portal to document.body so the instrument escapes the minimized-player container
          // (which is moved off-screen to -9999px) and is always visible during a field test.
          return createPortal((
            <div data-tick={dbgTick} style={{
              position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 6px)', left: 6, zIndex: 2147483647, pointerEvents: 'none',
              width: 232, maxWidth: '92vw', background: 'rgba(0,0,0,0.82)', color: '#e8e8e8',
              font: '10px/1.35 ui-monospace,Menlo,Consolas,monospace', padding: '7px 8px',
              borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', letterSpacing: '0.02em',
            }}>
              <div style={{ color: '#ffd27a', fontWeight: 700, marginBottom: 3 }}>L2 DEBUG · {clock}</div>
              {row('flags R/F', `${cfg.playbackResilience ? 'on' : 'off'}/${cfg.l2R2Fallback ? 'on' : 'off'}`)}
              {row('exhaust ms', cfg.l2LadderExhaustMs)}
              {row('hasPlayed', d.hasPlayed)}
              {row('wantPlaying', wantPlayingRef.current)}
              {row('doc.hidden', document.hidden)}
              {row('paused', el ? el.paused : '—')}
              {row('readyState', el ? (RS[el.readyState] || el.readyState) : '—')}
              {row('networkState', el ? (NS[el.networkState] || el.networkState) : '—')}
              {row('ladder', `att ${d.attempt}${d.reconnecting ? '·rc' : ''}${d.exhausted ? '·EXH' : ''}${d.fbOn ? '·FB' : ''}`)}
              {row('fallbackActive', fallbackActive)}
              {row('next retry', d.reconnecting ? nextIn + 'ms' : '—')}
              {row('play() res', d.lastPlayResult)}
              <div style={{ color: '#ffd27a', marginTop: 3 }}>events (new→old)</div>
              {events.length === 0 && <div style={{ color: '#888' }}>—none—</div>}
              {events.map((e, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ color: e.name === 'error' ? '#ff7a7a' : e.name.startsWith('net') ? '#7affb0' : '#cfcfcf' }}>{e.name}</span>
                  <span style={{ color: '#9a9a9a' }}>{e.t}</span>
                </div>
              ))}
            </div>
          ), document.body);
        })()}
      </div>
    </div>
  );
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// CHANNEL CARD (Shows) â€” mix mode support
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const ChannelCard = ({ channel, onPlay, isFavorite, onToggleFavorite, isMixMode, isSelected, onToggleMix }) => {
  const Icon = CHANNEL_ICONS[channel.slug] || Mic;
  const colors = c(channel.color);
  const handleClick = () => isMixMode ? onToggleMix(channel) : onPlay(channel, true);

  return (
    <div
      className={`group bg-gradient-to-br ${colors.card} border ${isMixMode && isSelected ? 'border-green-400/60 ring-1 ring-green-400/30' : isMixMode ? 'border-dashed border-white/20' : colors.border} rounded-2xl p-5 transition-all duration-300 cursor-pointer hover:shadow-xl hover:shadow-black/20 hover:-translate-y-0.5 relative`}
      onClick={handleClick}
    >
      {isMixMode && isSelected && (
        <div className="absolute top-3 right-3 w-6 h-6 border-2 border-green-400 rounded-full flex items-center justify-center z-10">
          <div className="w-3 h-3 bg-green-400 rounded-full" />
        </div>
      )}
      {isMixMode && !isSelected && (
        <div className="absolute top-3 right-3 w-6 h-6 border-2 border-white/25 rounded-full z-10" />
      )}
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 ${colors.solid} rounded-xl flex items-center justify-center shadow-lg text-white`}>
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <div className="flex items-center gap-2">
          {!isMixMode && (
            <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(channel.slug); }}
              className={`transition-all duration-200 ${isFavorite ? 'text-red-500 scale-110' : 'text-white/20 hover:text-white/50'}`}>
              <Heart size={18} strokeWidth={1.5} fill={isFavorite ? 'currentColor' : 'none'} />
            </button>
          )}
          <span className="text-[10px] text-white/50 bg-white/[0.08] px-2 py-0.5 rounded-full font-medium tracking-wide">{channel.badge}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-1">
        <h3 className="text-white font-semibold text-lg tracking-tight">{channel.name}</h3>
        <span className="text-[9px] text-amber-400/70 bg-amber-400/10 px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider">Show</span>
      </div>
      <p className="text-white/40 text-sm mb-4 leading-relaxed">{channel.tagline}</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-white/30 text-xs">
          <User size={12} strokeWidth={1.5} /><span>{channel.listeners}</span>
        </div>
        {!isMixMode && (
          <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/70 group-hover:bg-white group-hover:text-black transition-all duration-300 group-hover:scale-110">
            <Play size={16} strokeWidth={1.5} />
          </div>
        )}
      </div>
    </div>
  );
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// STATION CARD (Music) â€” mix mode support
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const StationCard = ({ station, onPlay, isFavorite, onToggleFavorite, isMixMode, isSelected, onToggleMix }) => {
  const Icon = STATION_ICONS[station.slug] || Music;
  const colors = c(station.color);
  const handleClick = () => isMixMode ? onToggleMix(station) : onPlay(station, false);

  return (
    <div className="group cursor-pointer" onClick={handleClick}>
      <div className={`aspect-square rounded-2xl bg-gradient-to-br ${colors.grad} p-4 flex flex-col justify-between mb-3 shadow-lg group-hover:shadow-2xl group-hover:shadow-black/30 transition-all duration-300 group-hover:scale-[1.03] relative overflow-hidden ${isMixMode && isSelected ? 'ring-2 ring-green-400/60' : isMixMode ? 'ring-1 ring-dashed ring-white/20' : ''}`}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-white/5 pointer-events-none" />
        {isMixMode && isSelected && (
          <div className="absolute top-2 left-2 w-6 h-6 border-2 border-green-400 rounded-full flex items-center justify-center z-10">
            <div className="w-3 h-3 bg-green-400 rounded-full" />
          </div>
        )}
        {isMixMode && !isSelected && (
          <div className="absolute top-2 left-2 w-6 h-6 border-2 border-white/25 rounded-full z-10" />
        )}
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-1 text-white/60 text-xs">
            <User size={12} strokeWidth={1.5} /><span>{station.listeners}</span>
          </div>
          {!isMixMode && (
            <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(station.slug); }}
              className={`transition-all duration-200 ${isFavorite ? 'text-red-500 scale-110' : 'text-white/25 hover:text-white/60'}`}>
              <Heart size={16} strokeWidth={1.5} fill={isFavorite ? 'currentColor' : 'none'} />
            </button>
          )}
        </div>
        <div className="relative">
          <p className="text-white/70 text-[10px] uppercase tracking-[0.15em] font-medium">{station.genre}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
            <span className="text-white/60 text-[10px] uppercase tracking-wider">Live</span>
          </div>
        </div>
      </div>
      <h3 className="text-white font-medium tracking-tight">{station.name}</h3>
      <p className="text-white/35 text-sm">Autonomous Station</p>
    </div>
  );
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// BUSINESS CHANNEL CARD â€” mix mode + horizontal style for library
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const BusinessChannelCard = ({ channel, onPlay, isMixMode, isSelected, onToggleMix, showMixBtn, onMixAdd, onRemove, isFollowing, onToggleFollow, followBusy }) => {
  const color = MOOD_COLORS[channel.mood] || 'slate';
  const isLive = channel.is_live && channel.stream_url;
  const colors = c(color);
  const BizIcon = getBusinessIcon(channel.mood);
  const handleClick = () => {
    if (isMixMode) { onToggleMix && onToggleMix(channel); return; }
    if (isLive) onPlay(channel, true);
  };

  return (
    <div
      className={`group bg-gradient-to-br ${colors.card} border ${isMixMode && isSelected ? 'border-green-400/60 ring-1 ring-green-400/30' : isMixMode ? 'border-dashed border-white/20' : colors.border} rounded-2xl p-4 transition-all duration-300 ${isLive ? 'cursor-pointer hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5' : 'opacity-60'} relative`}
      onClick={handleClick}
    >
      {isMixMode && isSelected && (
        <div className="absolute top-3 right-3 w-6 h-6 border-2 border-green-400 rounded-full flex items-center justify-center z-10">
          <div className="w-3 h-3 bg-green-400 rounded-full" />
        </div>
      )}
      {isMixMode && !isSelected && isLive && (
        <div className="absolute top-3 right-3 w-6 h-6 border-2 border-white/25 rounded-full z-10" />
      )}
      {/* 2.5.3a UX revision: inline whole-brand Follow pill (replaces the standalone follow grid). */}
      {!isMixMode && onToggleFollow && channel.company_id != null && (
        <button onClick={(e) => { e.stopPropagation(); onToggleFollow(channel.company_id); }}
          disabled={followBusy}
          className={`absolute top-3 right-3 z-10 text-[10px] px-2.5 py-1 rounded-full font-semibold transition flex items-center gap-1 ${isFollowing ? 'bg-white/10 text-white/60 border border-white/15' : 'bg-white text-black hover:bg-white/90'}`}>
          {isFollowing ? <><Check size={11} />Following</> : <><Plus size={11} />Follow</>}
        </button>
      )}
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 ${colors.solid} rounded-xl flex items-center justify-center flex-shrink-0 text-white shadow-lg`}>
          <BizIcon size={20} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <h3 className="text-white font-semibold truncate">{channel.channel_name}</h3>
            <span className="text-[9px] text-blue-400/70 bg-blue-400/10 px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider flex-shrink-0">Biz</span>
          </div>
          <p className="text-white/45 text-sm truncate">{channel.company_name}</p>
          {channel.location_name && <p className="text-white/25 text-xs truncate mt-0.5">{channel.location_name}</p>}
          <div className="flex items-center gap-3 mt-2">
            {isLive ? (
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                <span className="text-red-400 text-[10px] font-semibold uppercase tracking-wider">Live</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                <span className="text-amber-400 text-[10px] font-semibold uppercase tracking-wider">Coming Soon</span>
              </div>
            )}
            {isLive && <div className="flex items-center gap-1 text-white/30 text-xs"><User size={10} strokeWidth={1.5} /><span>{channel.listeners}</span></div>}
            {showMixBtn && isLive && !isMixMode && (
              <button onClick={(e) => { e.stopPropagation(); onMixAdd && onMixAdd(channel); }}
                className="ml-auto text-white/30 hover:text-white/60 transition" title="Add to mix">
                <Zap size={14} strokeWidth={1.5} />
              </button>
            )}
            {onRemove && !isMixMode && (
              <button onClick={(e) => { e.stopPropagation(); onRemove(channel.id); }}
                className={`${showMixBtn ? '' : 'ml-auto'} text-white/20 hover:text-red-400 transition`} title="Remove from library">
                <X size={14} strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// DISCOVER BUSINESS CARD (for Discover tab â€” with Add to Library + Mix)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const DiscoverCard = ({ channel, isAdded, onAdd, onPlay, onMixAdd, isMixMode, isSelected, onToggleMix, isFollowing, onToggleFollow, followBusy }) => {
  const color = MOOD_COLORS[channel.mood] || 'slate';
  const isLive = channel.is_live && channel.stream_url;
  const colors = c(color);
  const BizIcon = getBusinessIcon(channel.mood);

  return (
    <div className={`bg-gradient-to-br ${colors.card} border ${isMixMode && isSelected ? 'border-green-400/60' : colors.border} rounded-2xl p-5 transition-all duration-300 ${isLive ? 'hover:shadow-lg hover:-translate-y-0.5' : 'opacity-60'}`}>
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 ${colors.solid} rounded-xl flex items-center justify-center text-white shadow-lg flex-shrink-0`}>
          <BizIcon size={24} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-lg truncate">{channel.channel_name}</h3>
          <p className="text-white/45 text-sm">{channel.company_name}</p>
          {channel.location_name && <p className="text-white/30 text-xs mt-0.5">{channel.location_name}</p>}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-[10px] text-white/40 bg-white/[0.06] px-2 py-0.5 rounded-full capitalize">{(channel.mood || 'general').replace(/_/g, ' ')}</span>
            {isLive ? (
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" /><span className="text-red-400 text-[10px] font-semibold">LIVE</span></span>
            ) : (
              <span className="text-amber-400 text-[10px] font-semibold">Coming Soon</span>
            )}
            {isLive && <span className="flex items-center gap-1 text-white/30 text-xs"><User size={10} strokeWidth={1.5} />{channel.listeners}</span>}
          </div>
        </div>
      </div>
      {isLive && (
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/[0.06]">
          {!isAdded ? (
            <button onClick={() => onAdd(channel.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-white/[0.06] hover:bg-white/10 rounded-xl text-white/60 hover:text-white text-sm transition">
              <Plus size={14} strokeWidth={1.5} /> Add to Library
            </button>
          ) : (
            <span className="flex-1 flex items-center justify-center gap-1.5 py-2 text-green-400 text-sm">
              <Check size={14} strokeWidth={1.5} /> In Library
            </span>
          )}
          <button onClick={() => isLive && onPlay(channel)} className="flex items-center justify-center gap-1.5 px-4 py-2 bg-white/[0.06] hover:bg-white/10 rounded-xl text-white/60 hover:text-white text-sm transition">
            <Play size={14} strokeWidth={1.5} /> Play
          </button>
          <button onClick={() => { onMixAdd(channel); }} className="flex items-center justify-center gap-1.5 px-4 py-2 bg-white/[0.06] hover:bg-white/10 rounded-xl text-white/60 hover:text-white text-sm transition">
            <Zap size={14} strokeWidth={1.5} /> Mix
          </button>
          {/* 2.5.3a UX revision: inline whole-brand Follow (replaces the standalone follow grid). */}
          {onToggleFollow && channel.company_id != null && (
            <button onClick={() => onToggleFollow(channel.company_id)} disabled={followBusy}
              className={`flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-sm transition ${isFollowing ? 'bg-white/10 text-white/60' : 'bg-white text-black hover:bg-white/90'}`}>
              {isFollowing ? <><Check size={14} strokeWidth={1.5} /> Following</> : <><Plus size={14} strokeWidth={1.5} /> Follow</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// NOW PLAYING BAR (persistent bottom bar)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const NowPlayingBar = ({ station, mixItems, onExpand, onStop, reconnecting }) => {
  const colors = c(station.color);
  const Icon = CHANNEL_ICONS[station.slug] || STATION_ICONS[station.slug] || (station.isBusinessChannel ? Building : Radio);
  const isMix = mixItems && mixItems.length > 0;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-4">
      <div className={`max-w-2xl mx-auto bg-neutral-900/95 backdrop-blur-xl border border-white/10 rounded-2xl p-3 flex items-center gap-3 shadow-2xl shadow-black/40 cursor-pointer`}
        onClick={onExpand}>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors.grad} flex items-center justify-center text-white flex-shrink-0 shadow-lg`}>
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">
            {isMix ? `Mix Play — ${mixItems.length} channels` : station.name}
          </p>
          {reconnecting ? (
            <p className="text-amber-400 text-xs truncate flex items-center gap-1">
              <Loader2 size={11} className="animate-spin flex-shrink-0" /> Reconnecting to live…
            </p>
          ) : (
            <p className="text-white/40 text-xs truncate">
              {isMix ? mixItems.slice(0, 3).map(i => i.name || i.channel_name).join(', ') : (station.isGroovieChannel ? 'Autonomous Show' : station.isBusinessChannel ? station.company_name : station.genre || 'Station')}
            </p>
          )}
        </div>
        {/* Mini visualizer */}
        <div className="flex items-end gap-[3px] h-5 mr-2">
          {[0, 0.15, 0.3, 0.1].map((delay, i) => (
            <div key={i} className="w-[3px] bg-white/60 rounded-full"
              style={{ animation: 'eqBounce 0.8s ease-in-out infinite', animationDelay: `${delay}s`, height: '4px' }} />
          ))}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onStop(); }} className="text-white/30 hover:text-white/60 transition flex-shrink-0">
          <Square size={16} strokeWidth={1.5} />
        </button>
      </div>
      <style>{`@keyframes eqBounce { 0%,100%{height:4px} 50%{height:18px} }`}</style>
    </div>
  );
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// FLOATING MIX TRAY
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const FloatingMixTray = ({ items, onClear, onPlay, isAbovePlayer, onRemoveItem }) => {
  if (items.length === 0) return null;
  const totalTracks = items.reduce((sum, item) => {
    const slug = item.slug || item.channel_slug;
    const music = STATION_MUSIC[slug] || [];
    const audio = CHANNEL_AUDIO[slug] || [];
    return sum + (music.length || audio.length || 0);
  }, 0);
  return (
    <div className={`fixed left-0 right-0 z-40 px-4 animate-slideUp ${isAbovePlayer ? 'bottom-20' : 'bottom-0 pb-4'}`}>
      <div className="max-w-2xl mx-auto bg-neutral-900/95 backdrop-blur-xl border border-green-500/20 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden">
        <div className="px-4 pt-3 pb-2 flex gap-2 overflow-x-auto scrollbar-hide">
          {items.map((item, i) => {
            const col = c(item.color || MOOD_COLORS[item.mood] || 'slate');
            const name = item.name || item.channel_name || 'Station';
            const slug = item.slug || item.channel_slug;
            const trackCount = (STATION_MUSIC[slug] || []).length || (CHANNEL_AUDIO[slug] || []).length || 0;
            return (
              <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gradient-to-r ${col.grad} bg-opacity-20 border border-white/10 flex-shrink-0 group`}>
                <Music size={12} className="text-white/70" strokeWidth={1.5} />
                <span className="text-white/80 text-xs font-medium whitespace-nowrap">{name}</span>
                {trackCount > 0 && <span className="text-white/30 text-[10px]">{trackCount}</span>}
                {onRemoveItem && (
                  <button onClick={(e) => { e.stopPropagation(); onRemoveItem(item); }}
                    className="w-4 h-4 rounded-full bg-white/10 hover:bg-red-500/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all ml-0.5">
                    <X size={8} className="text-white/60" strokeWidth={2.5} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-4 pb-3 pt-1 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-white/50 text-xs">
              {items.length} channel{items.length > 1 ? 's' : ''} {totalTracks > 0 && <span className="text-white/30">&middot; {totalTracks} tracks</span>}
            </p>
          </div>
          <button onClick={onClear} className="text-white/30 hover:text-white/60 text-xs transition px-3 py-1.5 rounded-lg hover:bg-white/5">Clear</button>
          <button onClick={onPlay}
            disabled={items.length < 2}
            className={`px-5 py-2 rounded-xl text-sm font-semibold transition flex items-center gap-1.5 ${items.length >= 2 ? 'bg-gradient-to-r from-green-500 to-emerald-400 text-black hover:opacity-90' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}>
            <Play size={14} strokeWidth={2.5} /> Play Mix
          </button>
        </div>
      </div>
      <style>{`@keyframes slideUp { from{transform:translateY(100%);opacity:0} to{transform:translateY(0);opacity:1} } .animate-slideUp{animation:slideUp 0.3s ease-out} .scrollbar-hide::-webkit-scrollbar{display:none} .scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}`}</style>
    </div>
  );
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// USER PROFILE PANEL
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•


const UpgradePrompt = ({ feature, onUpgradeMonthly, onUpgradeAnnual, onClose }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
      <div className="text-center mb-4">
        <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
          <Lock size={20} className="text-purple-400" />
        </div>
        <h3 className="text-white font-bold text-lg">Premium Feature</h3>
        <p className="text-white/40 text-sm mt-1">{feature} is available with Groovie Premium.</p>
      </div>
      <div className="space-y-2">
        <div className="flex gap-2">
          <button onClick={onUpgradeMonthly} className="flex-1 py-2.5 bg-purple-500 hover:bg-purple-600 text-white text-sm font-semibold rounded-xl transition">$4.99/mo</button>
          <button onClick={onUpgradeAnnual} className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white text-sm font-semibold rounded-xl transition">$49.99/yr<span className="block text-[9px] opacity-70">Save 17%</span></button>
        </div>
        <button onClick={onClose} className="w-full py-2 text-white/30 text-xs hover:text-white/50 transition">Not Now</button>
      </div>
    </div>
  </div>
);

const UserProfilePanel = ({ user, isPremium, favorites, recent, onClose, onTogglePremium, onSignOut, subStatus, handleUpgrade, handleManageSub }) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [prefs, setPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('groovie_prefs') || '{}'); } catch { return {}; }
  });
  const savePrefs = (update) => {
    const next = { ...prefs, ...update };
    setPrefs(next);
    localStorage.setItem('groovie_prefs', JSON.stringify(next));
  };
  const totalTracks = favorites.length + recent.length;
  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Jan 2026';
  const genreOptions = ['Gospel', 'Afrobeats', 'Pop', 'R&B', 'Jazz', 'Hip Hop', 'Latin', 'Electronic', 'Ambient', 'Christian Kids'];
  const selectedGenres = prefs.genres || [];
  const toggleGenre = (g) => {
    const next = selectedGenres.includes(g) ? selectedGenres.filter(x => x !== g) : [...selectedGenres, g];
    savePrefs({ genres: next });
  };
  const sections = [
    { key: 'overview', label: 'Overview' },
    { key: 'preferences', label: 'Music' },
    { key: 'playback', label: 'Playback' },
    { key: 'news', label: 'News' },
    { key: 'subscription', label: 'Plan' },
  ];
  const Toggle = ({ on, onTap }) => (
    <button onClick={onTap} className={"w-10 h-6 rounded-full transition-all relative " + (on ? "bg-purple-500" : "bg-white/10")}>
      <div className={"absolute top-1 w-4 h-4 rounded-full bg-white transition-all shadow " + (on ? "left-5" : "left-1")} />
    </button>
  );
  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[55]" onClick={onClose}>
      <div className="w-full max-w-md mx-auto h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 pb-32">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-white text-xl font-bold tracking-tight">Your Profile</h2>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white transition">
              <X size={16} strokeWidth={2} />
            </button>
          </div>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500/30 to-pink-500/30 border border-white/10 flex items-center justify-center">
              <span className="text-3xl font-bold text-white/70">{(user.full_name || user.email || '?')[0].toUpperCase()}</span>
            </div>
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg leading-tight">{user.full_name || 'Listener'}</h3>
              <p className="text-white/30 text-sm mt-0.5">{user.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className={"px-2.5 py-0.5 rounded-full text-[11px] font-semibold " + (isPremium ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-white/5 text-white/30 border border-white/10")}>
                  {isPremium ? '\u2605 Premium' : 'Free'}
                </span>
                <span className="text-white/20 text-[11px]">Since {memberSince}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="bg-white/[0.03] rounded-xl p-3.5 border border-white/5 text-center">
              <p className="text-xl font-bold text-white">{favorites.length}</p>
              <p className="text-white/25 text-[11px] mt-0.5">Favorites</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-3.5 border border-white/5 text-center">
              <p className="text-xl font-bold text-white">{recent.length}</p>
              <p className="text-white/25 text-[11px] mt-0.5">Recent</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-3.5 border border-white/5 text-center">
              <p className="text-xl font-bold text-white">{totalTracks}</p>
              <p className="text-white/25 text-[11px] mt-0.5">Total Plays</p>
            </div>
          </div>
          <div className="flex gap-1.5 mb-6 bg-white/[0.03] rounded-xl p-1 border border-white/5">
            {sections.map(s => (
              <button key={s.key} onClick={() => setActiveSection(s.key)}
                className={"flex-1 py-2 rounded-lg text-[11px] font-medium transition-all " + (activeSection === s.key ? "bg-white/10 text-white" : "text-white/30 hover:text-white/50")}>
                {s.label}
              </button>
            ))}
          </div>
          {activeSection === 'overview' && (
            <div className="space-y-4">
              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Favorite Stations</h4>
                </div>
                <div className="p-3">
                  {favorites.length === 0 ? (
                    <p className="text-white/20 text-sm p-2">No favorites yet. Tap the heart on any station!</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {favorites.slice(0, 8).map(slug => (
                        <span key={slug} className="px-3 py-1.5 bg-white/5 rounded-lg text-white/60 text-xs border border-white/5">
                          {slug.replace(/-/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Recently Played</h4>
                </div>
                <div className="p-3">
                  {recent.length === 0 ? (
                    <p className="text-white/20 text-sm p-2">Nothing played yet. Start listening!</p>
                  ) : (
                    <div className="space-y-2">
                      {recent.slice(0, 5).map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition">
                          <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                            <Music size={14} className="text-purple-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white/80 text-sm font-medium truncate">{item.name || (item.slug || '').replace(/-/g, ' ')}</p>
                            <p className="text-white/25 text-[11px]">{item.genre || 'Station'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {activeSection === 'preferences' && (
            <div className="space-y-4">
              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Favorite Genres</h4>
                  <p className="text-white/20 text-[11px] mt-1">Personalize your recommendations</p>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {genreOptions.map(g => (
                      <button key={g} onClick={() => toggleGenre(g)}
                        className={"px-3.5 py-1.5 rounded-full text-xs font-medium transition-all border " + (selectedGenres.includes(g) ? "bg-purple-500/20 text-purple-300 border-purple-500/30" : "bg-white/[0.03] text-white/30 border-white/5 hover:border-white/10")}>
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Content Settings</h4>
                </div>
                <div className="p-2">
                  {[{k:'explicit',l:'Allow Explicit Content',d:'Show content with explicit language'},{k:'discover',l:'Discovery Mode',d:'Mix in new stations you might like'}].map(opt => (
                    <div key={opt.k} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/[0.02] transition">
                      <div><p className="text-white/70 text-sm font-medium">{opt.l}</p><p className="text-white/20 text-[11px] mt-0.5">{opt.d}</p></div>
                      <Toggle on={!!prefs[opt.k]} onTap={() => savePrefs({ [opt.k]: !prefs[opt.k] })} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {activeSection === 'playback' && (
            <div className="space-y-4">
              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Audio Quality</h4>
                </div>
                <div className="p-3">
                  {['Auto','High (320kbps)','Medium (192kbps)','Low (96kbps)'].map((q, i) => {
                    const val = ['auto','high','medium','low'][i];
                    return (
                      <button key={val} onClick={() => savePrefs({ quality: val })}
                        className={"w-full flex items-center justify-between p-3 rounded-xl transition mb-1 " + ((prefs.quality || 'auto') === val ? "bg-purple-500/10 border border-purple-500/20" : "hover:bg-white/[0.03] border border-transparent")}>
                        <span className={"text-sm " + ((prefs.quality || 'auto') === val ? "text-purple-300 font-medium" : "text-white/40")}>{q}</span>
                        {(prefs.quality || 'auto') === val && <div className="w-2 h-2 rounded-full bg-purple-400" />}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Playback Settings</h4>
                </div>
                <div className="p-2">
                  {[{k:'autoplay',l:'Autoplay Next',d:'Automatically play next track'},{k:'crossfade',l:'Crossfade',d:'Smooth transitions between tracks'},{k:'gapless',l:'Gapless Playback',d:'No silence between songs'}].map(opt => (
                    <div key={opt.k} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/[0.02] transition">
                      <div><p className="text-white/70 text-sm font-medium">{opt.l}</p><p className="text-white/20 text-[11px] mt-0.5">{opt.d}</p></div>
                      <Toggle on={!!prefs[opt.k]} onTap={() => savePrefs({ [opt.k]: !prefs[opt.k] })} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {activeSection === 'news' && (
            <div className="space-y-4">
              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">News Briefings</h4>
                  <p className="text-white/20 text-[11px] mt-1">Get personalized news updates while you listen</p>
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between p-3 rounded-xl hover:bg-white/[0.02] transition">
                    <div><p className="text-white/70 text-sm font-medium">Enable News</p><p className="text-white/20 text-[11px] mt-0.5">Auto-play news at your set frequency</p></div>
                    <Toggle on={!!prefs.news} onTap={() => savePrefs({ news: !prefs.news })} />
                  </div>
                </div>
              </div>

              {prefs.news && (<>
              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Your Location</h4>
                  <p className="text-white/20 text-[11px] mt-1">For local news coverage</p>
                </div>
                <div className="p-4 space-y-3">
                  <input value={prefs.news_city || ''} onChange={e => savePrefs({ news_city: e.target.value })}
                    placeholder="City (e.g. New York)"
                    className="w-full px-3 py-2.5 bg-white/[0.05] border border-white/10 rounded-xl text-white text-sm placeholder-white/20 outline-none focus:border-purple-500/40" />
                  <div className="flex gap-2">
                    <input value={prefs.news_state || ''} onChange={e => savePrefs({ news_state: e.target.value })}
                      placeholder="State"
                      className="flex-1 px-3 py-2.5 bg-white/[0.05] border border-white/10 rounded-xl text-white text-sm placeholder-white/20 outline-none focus:border-purple-500/40" />
                    <input value={prefs.news_country || 'us'} onChange={e => savePrefs({ news_country: e.target.value })}
                      placeholder="Country"
                      className="w-20 px-3 py-2.5 bg-white/[0.05] border border-white/10 rounded-xl text-white text-sm placeholder-white/20 outline-none focus:border-purple-500/40" />
                  </div>
                </div>
              </div>

              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Interests</h4>
                  <p className="text-white/20 text-[11px] mt-1">Select topics for your news updates</p>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {NEWS_INTERESTS.map(interest => {
                      const sel = (prefs.news_interests || []).includes(interest);
                      return (
                        <button key={interest} onClick={() => {
                          const cur = prefs.news_interests || [];
                          const next = sel ? cur.filter(x => x !== interest) : [...cur, interest].slice(0, 5);
                          savePrefs({ news_interests: next });
                        }}
                          className={"px-3 py-1.5 rounded-full text-xs font-medium transition-all border capitalize " + (sel ? "bg-purple-500/20 text-purple-300 border-purple-500/30" : "bg-white/[0.03] text-white/30 border-white/5 hover:border-white/10")}>
                          {interest}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-white/15 text-[10px] mt-2">Select up to 5 topics</p>
                </div>
              </div>

              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Frequency & Style</h4>
                </div>
                <div className="p-3 space-y-1">
                  {NEWS_FREQUENCIES.map(freq => (
                    <button key={freq.value} onClick={() => savePrefs({ news_frequency: freq.value })}
                      className={"w-full flex items-center justify-between p-3 rounded-xl transition mb-1 " + ((prefs.news_frequency || 120) === freq.value ? "bg-purple-500/10 border border-purple-500/20" : "hover:bg-white/[0.03] border border-transparent")}>
                      <span className={"text-sm " + ((prefs.news_frequency || 120) === freq.value ? "text-purple-300 font-medium" : "text-white/40")}>{freq.label}</span>
                      {(prefs.news_frequency || 120) === freq.value && <div className="w-2 h-2 rounded-full bg-purple-400" />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">News Anchor Voice</h4>
                </div>
                <div className="p-3">
                  <div className="grid grid-cols-2 gap-1.5">
                    {NEWS_VOICES.map(v => (
                      <button key={v.id} onClick={() => savePrefs({ news_voice: v.id })}
                        className={"p-2.5 rounded-xl text-xs font-medium transition-all border " + ((prefs.news_voice || 'female_us') === v.id ? "bg-purple-500/10 text-purple-300 border-purple-500/20" : "bg-white/[0.02] text-white/30 border-white/5 hover:border-white/10")}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">News Length</h4>
                </div>
                <div className="p-3 space-y-1">
                  {[{v:'standard',l:'Standard',d:'2-3 min, headlines + context'},{v:'deep',l:'Deep Dive',d:'4-5 min, full roundup'}].map(opt => (
                    <button key={opt.v} onClick={() => savePrefs({ news_length: opt.v })}
                      className={"w-full flex items-center justify-between p-3 rounded-xl transition mb-1 " + ((prefs.news_length || 'standard') === opt.v ? "bg-purple-500/10 border border-purple-500/20" : "hover:bg-white/[0.03] border border-transparent")}>
                      <div className="text-left">
                        <span className={"text-sm block " + ((prefs.news_length || 'standard') === opt.v ? "text-purple-300 font-medium" : "text-white/40")}>{opt.l}</span>
                        <span className="text-[10px] text-white/20">{opt.d}</span>
                      </div>
                      {(prefs.news_length || 'standard') === opt.v && <div className="w-2 h-2 rounded-full bg-purple-400" />}
                    </button>
                  ))}
                </div>
              </div>
              </>)}
            </div>
          )}

          {activeSection === 'subscription' && (
            <div className="space-y-4">
              {/* Trial Banner */}
              {isPremium && !(subStatus?.status === 'active' && subStatus?.plan !== 'trial') && (
                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={16} className="text-purple-400" />
                    <h4 className="text-white font-semibold text-sm">Free Trial Active</h4>
                  </div>
                  <p className="text-white/60 text-xs mb-3">You have full Premium access during your trial period.</p>
                  <div className="flex gap-2">
                    <button onClick={() => handleUpgrade('monthly')} className="flex-1 py-2 bg-purple-500 hover:bg-purple-600 text-white text-xs font-semibold rounded-lg transition">$4.99/mo</button>
                    <button onClick={() => handleUpgrade('annual')} className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white text-xs font-semibold rounded-lg transition">$49.99/yr (Save 17%)</button>
                  </div>
                </div>
              )}

              {/* Active Paid Sub */}
              {subStatus?.status === 'active' && subStatus?.plan !== 'trial' && (
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/20 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap size={16} className="text-amber-400" />
                    <h4 className="text-white font-semibold text-sm">Premium Active</h4>
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] font-semibold rounded-full border border-amber-500/30">{subStatus.plan === 'annual' ? 'Annual' : 'Monthly'}</span>
                  </div>
                  <p className="text-white/40 text-xs mb-3">All Premium features unlocked.</p>
                  <button onClick={handleManageSub} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/60 text-xs font-medium rounded-lg border border-white/10 transition">Manage Subscription</button>
                </div>
              )}

              {/* Free / Expired */}
              {SUBSCRIPTIONS_ENABLED && !isPremium && (
                <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4">
                  <h4 className="text-white font-semibold text-sm mb-3">Upgrade to Premium</h4>
                  <div className="space-y-2 mb-4">
                    {['All 8 stations + all 6 shows', 'Mix Play unlocked', 'News: any frequency + deep dive', 'High quality audio (320kbps)', 'Unlimited skips', 'All anchor voices'].map(f => (
                      <div key={f} className="flex items-center gap-2">
                        <Check size={14} className="text-purple-400" />
                        <span className="text-white/50 text-xs">{f}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleUpgrade('monthly')} className="flex-1 py-2.5 bg-purple-500 hover:bg-purple-600 text-white text-xs font-semibold rounded-lg transition">$4.99/mo</button>
                    <button onClick={() => handleUpgrade('annual')} className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white text-xs font-semibold rounded-lg transition">
                      $49.99/yr<span className="block text-[9px] opacity-70">Save 17%</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Free vs Premium comparison */}
              <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Free vs Premium</h4>
                </div>
                <div className="divide-y divide-white/5">
                  {[
                    { feature: 'Music Stations', free: '8 stations', premium: '8 stations' },
                    { feature: 'Show Channels', free: '3 channels', premium: 'All 6 channels' },
                    { feature: 'Mix Play', free: 'Locked', premium: 'Unlimited' },
                    { feature: 'News Updates', free: '1x daily', premium: 'Any frequency + deep' },
                    { feature: 'Audio Quality', free: '192 kbps', premium: '320 kbps' },
                    { feature: 'Skips', free: '6 per hour', premium: 'Unlimited' },
                    { feature: 'Voices', free: '2 voices', premium: 'All 8 voices' },
                  ].map(row => (
                    <div key={row.feature} className="flex items-center px-4 py-2.5">
                      <span className="flex-1 text-white/40 text-xs">{row.feature}</span>
                      <span className="w-20 text-center text-white/25 text-[10px]">{row.free}</span>
                      <span className="w-20 text-center text-purple-300 text-[10px] font-medium">{row.premium}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Account */}
              <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4">
                <h4 className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-3">Account</h4>
                <button onClick={onSignOut} className="w-full p-3 text-red-400/60 hover:text-red-400 hover:bg-red-500/5 text-sm font-medium transition rounded-xl border border-white/5">Sign Out</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

//
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// MAIN APP
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

export default function App() {
  // Auth & user
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(null);
  const [trialDays, setTrialDays] = useState(null);
  const [isLogin, setIsLogin] = useState(true);
  const [authData, setAuthData] = useState({ email: '', password: '', full_name: '' });
  const [showProfile, setShowProfile] = useState(false);

  // Playback
  const [playingStation, setPlayingStation] = useState(null);
  const [sharedNudge, setSharedNudge] = useState(null);   // Q4: shared-station signup nudge {name}
  const [isPlayerMinimized, setIsPlayerMinimized] = useState(false);
  const [mixPlaylist, setMixPlaylist] = useState(null);
  const [playerFallback, setPlayerFallback] = useState(false);   // L2: RadioPlayer is in R2 degraded mode -> show on mini-bar

  // L2 build-staleness auto-updater: reload to the current build at a natural boundary (silent, no prompt).
  // On open: if the running bundle is stale, reload immediately (fresh open, sub-second). On idle-revisit
  // (tab refocused while nothing is playing): reload. Track-change boundary is handled in the player's playNext.
  const playingStationRef = useRef(null);
  useEffect(() => { playingStationRef.current = playingStation; }, [playingStation]);
  useEffect(() => {
    checkForUpdate().then(p => { if (p) reloadForUpdate('app-open'); });
    const onVis = () => { if (!document.hidden) checkForUpdate().then(p => { if (p && !playingStationRef.current) reloadForUpdate('idle-revisit'); }); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Tabs & UI
  // S1: land on Discover (business search + browse) by default, not an empty Library.
  // A one-time effect below retargets to My Library for signed-in users who already have a library,
  // and never overrides a shared /listen/{id} deep-link arrival.
  // Demo-fix: capture the deep-link id ONCE at mount — BEFORE the deep-link effect replaceState's
  // the URL to /listen — so all tab logic is short-circuited by deep-link presence deterministically
  // (the old guard re-read window.location.pathname and lost the race on mobile).
  const [initialDeepLinkId] = useState(() => {
    try { const m = window.location.pathname.match(/^\/listen\/([A-Za-z0-9_-]+)\/?$/); return m ? m[1] : null; }
    catch { return null; }
  });
  const deepLinkRetriesRef = useRef(0);
  // Arriving via a deep-link NEVER lands on the shimmering Discover browse; plain guests still default to Discover.
  const [activeTab, setActiveTab] = useState(initialDeepLinkId ? 'library' : 'discover');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Mix mode
  const [isMixMode, setIsMixMode] = useState(false);
  const [mixSelection, setMixSelection] = useState([]);

  // Added business channels (library)
  const [addedBusinessIds, setAddedBusinessIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('groovie_added_biz') || '[]'); } catch { return []; }
  });
  const { favorites, toggleFavorite, isFavorite } = useFavorites();
  const { recent, addRecent } = useRecentlyPlayed();
  const { channels: businessChannels, loading: businessLoading, error: businessError, retry: retryBusiness } = useBusinessChannels();
  const follows = useConsumerFollows();  // 2.5.3a: shared whole-brand follow state (pills + Following list)

  // Persist added business IDs
  useEffect(() => { localStorage.setItem('groovie_added_biz', JSON.stringify(addedBusinessIds)); }, [addedBusinessIds]);

  // S1: default-tab decision (runs once, after channels resolve). Guests / empty library stay on Discover;
  // signed-in users with a library go to My Library; a /listen/{id} deep-link is never overridden.
  const tabDefaultedRef = useRef(false);
  useEffect(() => {
    if (tabDefaultedRef.current) return;
    // Deep-link presence short-circuits ALL default-tab logic — evaluated FIRST, before the loading gate,
    // so an arriving /listen/{id} can never be flipped to Discover (race-free on mobile).
    if (initialDeepLinkId) { tabDefaultedRef.current = true; return; }
    if (businessLoading) return;   // plain entry: wait for channels/library to settle before deciding
    if (user && addedBusinessIds.length > 0) setActiveTab('library');
    tabDefaultedRef.current = true;
  }, [user, businessLoading, addedBusinessIds, initialDeepLinkId]);

  useEffect(() => {
    const token = localStorage.getItem('groovie_token');
    const userData = localStorage.getItem('groovie_user');
    if (token && userData) {
      try { setUser(JSON.parse(userData)); setIsPremium(JSON.parse(userData).is_premium || false); } catch (e) {}
    }
  }, []);

  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      const endpoint = isLogin ? '/login' : '/register';
      const body = isLogin
        ? { email: authData.email, password: authData.password }
        : { email: authData.email, password: authData.password, full_name: authData.full_name };
      const res = await fetch(`${AUTH_API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) {
          const ra = data.retry_after || res.headers.get('Retry-After');
          setAuthError(data.detail || (ra ? `Too many attempts. Try again in ${ra}s.` : 'Too many attempts. Please wait a moment.'));
        } else {
          setAuthError(data.detail || 'Authentication failed');
        }
        setAuthLoading(false);
        return;
      }
      localStorage.setItem('groovie_token', data.access_token);
      localStorage.setItem('groovie_user', JSON.stringify(data.user));
      setUser(data.user);
      setIsPremium(data.user.is_premium || false);
      setShowAuth(false);
      // Check subscription status
      fetch(`${SUB_API_URL}/subscription/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.user.email }),
      }).then(r => r.json()).then(subData => {
        setSubStatus(subData);
        setIsPremium(subData.is_premium || false);
        if (subData.status === 'trial') setTrialDays(subData.trial_days || subData.days_remaining || 0);
      }).catch(() => {});
    } catch (e) {
      setAuthError('Connection error. Please try again.');
      console.warn('Auth error:', e);
    }
    setAuthLoading(false);
  };

  const handleUpgrade = async (plan = 'monthly') => {
    const email = user?.email;
    if (!email) { setShowAuth(true); return; }
    try {
      const res = await fetch(`${SUB_API_URL}/subscribe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, plan }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { console.warn('Upgrade error:', e); }
  };

  const handleManageSub = async () => {
    const email = user?.email;
    if (!email) return;
    try {
      const res = await fetch(`${SUB_API_URL}/subscription/portal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.portal_url) window.location.href = data.portal_url;
    } catch (e) { console.warn('Portal error:', e); }
  };

  const playStation = (station, isChannel = false) => {
    // Gate locked shows for free users
    if (SUBSCRIPTIONS_ENABLED && !isPremium && isChannel && station.slug && !FREE_SHOWS.includes(station.slug)) {
      setShowUpgradePrompt(station.name || 'This show');
      return;
    }
    // Toggle: clicking the station that's already playing stops it
    if (playingStation && playingStation.slug === station.slug) {
      stopPlayback();
      return;
    }
    const stationData = { ...station, isGroovieChannel: isChannel };
    setPlayingStation(stationData);
    setIsPlayerMinimized(false);
    setMixPlaylist(null);
    addRecent(stationData);
  };

  const playBusinessChannel = (channel) => {
    // Toggle: clicking the business channel that's already playing stops it
    if (playingStation && playingStation.slug === channel.channel_slug) {
      stopPlayback();
      return;
    }
    const stationData = {
      ...channel, slug: channel.channel_slug, name: channel.channel_name,
      color: MOOD_COLORS[channel.mood] || 'slate', isBusinessChannel: true, isGroovieChannel: false,
    };
    setPlayingStation(stationData);
    setIsPlayerMinimized(false);
    setMixPlaylist(null);
  };

  // Mix mode
  const toggleMixItem = (item) => {
    const slug = item.slug || item.channel_slug;
    const exists = mixSelection.find(s => (s.slug || s.channel_slug) === slug);
    if (exists) setMixSelection(mixSelection.filter(s => (s.slug || s.channel_slug) !== slug));
    else setMixSelection([...mixSelection, item]);
  };

  const isMixSelected = (item) => {
    const slug = item.slug || item.channel_slug;
    return mixSelection.some(s => (s.slug || s.channel_slug) === slug);
  };

  const handleMixAdd = (item) => {
    if (!isMixMode) setIsMixMode(true);
    if (!isMixSelected(item)) setMixSelection(prev => [...prev, item]);
  };

  const playMix = () => {
    if (mixSelection.length === 0) return;
    if (SUBSCRIPTIONS_ENABLED && !isPremium) { setShowUpgradePrompt('Mix Play'); return; }
    const pl = generateMixPlaylist(mixSelection.map(s => ({
      slug: s.slug || s.channel_slug,
      isGroovieChannel: s.isGroovieChannel || false,
      isBusinessChannel: s.isBusinessChannel || false,
    })), isPremium);
    const firstItem = mixSelection[0];
    setMixPlaylist(pl);
    setPlayingStation({
      slug: 'groovie-mix', name: 'Mix Play', color: firstItem.color || MOOD_COLORS[firstItem.mood] || 'purple',
      isGroovieChannel: false, isBusinessChannel: false, isMix: true, items: mixSelection,
    });
    setIsPlayerMinimized(false);
    setIsMixMode(false);
  };

  // Re-check subscription on mount and after Stripe redirect
  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('groovie_user') || 'null');
    if (!u?.email) return;
    const params = new URLSearchParams(window.location.search);
    const isStripeReturn = params.get('subscription') === 'success';
    // Always check on mount, with slight delay after Stripe return for webhook processing
    const delay = isStripeReturn ? 3000 : 0;
    const timer = setTimeout(() => {
      fetch(`${SUB_API_URL}/subscription/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: u.email }),
      }).then(r => r.json()).then(subData => {
        setSubStatus(subData);
        setIsPremium(subData.is_premium || false);
        if (subData.status === 'trial') setTrialDays(subData.trial_days || 0);
        // Update localStorage
        const stored = JSON.parse(localStorage.getItem('groovie_user') || '{}');
        stored.is_premium = subData.is_premium || false;
        localStorage.setItem('groovie_user', JSON.stringify(stored));
      }).catch(() => {});
      // Clean URL params
      if (isStripeReturn) window.history.replaceState({}, '', window.location.pathname);
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  // Q4/Q5: share links + QR (groovie.live/listen/{channelId}) open the app with the shared
  // business station primary — guest plays immediately (no gate), a signup nudge overlays.
  // Routing/param only: resolve the id against the existing public channels list, then reuse
  // the existing playBusinessChannel. No new auth, no playback internals.
  // (Q5 retired the old /scan/{locationId} handler — nothing generated those URLs and it gated guests.
  //  Old /channel/{id} QRs 301 to /listen/{id} at the edge; the app is served at /listen.)
  const sharedDeepLinkDone = useRef(false);
  useEffect(() => {
    if (sharedDeepLinkDone.current || !initialDeepLinkId || businessLoading) return;
    const ch = businessChannels.find(c => String(c.id) === String(initialDeepLinkId));
    if (ch) {
      sharedDeepLinkDone.current = true;
      playBusinessChannel(ch);               // guest autoplay via existing station-selection
      if (!_IS_MOMENT) setSharedNudge({ name: ch.channel_name || 'this station' });  // Moments: no signup/follow banner
      window.history.replaceState({}, '', '/listen');   // app home is /listen post-Q5
    } else if (businessError && deepLinkRetriesRef.current < 5) {
      // The channel list failed/timed out and the shared station isn't resolvable yet — retry the fetch
      // (bounded) so a transient stall doesn't permanently break the demo QR. The deep-link stays pending.
      deepLinkRetriesRef.current += 1;
      setTimeout(() => { try { retryBusiness(); } catch {} }, 1500);
    }
    // else (list loaded, id genuinely absent) -> leave app at home; no broken state.
  }, [businessLoading, businessChannels, businessError, initialDeepLinkId]);

  const addBusinessToLibrary = (id) => {
    if (!addedBusinessIds.includes(id)) setAddedBusinessIds([...addedBusinessIds, id]);
  };

  const removeBusinessFromLibrary = (id) => {
    setAddedBusinessIds(addedBusinessIds.filter(bid => bid !== id));
  };

  const stopPlayback = () => { setPlayingStation(null); setIsPlayerMinimized(false); setMixPlaylist(null); };

  // Library content
  const myBusinessChannels = businessChannels.filter(ch => addedBusinessIds.includes(ch.id));
  const allMyContent = [
    ...MUSIC_STATIONS.map(s => ({ ...s, category: 'station' })),
    ...GROOVIE_CHANNELS.map(s => ({ ...s, category: 'show' })),
    ...myBusinessChannels.map(s => ({ ...s, category: 'business', slug: s.channel_slug, name: s.channel_name, color: MOOD_COLORS[s.mood] || 'slate' })),
  ];
  const filteredContent = filterType === 'all' ? allMyContent
    : filterType === 'stations' ? allMyContent.filter(i => i.category === 'station')
    : filterType === 'business' ? allMyContent.filter(i => i.category === 'business')
    : allMyContent.filter(i => i.category === 'show');

  // Discover search
  const filteredBusiness = businessChannels.filter(ch => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (ch.channel_name || '').toLowerCase().includes(q) || (ch.company_name || '').toLowerCase().includes(q) || (ch.mood || '').toLowerCase().includes(q);
  });

  const favoriteItems = [...GROOVIE_CHANNELS, ...MUSIC_STATIONS].filter(s => favorites.includes(s.slug));

  // Auth Screen
  if (showAuth) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="text-white text-3xl font-bold tracking-tight">groovie</h1>
            <p className="text-white/40 mt-2">Autonomous Radio</p>
          </div>
          <form onSubmit={handleAuth} className="space-y-4">
            {!isLogin && (
              <input type="text" placeholder="Full Name" value={authData.full_name}
                onChange={(e) => setAuthData({...authData, full_name: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition" required />
            )}
            <input type="email" placeholder="Email" value={authData.email}
              onChange={(e) => setAuthData({...authData, email: e.target.value})}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition" required />
            <input type="password" placeholder="Password" value={authData.password}
              onChange={(e) => setAuthData({...authData, password: e.target.value})}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition" required />
            <button type="submit" disabled={authLoading}
              className={`w-full py-3 rounded-xl font-semibold transition ${authLoading ? 'bg-white/60 text-black/60 cursor-not-allowed' : 'bg-white text-black hover:bg-white/90'}`}>
              {authLoading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
            {authError && (
              <p className="text-red-400 text-sm text-center" role="alert">{authError}</p>
            )}
          </form>
          <p className="text-center mt-6 text-white/40 text-sm">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button onClick={() => setIsLogin(!isLogin)} className="text-white hover:underline">{isLogin ? 'Sign Up' : 'Sign In'}</button>
          </p>
          <button onClick={() => setShowAuth(false)} className="w-full mt-4 text-white/30 text-sm hover:text-white/50 transition">Continue as guest</button>
        </div>
      </div>
    );
  }

  // â•â•â•â•â• RENDER â•â•â•â•â•
  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Q4: shared-station signup nudge — non-modal, overlays without blocking playback, dismissible */}
      {sharedNudge && (
        <div className="fixed top-0 left-0 right-0 z-[70] flex justify-center px-4 pt-3 pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-3 bg-violet-600/95 backdrop-blur text-white rounded-2xl shadow-xl px-4 py-3 max-w-md w-full">
            <span className="text-sm flex-1 leading-snug">Create a free account to follow <b>{sharedNudge.name}</b>.</span>
            <button onClick={() => { setIsLogin(false); setShowAuth(true); }}
              className="text-sm font-semibold bg-white text-violet-700 rounded-full px-3 py-1.5 whitespace-nowrap hover:bg-white/90 transition">Sign up</button>
            <button onClick={() => setSharedNudge(null)} aria-label="Dismiss"
              className="text-white/80 hover:text-white text-xl leading-none px-1">×</button>
          </div>
        </div>
      )}
      {/* RadioPlayer â€” single instance, visibility controlled by CSS so audio persists */}
      {playingStation && (
        <div className={isPlayerMinimized ? 'fixed -left-[9999px] -top-[9999px] w-0 h-0 overflow-hidden' : ''}>
          <RadioPlayer
            station={playingStation}
            isGroovieChannel={playingStation.isGroovieChannel}
            isPremium={isPremium}
            onClose={stopPlayback}
            onMinimize={() => setIsPlayerMinimized(true)}
            onToggleFavorite={toggleFavorite}
            isFavorite={isFavorite(playingStation.slug)}
            prebuiltPlaylist={mixPlaylist}
            followFeed={follows.feed}
            onFallbackChange={setPlayerFallback}
          />
        </div>
      )}

      {/* Now Playing Bar â€” visible when player is minimized */}
      {playingStation && isPlayerMinimized && (
        <NowPlayingBar station={playingStation} mixItems={playingStation.isMix ? playingStation.items : null}
          reconnecting={playerFallback}
          onExpand={() => setIsPlayerMinimized(false)} onStop={stopPlayback} />
      )}

      {/* Floating Mix Tray â€” visible during mix selection (positioned above NowPlayingBar if playing) */}
      {isMixMode && <FloatingMixTray items={mixSelection} onClear={() => { setMixSelection([]); setIsMixMode(false); }} onPlay={playMix} isAbovePlayer={!!playingStation && isPlayerMinimized} onRemoveItem={(item) => { const slug = item.slug || item.channel_slug; setMixSelection(prev => prev.filter(s => (s.slug || s.channel_slug) !== slug)); }} />}

      {showUpgradePrompt && <UpgradePrompt feature={showUpgradePrompt} onUpgradeMonthly={() => { setShowUpgradePrompt(null); handleUpgrade('monthly'); }} onUpgradeAnnual={() => { setShowUpgradePrompt(null); handleUpgrade('annual'); }} onClose={() => setShowUpgradePrompt(null)} />}
      {/* Profile Panel */}
      {showProfile && user && (
        <UserProfilePanel user={user} isPremium={isPremium} favorites={favorites} recent={recent} subStatus={subStatus} handleUpgrade={handleUpgrade} handleManageSub={handleManageSub}
          onClose={() => setShowProfile(false)} onTogglePremium={() => setIsPremium(!isPremium)}
          onSignOut={() => { localStorage.removeItem('groovie_token'); localStorage.removeItem('groovie_user'); setUser(null); setIsPremium(false); setSubStatus(null); setTrialDays(null); setShowProfile(false); }} />
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-neutral-950/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <a href="/" aria-label="Groovie home" className="flex items-center gap-3 hover:opacity-80 transition">
            <h1 className="text-2xl font-bold tracking-tight">groovie</h1>
            <span className="text-[10px] text-white/25 uppercase tracking-[0.2em] font-medium hidden sm:block">Autonomous Radio</span>
          </a>
          <div className="flex items-center gap-3">
            {!_IS_MOMENT && (
            <a href="https://business.groovie.live" target="_blank" rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-1 text-white/30 hover:text-white/60 text-xs transition">
              For Business <ExternalLink size={12} strokeWidth={1.5} />
            </a>
            )}
            {user ? (
              <button onClick={() => setShowProfile(true)}
                className="w-9 h-9 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-white/20 transition-all">
                <span className="text-sm font-semibold">{(user.full_name || user.email || '?')[0].toUpperCase()}</span>
              </button>
            ) : (
              <button onClick={() => setShowAuth(true)} className="px-5 py-2 bg-white text-black rounded-full text-sm font-semibold hover:bg-white/90 transition">Sign In</button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        {/* Hero - only for guests (hidden in Moments/event context) */}
        {!user && !_IS_MOMENT && (
          <section className="text-center py-20 mb-10">
            <p className="text-white/30 uppercase tracking-[0.25em] text-xs font-medium mb-6">Autonomous Radio Platform</p>
            <h2 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6 leading-[0.95]">
              Radio, <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white/80 to-white/40">reimagined</span>
            </h2>
            <p className="text-white/40 text-lg max-w-lg mx-auto mb-10 leading-relaxed">Autonomous channels, curated stations, and original shows. Always on. Always evolving.</p>
            <button onClick={() => setShowAuth(true)}
              className="px-8 py-3.5 bg-white text-black rounded-full font-semibold hover:bg-white/90 transition-all hover:shadow-xl hover:shadow-white/10">Start Listening</button>
          </section>
        )}

        {/* Tab Bar (hidden in Moments/event context) */}
        {!_IS_MOMENT && (
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-1 bg-white/[0.04] rounded-xl p-1">
            {[{ key: 'library', label: 'My Library', icon: LayoutGrid }, { key: 'discover', label: 'Discover', icon: Search }].map(tab => (
              <button key={tab.key} onClick={() => { setActiveTab(tab.key); setSearchQuery(''); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.key ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}>
                <tab.icon size={16} strokeWidth={1.5} /> {tab.label}
              </button>
            ))}
          </div>
          {/* Mix Play toggle */}
          <button onClick={() => { if (SUBSCRIPTIONS_ENABLED && !isPremium) { setShowUpgradePrompt('Mix Play'); return; } setIsMixMode(!isMixMode); if (isMixMode) setMixSelection([]); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${isMixMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-white/[0.04] text-white/40 hover:text-white/60 border border-transparent'}`}>
            <Zap size={16} strokeWidth={1.5} /> {isMixMode ? 'Cancel Mix' : 'Mix Play'}
            {isMixMode && mixSelection.length > 0 && <span className="w-5 h-5 rounded-full bg-green-500 text-black text-[10px] font-bold flex items-center justify-center">{mixSelection.length}</span>}
          </button>
        </div>
        )}

        {/* â•â•â•â•â•â•â•â•â• LIBRARY TAB â•â•â•â•â•â•â•â•â• */}
        {!_IS_MOMENT && activeTab === 'library' && (
          <>
            {/* Filter chips */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {[{ key: 'all', label: 'All' }, { key: 'stations', label: 'Stations' }, { key: 'business', label: 'Business' }, { key: 'shows', label: 'Shows' }].map(f => (
                <button key={f.key} onClick={() => setFilterType(f.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${filterType === f.key ? 'bg-white text-black' : 'bg-white/[0.06] text-white/50 hover:text-white/70 hover:bg-white/10'}`}>
                  {f.label}
                </button>
              ))}
            </div>

            {/* Recently Played */}
            {recent.length > 0 && filterType === 'all' && (
              <section className="mb-12">
                <div className="flex items-center gap-2.5 mb-5">
                  <Clock size={18} strokeWidth={1.5} className="text-white/40" />
                  <h2 className="text-lg font-semibold tracking-tight">Recently Played</h2>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-4 -mx-6 px-6 scrollbar-hide">
                  {recent.slice(0, 6).map(item => {
                    const Icon = CHANNEL_ICONS[item.slug] || STATION_ICONS[item.slug] || Radio;
                    return (
                      <button key={item.slug} onClick={() => playStation(item, item.isGroovieChannel)} className="flex-shrink-0 w-36 text-left group">
                        <div className={`aspect-square rounded-2xl bg-gradient-to-br ${c(item.color).grad} mb-2 flex items-center justify-center group-hover:scale-[1.03] transition-all duration-300 shadow-lg text-white`}>
                          <Icon size={24} strokeWidth={1.5} />
                        </div>
                        <p className="text-white text-sm font-medium truncate">{item.name}</p>
                        <p className="text-white/30 text-xs">{item.isGroovieChannel ? 'Show' : 'Station'}</p>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Favorites */}
            {favoriteItems.length > 0 && filterType === 'all' && (
              <section className="mb-12">
                <div className="flex items-center gap-2.5 mb-5">
                  <Heart size={18} strokeWidth={1.5} className="text-red-500" fill="currentColor" />
                  <h2 className="text-lg font-semibold tracking-tight">Your Favorites</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {favoriteItems.map(item => {
                    const Icon = CHANNEL_ICONS[item.slug] || STATION_ICONS[item.slug] || Radio;
                    return (
                      <button key={item.slug} onClick={() => playStation(item, item.isGroovieChannel)} className="text-left group">
                        <div className={`aspect-square rounded-2xl bg-gradient-to-br ${c(item.color).grad} mb-2 flex items-center justify-center relative group-hover:scale-[1.03] transition-all duration-300 shadow-lg text-white`}>
                          <Icon size={24} strokeWidth={1.5} />
                          <div className="absolute top-2 right-2 text-red-500"><Heart size={14} strokeWidth={1.5} fill="currentColor" /></div>
                        </div>
                        <p className="text-white text-sm font-medium truncate">{item.name}</p>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Unified Grid: filtered content */}
            {(filterType === 'all' || filterType === 'shows') && (
              <section className="mb-14">
                {filterType === 'all' && <div className="mb-6"><h2 className="text-xl font-semibold tracking-tight">Original Shows</h2><p className="text-white/30 text-sm mt-1">Autonomous content, real voices</p></div>}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(filterType === 'shows' ? filteredContent : GROOVIE_CHANNELS).filter(ch => ch.isGroovieChannel || ch.category === 'show').map(ch => {
                    const channelData = ch.category === 'show' ? GROOVIE_CHANNELS.find(g => g.slug === ch.slug) || ch : ch;
                    return <ChannelCard key={channelData.slug} channel={channelData} onPlay={playStation} isFavorite={isFavorite(channelData.slug)}
                      onToggleFavorite={toggleFavorite} isMixMode={isMixMode} isSelected={isMixSelected(channelData)} onToggleMix={toggleMixItem} />;
                  })}
                </div>
              </section>
            )}

            {(filterType === 'all' || filterType === 'stations') && (
              <section className="mb-14">
                {filterType === 'all' && <div className="mb-6"><h2 className="text-xl font-semibold tracking-tight">Live Stations</h2><p className="text-white/30 text-sm mt-1">Curated music, 24/7</p></div>}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                  {(filterType === 'stations' ? filteredContent : MUSIC_STATIONS).filter(s => !s.isGroovieChannel && s.category !== 'business' && s.category !== 'show' || !s.category).map(st => {
                    const stationData = st.category === 'station' ? MUSIC_STATIONS.find(m => m.slug === st.slug) || st : st;
                    return <StationCard key={stationData.slug} station={stationData} onPlay={playStation} isFavorite={isFavorite(stationData.slug)}
                      onToggleFavorite={toggleFavorite} isMixMode={isMixMode} isSelected={isMixSelected(stationData)} onToggleMix={toggleMixItem} />;
                  })}
                </div>
              </section>
            )}

            {(filterType === 'all' || filterType === 'business') && (
              <section className="mb-14">
                {filterType === 'all' && (
                  <div className="flex items-center justify-between mb-6">
                    <div><h2 className="text-xl font-semibold tracking-tight">Business Channels</h2><p className="text-white/30 text-sm mt-1">Live radio from businesses worldwide</p></div>
                    <a href="https://business.groovie.live" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-white/30 hover:text-white/60 text-sm transition">Create Your Channel <ExternalLink size={12} strokeWidth={1.5} /></a>
                  </div>
                )}
                {businessLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="bg-white/[0.03] rounded-2xl p-4 animate-pulse border border-white/[0.04]">
                        <div className="flex items-start gap-3"><div className="w-11 h-11 bg-white/[0.06] rounded-xl" /><div className="flex-1"><div className="h-4 bg-white/[0.06] rounded w-3/4 mb-2" /><div className="h-3 bg-white/[0.06] rounded w-1/2" /></div></div>
                      </div>
                    ))}
                  </div>
                ) : myBusinessChannels.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {myBusinessChannels.map(channel => (
                      <BusinessChannelCard key={channel.id} channel={channel} onPlay={playBusinessChannel}
                        isMixMode={isMixMode} isSelected={isMixSelected(channel)} onToggleMix={toggleMixItem}
                        showMixBtn={true} onMixAdd={handleMixAdd}
                        isFollowing={follows.followedIds.has(channel.company_id)} onToggleFollow={follows.toggle} followBusy={follows.busy === channel.company_id}
                        onRemove={addedBusinessIds.includes(channel.id) ? removeBusinessFromLibrary : null} />
                    ))}
                  </div>
                ) : (
                  <div className="bg-white/[0.03] rounded-2xl p-10 text-center border border-white/[0.04]">
                    <Building size={24} strokeWidth={1.5} className="text-white/20 mx-auto mb-3" />
                    <p className="text-white/30 text-sm">No business channels in your library</p>
                    <button onClick={() => setActiveTab('discover')} className="mt-3 text-white/50 hover:text-white text-sm underline transition">Browse Discover</button>
                  </div>
                )}
              </section>
            )}

            {/* 2.5.3a/b: single "Following" block — business rows (scope pills) + nested announcements/offers feed. */}
            {(filterType === 'all' || filterType === 'business') && (
              <FollowBusinesses following={follows.following} feed={follows.feed}
                onToggle={follows.toggle} onSetScope={follows.setScope} busy={follows.busy} signedIn={follows.signedIn} />
            )}

            {/* Stats */}
            {filterType === 'all' && (
              <section className="grid grid-cols-2 md:grid-cols-4 gap-8 py-14 border-t border-white/[0.06]">
                <div><p className="text-4xl font-bold tracking-tight">8</p><p className="text-white/30 text-sm mt-1">Live Stations</p></div>
                <div><p className="text-4xl font-bold tracking-tight">6</p><p className="text-white/30 text-sm mt-1">Autonomous Shows</p></div>
                <div><p className="text-4xl font-bold tracking-tight">120+</p><p className="text-white/30 text-sm mt-1">Licensed Songs</p></div>
                <div><p className="text-4xl font-bold tracking-tight">24/7</p><p className="text-white/30 text-sm mt-1">Streaming</p></div>
              </section>
            )}
          </>
        )}

        {/* â•â•â•â•â•â•â•â•â• DISCOVER TAB â•â•â•â•â•â•â•â•â• */}
        {!_IS_MOMENT && activeTab === 'discover' && (
          <>
            <div className="mb-8">
              <div className="relative max-w-md">
                <Search size={18} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input type="text" placeholder="Search by name, type, or genre..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-white/20 transition text-sm" />
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-xl font-semibold tracking-tight">Browse Business Channels</h2>
              <p className="text-white/30 text-sm mt-1">{filteredBusiness.length} channel{filteredBusiness.length !== 1 ? 's' : ''} available</p>
            </div>

            {businessError && !businessLoading ? (
              <div className="bg-white/[0.03] rounded-2xl p-10 text-center border border-white/[0.04]">
                <Building size={24} strokeWidth={1.5} className="text-white/20 mx-auto mb-3" />
                <p className="text-white/40 text-sm mb-4">Couldn't load businesses. Check your connection and try again.</p>
                <button onClick={retryBusiness} className="px-5 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition">Retry</button>
              </div>
            ) : businessLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1,2,3,4].map(i => (
                  <div key={i} className="bg-white/[0.03] rounded-2xl p-5 animate-pulse border border-white/[0.04]">
                    <div className="flex items-start gap-4"><div className="w-14 h-14 bg-white/[0.06] rounded-xl" /><div className="flex-1"><div className="h-5 bg-white/[0.06] rounded w-3/4 mb-2" /><div className="h-4 bg-white/[0.06] rounded w-1/2" /></div></div>
                  </div>
                ))}
              </div>
            ) : filteredBusiness.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredBusiness.map(channel => (
                  <DiscoverCard key={channel.id} channel={channel}
                    isAdded={addedBusinessIds.includes(channel.id)} onAdd={addBusinessToLibrary}
                    onPlay={playBusinessChannel} onMixAdd={handleMixAdd}
                    isFollowing={follows.followedIds.has(channel.company_id)} onToggleFollow={follows.toggle} followBusy={follows.busy === channel.company_id}
                    isMixMode={isMixMode} isSelected={isMixSelected(channel)} onToggleMix={toggleMixItem} />
                ))}
              </div>
            ) : (
              <div className="bg-white/[0.03] rounded-2xl p-10 text-center border border-white/[0.04]">
                <Search size={24} strokeWidth={1.5} className="text-white/20 mx-auto mb-3" />
                <p className="text-white/30 text-sm">No channels match "{searchQuery}"</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] py-14 mt-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h3 className="text-xl font-bold tracking-tight">groovie</h3>
              <p className="text-white/25 text-sm mt-1">Autonomous radio, powered by AI</p>
            </div>
            <div className="flex gap-8 text-white/30 text-sm">
              <a href="/about" className="hover:text-white/60 transition">About</a>
              <a href="/faq" className="hover:text-white/60 transition">FAQ</a>
              <a href="/contact" className="hover:text-white/60 transition">Contact</a>
              {!_IS_MOMENT && <a href="https://business.groovie.live" target="_blank" rel="noopener noreferrer" className="hover:text-white/60 transition">For Business</a>}
              <a href="/privacy" className="hover:text-white/60 transition">Privacy</a>
              <a href="/terms" className="hover:text-white/60 transition">Terms</a>
            </div>
          </div>
          <p className="text-white/15 text-xs mt-10 text-center">&copy; 2026 Gooverio Labs. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
